import { useState, useEffect, useRef, Fragment } from 'react'
import Auth from './pages/Auth'
import PickemMode from './components/PickemMode'
import BracketMode, { BracketResultsFeed } from './components/BracketMode'
import GolfPicksMode from './components/GolfPicksMode'
import GolfDivisionsMode from './components/GolfDivisionsMode'
import GroupChat from './components/GroupChat'
import TeamComparison from './components/TeamComparison'
import { Skeleton, SkeletonCard } from './components/Skeleton'
import { getGames, getGamesByDate, getPickemAvailability, getBracketAvailability, spreadLabel, fmtML, PLAYOFF_ROUND_INFO, getGameCountsByDay, getNextUFCEventDate, getNextUFCEvent, getUpcomingEvents, fetchGolfSchedule, fetchGolfEventMeta, fetchTierSnapshot, attemptFreezeSnapshot, isGameLocked, fetchGolfRoundScores, fetchMemberDivisionPicks } from './lib/odds'
import { computeGolfPoolDates } from './lib/golfPoolDates'
import { formatScoreToPar } from './lib/golfDisplay'
import {
  getCurrentUser, signOut, deleteAccount, createGroup, joinGroup, getPoolPreview,
  getUserGroups, getGroupMembers,
  savePicks, loadPicks, loadGradedPicks, loadDivisionGradedPicks, getDayKey, getRecentPicks, getUserStats,
  finalizeIfDue, resolvePicks, deleteGroup,
  getProfile, followUser, unfollowUser, getFollowing, getFollowers,
  getSharedPoolMemberIds, getSharedPools,
  getPoolUnlockAttribution,
} from './lib/db'
import { deriveUnlockState } from './lib/unlockState'
import LockedShellPreview from './pages/LockedShellPreview'
import { supabase, isSupabaseEnabled } from './lib/supabase'
import TeamLogoImg from './components/TeamLogoImg'
import BrandMark from './components/BrandMark'
import FighterPanelLayers from './components/FighterPanelLayers'
import { cornerTintBg as ufcCornerTintBg } from './lib/ufcFighterPanel'
import { iconicTeamColor } from './lib/teamColors'
import { createCheckoutSession, createCheckoutSessionUnlock, completePoolConfig, getServerConfig, pollPaymentStatus, TIERS, CONTEST_CLASS_BASE_CENTS, deriveContestClass } from './lib/stripe'
import { Capacitor } from '@capacitor/core'
import { purchaseAndVerifyUnlock } from './lib/appleIapClient'
import { containsProfanity } from './lib/profanityFilter'
import { getBlockedUserIds, getBlockedUsers, unblockUser } from './lib/db'
import { computePoolLockout } from './lib/poolLockout'
import LockedShell from './components/LockedShell'
import { isPlayoffSeason, getPlayoffStatus, getBracketLockout } from './lib/playoffs'
import { timeAgo } from './lib/time'
import { log } from './lib/log'

// ── SVG icons ─────────────────────────────────────────────────────────────────
const IcoGames = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/>
  </svg>
)
const IcoGroups = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
)
const IcoProfile = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
  </svg>
)
const IcoModes = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" rx="1"/>
    <rect x="14" y="3" width="7" height="7" rx="1"/>
    <rect x="3" y="14" width="7" height="7" rx="1"/>
    <rect x="14" y="14" width="7" height="7" rx="1"/>
  </svg>
)

const SPORTS          = ['NBA', 'MLB', 'NHL', 'NFL', 'UFC']
const PICKEM_SPORTS   = ['NBA', 'MLB', 'NHL', 'NFL', 'UFC']
const BRACKET_SPORTS  = ['NBA', 'NHL', 'NFL', 'MLB', 'NCAA Basketball', 'NCAA Football']

// ── Sport/Mode Picker — capability matrix ─────────────────────────────────
// sport:contest_type keys actually wired end-to-end today (ESPN data path +
// grading sportPaths + bracket-round metadata where applicable). Anything
// NOT in this Set renders as "Coming soon" in the picker — non-tappable,
// no real handler. Add a key here ONLY after the underlying data + grading
// layers exist for that combo. NCAA brackets are listed in BRACKET_SPORTS
// (the modal dropdown) but deliberately absent here — there's no ESPN
// path, no server grading, no PLAYOFF_ROUND_INFO entry behind them.
const IMPLEMENTED_COMBOS = new Set([
  'UFC:pick_em',
  'UFC:pick_em_main_card_only',
  'NBA:pick_em', 'NBA:bracket',
  'NHL:pick_em', 'NHL:bracket',
  'MLB:pick_em', 'MLB:bracket',
  'NFL:pick_em', 'NFL:bracket',
  'Golf:pick_em',
  'Golf:divisions',
])

// Picker catalog — declares every sport/mode combo the picker surfaces.
// Tappability is derived per-row from pickerModeAvailable(); this catalog
// only declares STRUCTURE + display copy. Adding a sport here doesn't make
// it work — IMPLEMENTED_COMBOS gates real data-layer availability.
const PICKER_CATALOG = [
  { sport: 'UFC', modes: [
    { contest_type: 'pick_em',                label: "Pick'em" },
    { contest_type: 'pick_em_main_card_only', label: "Main Card Pick'em" },
  ]},
  { sport: 'NBA', modes: [
    { contest_type: 'pick_em', label: "Pick'em" },
    { contest_type: 'bracket', label: 'Bracket' },
  ]},
  { sport: 'NHL', modes: [
    { contest_type: 'pick_em', label: "Pick'em" },
    { contest_type: 'bracket', label: 'Bracket' },
  ]},
  { sport: 'MLB', modes: [
    { contest_type: 'pick_em', label: "Pick'em" },
    { contest_type: 'bracket', label: 'Bracket' },
  ]},
  { sport: 'NFL', modes: [
    { contest_type: 'pick_em', label: "Pick'em" },
    { contest_type: 'bracket', label: 'Bracket' },
  ]},
  { sport: 'NCAA Basketball', modes: [
    { contest_type: 'pick_em', label: "Pick'em" },
    { contest_type: 'bracket', label: 'Bracket' },
  ]},
  { sport: 'NCAA Football', modes: [
    { contest_type: 'pick_em', label: "Pick'em" },
    { contest_type: 'bracket', label: 'Bracket' },
  ]},
  { sport: 'Golf', modes: [
    { contest_type: 'pick_em',   label: "Pick'em" },
    { contest_type: 'divisions', label: 'Pick 6' },
  ]},
]

// Three-state availability for the picker. coming-soon (capability not
// built) vs. gated (built but out-of-season) vs. tappable (built +
// in-season). Reuses getBracketAvailability / getPickemAvailability so
// seasonal gating stays consistent with CreateGroupModal's existing inline
// error path.
//
// Stage 8 cleanup 2026-06-12: the prior resolveGolfUnlocked() gate and
// the ?golfdev=1 escape hatch were retired here when Golf:pick_em landed
// in IMPLEMENTED_COMBOS unconditionally (see commit 581550a). Golf is
// now a first-class top-level chip on Home; Golf:divisions is ALSO in
// IMPLEMENTED_COMBOS and tappable end-to-end — don't re-gate it.
function pickerModeAvailable(sport, contest_type) {
  const key = `${sport}:${contest_type}`
  if (!IMPLEMENTED_COMBOS.has(key)) {
    return { state: 'coming-soon', tag: 'Coming soon', tappable: false }
  }
  const avail = contest_type === 'bracket'
    ? getBracketAvailability(sport)
    : getPickemAvailability(sport)
  if (!avail.available) {
    return { state: 'gated', tag: avail.reason, tappable: false }
  }
  return { state: 'tappable', tag: null, tappable: true }
}

// Single source of truth for createForm defaults. Both create-flow triggers
// AND the SportModePicker handoff use this — was previously inlined at
// THREE sites (createForm useState init + two trigger onClicks), which
// risked drift any time a field was added (e.g. `tier: null` in Phase D,
// upcoming `ufc_main_card_only` for backlog 3). Centralized here so future
// field-adds are one-line.
function defaultCreateForm(overrides = {}) {
  return {
    name:             '',
    contest_type:     'pick_em',
    duration_type:    'daily',
    sport:            '',
    startRound:       1,
    entryId:          'full',
    start_date:       getDayKey(),
    tier:             null,
    golf_config:      null,   // Stage 4: { eventId, rounds, format } when sport='Golf', else null
    ufc_config:       null,   // UFC dropdown Phase 1: { eventId, eventName, eventDate } when sport='UFC', else null
    ...overrides,
  }
}

// Stage 4: derive per-round format from a rounds array. Single source of
// truth — the picker on-change handlers, the auto-populate effect, and any
// future caller must all go through this so the format/rounds invariant
// (R1-2 = 3ball, R3-4 = 2ball) lives in one place. Output is a plain
// object keyed by stringified round number, matching the
// .claude/golf-design.md golf_config shape: { "1":"3ball", "3":"2ball", ... }.
function deriveFormat(rounds) {
  const out = {}
  for (const r of rounds || []) {
    out[String(r)] = (r === 1 || r === 2) ? '3ball' : '2ball'
  }
  return out
}

// Inline explainer text per sport:contest_type. Every live-mode entry below
// is sourced from real scoring code at write time (2026-06-12 audit):
//   - UFC tiers: src/lib/odds.js getUFCFights points ternary (line ~1073)
//     → 1/2/3/5/7 for prelim/main card/co-main/main event/title fight.
//   - Bracket scoring: src/lib/odds.js PLAYOFF_ROUND_INFO (line 172)
//     → 1/2/4/8 progression per round across NBA/NHL/MLB/NFL.
//   - Pickem default: server/index.js gradePicksForUser (line ~1780)
//     uses pick.round_points || 1 → 1 pt per correct pick.
//   - Lock timing: src/lib/odds.js isGameLocked (line 694)
//     → Date.now() >= startTimeMs - 10 min, per game.
//   - Golf pairings + scoring: server/golfGrader.js (line ~340 + comment
//     stack) → 3-balls R1-R2, 2-balls R3-R4 (cut format), 1 pt per
//     correct pick, locks at pairing tee time.
//
// Tone discipline: each live-mode entry follows the pattern
//   [what you pick] — [exclusions/scope]. [scoring]. [lock timing].
// in 2-4 sentences. Genuinely-unbuilt modes get a one-line "(Coming soon)"
// status only — no scaffolding language ("rules TBD", "format details").
// NEVER ship "(draft — verify)" or placeholder text in user-facing strings.
const EXPLAINERS = {
  'UFC:pick_em':
    "Pick the winner of every fight on the card, prelims through title fight. " +
    "Scoring scales by tier: 1 pt prelim, 2 pts main card, 3 pts co-main, 5 pts main event, 7 pts title fight. " +
    "Picks lock 10 minutes before each fight starts. Submit every fight to enter.",
  'UFC:pick_em_main_card_only':
    "Pick the winner of every main card fight — prelims don't count and won't appear in your pool. " +
    "Scoring: 2 pts main card, 3 pts co-main, 5 pts main event, 7 pts title fight. " +
    "Picks lock 10 minutes before each fight starts. Fewer fights, higher stakes per pick.",
  'NBA:pick_em':
    "Pick the winner of every NBA game in your pool's window. 1 pt per correct pick. " +
    "Each game's pick locks 10 minutes before tip-off.",
  'NBA:bracket':
    "Predict the winner of every NBA playoff series across all four rounds. " +
    "Scoring doubles each round: 1 pt First Round, 2 pts Conference Semifinals, 4 pts Conference Finals, 8 pts Finals. " +
    "Bracket picks lock when your selected start round begins.",
  'NHL:pick_em':
    "Pick the winner of every NHL game in your pool's window. 1 pt per correct pick. " +
    "Each game's pick locks 10 minutes before puck drop.",
  'NHL:bracket':
    "Predict the winner of every NHL playoff series across all four rounds. " +
    "Scoring doubles each round: 1 pt First Round, 2 pts Second Round, 4 pts Conference Finals, 8 pts Stanley Cup Final. " +
    "Bracket picks lock when your selected start round begins.",
  'MLB:pick_em':
    "Pick the winner of every MLB game in your pool's window. 1 pt per correct pick. " +
    "Each game's pick locks 10 minutes before first pitch.",
  'MLB:bracket':
    "Predict the winner of every MLB postseason series. " +
    "Scoring doubles each round: 1 pt Wild Card, 2 pts Division Series, 4 pts Championship Series, 8 pts World Series. " +
    "Bracket picks lock when your selected start round begins.",
  'NFL:pick_em':
    "Pick the winner of every NFL game in your pool's window. 1 pt per correct pick. " +
    "Each game's pick locks 10 minutes before kickoff.",
  'NFL:bracket':
    "Predict the winner of every NFL playoff game. " +
    "Scoring doubles each round: 1 pt Wild Card, 2 pts Divisional, 4 pts Conference Championship, 8 pts Super Bowl. " +
    "Bracket picks lock when your selected start round begins.",
  'NCAA Basketball:pick_em':  "(Coming soon)",
  'NCAA Basketball:bracket':  "(Coming soon)",
  'NCAA Football:pick_em':    "(Coming soon)",
  'NCAA Football:bracket':    "(Coming soon)",
  'Golf:pick_em':
    "Pick the winner of each pairing — 3-balls in the first two rounds, 2-balls after the cut. " +
    "1 pt per correct pick; points sum across every pairing. " +
    "Each pairing's pick locks at that pairing's tee time.",
  'Golf:divisions':
    "Roster-style golf pool — pick one golfer per tier across 6 tiers (T1-T5 = 10 each by odds, T6 = remainder). " +
    "Best 4 of 6 tournament totals count, shown relative to par (e.g. +15); lowest wins. " +
    "Missed rounds (cut, WD, MDF, DQ) count as 80 strokes. " +
    "Tier assignments freeze at pool creation from DataGolf pre-tournament odds.",
}

// PPV vs Fight Night badge — gold gradient for numbered cards, muted slate
// for everything else. Used in both CreateGroupModal and GamesTab.
function UFCTypeBadge({ isPPV }) {
  if (isPPV) {
    return (
      <span style={{
        background:'linear-gradient(135deg, #00c853, #00e676)', color:'#000',
        padding:'2px 8px', borderRadius:4, fontSize:11, fontWeight:700,
        fontFamily:'Arial,sans-serif', letterSpacing:'0.5px',
      }}>PPV</span>
    )
  }
  return (
    <span style={{
      background:'#1a1a2a', color:'#aaa',
      padding:'2px 8px', borderRadius:4, fontSize:11,
      border:'1px solid #333', fontFamily:'Arial,sans-serif',
    }}>Fight Night</span>
  )
}

// Renders the next UFC event in card form. Used in the create-group modal
// (banner) and the Games tab (top header when sport === 'UFC').
function UFCEventCard({ event, compact = false }) {
  const dateStr = event.startTimeMs
    ? new Date(event.startTimeMs).toLocaleDateString('en-US', { weekday:'long', month:'short', day:'numeric' })
    : ''
  const timeStr = event.startTimeMs
    ? new Date(event.startTimeMs).toLocaleTimeString('en-US', { hour:'numeric', minute:'2-digit', timeZoneName:'short' })
    : ''
  return (
    <div style={{
      background:'var(--gold-bg)', border:'1px solid rgba(0,230,118,0.3)',
      borderRadius:8, padding: compact ? '10px 14px' : '12px 16px', marginBottom:12,
    }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, flexWrap:'wrap' }}>
        <span style={{ fontSize: compact ? 13 : 14, fontWeight:'bold', color:'var(--gold)', fontFamily:'inherit' }}>
          🥊 {event.name}
        </span>
        <UFCTypeBadge isPPV={event.isPPV} />
      </div>
      {(dateStr || event.venue) && (
        <div style={{ fontSize:11, color:'var(--text-dim)', fontFamily:'Arial,sans-serif' }}>
          {[dateStr && timeStr ? `${dateStr} · ${timeStr}` : dateStr || '', event.venue].filter(Boolean).join(' · ')}
        </div>
      )}
    </div>
  )
}
const CONTEST_LABEL   = { pick_em:"Pick'em", bracket:'Bracket' }
const CONTEST_EMOJI   = { pick_em:'🎯', bracket:'🏆' }
const SPORT_EMOJI     = { MLB:'⚾', NBA:'🏀', NFL:'🏈', NHL:'🏒', UFC:'🥊', WNBA:'🏀' }

// Per-sport accent colors for the SportModePicker. Used to tint the
// sport-section header (icon + name) and the jump-bar pill icon. Picker-
// visual only; no behavior depends on these values.
const SPORT_ACCENT = {
  UFC: 'var(--green)',
  NBA: '#ff8c00',
  NHL: '#1d8bf0',
  MLB: '#d62828',
  NFL: '#7a4cd8',
  'NCAA Basketball': '#ff8c00',
  'NCAA Football':   '#7a4cd8',
  Golf: '#10b981',
}

// Small colored sport glyphs for the picker. Tinted via SPORT_ACCENT so a
// future palette swap is a single-map change. Shapes are pragmatic — UFC
// hexagon, basketball outline, hockey puck ellipse, baseball with
// stitching, football ellipse, golf flag — recognizable at 14-18px.
function SportIcon({ sport, size = 16 }) {
  const color = SPORT_ACCENT[sport] || 'var(--text-dim)'
  const common = { width: size, height: size, viewBox: '0 0 24 24', style: { flexShrink: 0 } }
  switch (sport) {
    case 'UFC':
      return (
        <svg {...common} fill={color}>
          <polygon points="12,2 21,7 21,17 12,22 3,17 3,7" />
        </svg>
      )
    case 'NBA':
    case 'NCAA Basketball':
      return (
        <svg {...common} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18M12 3v18M5.5 5.5c3.3 3 9.7 3 13 0M5.5 18.5c3.3-3 9.7-3 13 0" />
        </svg>
      )
    case 'NHL':
      return (
        <svg {...common} fill={color}>
          <ellipse cx="12" cy="12" rx="9" ry="4" />
        </svg>
      )
    case 'MLB':
      return (
        <svg {...common} fill={color} stroke={color} strokeWidth="1.4">
          <circle cx="12" cy="12" r="9" fillOpacity="0.18" />
          <path d="M5 7c3 1.5 3 8.5 0 10M19 7c-3 1.5-3 8.5 0 10" fill="none" />
        </svg>
      )
    case 'NFL':
    case 'NCAA Football':
      return (
        <svg {...common} fill={color}>
          <ellipse cx="12" cy="12" rx="10" ry="5.5" transform="rotate(-25 12 12)" />
        </svg>
      )
    case 'Golf':
      return (
        <svg {...common} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 20h12" />
          <path d="M12 4v14" />
          <path d="M12 4l7 2.5-7 2.5" fill={color} />
        </svg>
      )
    default:
      return <span style={{ display:'inline-block', width:size, height:size, borderRadius:'50%', background:color, flexShrink:0 }} />
  }
}

// Mode-type emblems — hexagonal owned glyphs that distinguish Pick'em
// from Bracket from locked/coming-soon modes on the Home browse cards.
// The hexagon outline is the same shape SportIcon already uses for UFC
// (polygon 12,2 21,7 21,17 12,22 3,17 3,7). Inner glyphs are
// owned-authorship — a checkmark for Pick'em, a bracket-tree pair for
// Bracket, a padlock for locked. All sport-tinted via SPORT_ACCENT.
function PickemHex({ color = 'var(--accent)', size = 52 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ flexShrink: 0 }} aria-hidden="true">
      <polygon points="12,2 21,7 21,17 12,22 3,17 3,7"
        fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M7.5 12 L10.5 15 L16.5 9"
        fill="none" stroke={color} strokeWidth="2.2"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
function BracketHex({ color = 'var(--accent)', size = 52 }) {
  // 4-into-1 single-elimination bracket tree. Four left-side rails feed
  // pairwise into two semifinal rails, which feed into one championship
  // rail on the right. Reads unambiguously as a tournament bracket.
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ flexShrink: 0 }} aria-hidden="true">
      <polygon points="12,2 21,7 21,17 12,22 3,17 3,7"
        fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" />
      <g fill="none" stroke={color} strokeWidth="1.3"
         strokeLinecap="round" strokeLinejoin="round">
        {/* Four left-side rails (round 1) */}
        <path d="M6 7 L9.5 7" />
        <path d="M6 10 L9.5 10" />
        <path d="M6 14 L9.5 14" />
        <path d="M6 17 L9.5 17" />
        {/* Vertical connectors pairing rails 1+2 and 3+4 */}
        <path d="M9.5 7 L9.5 10" />
        <path d="M9.5 14 L9.5 17" />
        {/* Two semifinal rails feeding from each pair midpoint */}
        <path d="M9.5 8.5 L13 8.5" />
        <path d="M9.5 15.5 L13 15.5" />
        {/* Vertical connector pairing the two semifinals */}
        <path d="M13 8.5 L13 15.5" />
        {/* Championship rail */}
        <path d="M13 12 L18 12" />
      </g>
    </svg>
  )
}
function LockHex({ color = 'var(--color-text-dim)', size = 52 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ flexShrink: 0 }} aria-hidden="true">
      <polygon points="12,2 21,7 21,17 12,22 3,17 3,7"
        fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" />
      <rect x="8.5" y="11.5" width="7" height="5.5" rx="1"
        fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M10 11.5 V9.5 a2 2 0 0 1 4 0 V11.5"
        fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}
function ModeEmblem({ contest_type, sport, tappable, size = 52 }) {
  const accent = SPORT_ACCENT[sport] || 'var(--accent)'
  const color = tappable ? accent : 'var(--color-text-dim)'
  if (!tappable) return <LockHex color={color} size={size} />
  if (contest_type === 'bracket') return <BracketHex color={color} size={size} />
  return <PickemHex color={color} size={size} />
}

// Short mode descriptions for the browse-mode cards on Home. Single-line
// (~5-7 words). Kept separate from the EXPLAINERS map (which holds the
// full "Learn more" paragraph) so the card surface stays clean.
const SHORT_MODE_DESCRIPTIONS = {
  'UFC:pick_em':                'Pick the winners of each fight',
  'UFC:pick_em_main_card_only': 'Main card fights only — pick every winner',
  'NBA:pick_em':                'Pick the winners of each game',
  'NBA:bracket':                'Predict the playoff bracket',
  'NHL:pick_em':                'Pick the winners of each game',
  'NHL:bracket':                'Predict the playoff bracket',
  'MLB:pick_em':                'Pick the winners of each game',
  'MLB:bracket':                'Predict the playoff bracket',
  'NFL:pick_em':                'Pick the winners of each game',
  'NFL:bracket':                'Predict the playoff bracket',
  // NCAA modes are not implemented — falls through to the mode.tag default
  // ("Coming soon") at the call site. No descriptive entry here is the
  // honest signal; a sentence about "winners of each game" would be
  // scaffolding for capability that doesn't exist yet.
  'Golf:pick_em':               'Pick the winner of each pairing, round by round.',
  'Golf:divisions':             'Pick 6, use 4',
}

function ordinal(n) {
  if (n == null) return '—'
  const lastTwo = n % 100, last = n % 10
  if (lastTwo >= 11 && lastTwo <= 13) return `${n}th`
  if (last === 1) return `${n}st`
  if (last === 2) return `${n}nd`
  if (last === 3) return `${n}rd`
  return `${n}th`
}

// Best-effort duration label for a group. Schema has `duration` as integer
// days; we don't store a `duration_type` column, so derive from contest_type
// + start_date presence. Pick'em groups that can't be classified default to
// "Daily" since that's by far the most common case.
function durationLabel(g) {
  if (!g) return 'Daily'
  if (g.duration_type) {
    return g.duration_type.charAt(0).toUpperCase() + g.duration_type.slice(1)
  }
  if (g.contest_type === 'bracket') return 'Playoffs'
  if (g.contest_type === 'pick_em' || g.contest_type === 'pick_em_main_card_only') {
    if (g.start_date)             return 'Daily'
    if (g.duration === 1)         return 'Daily'
    if (g.duration && g.duration <= 7) return 'Weekly'
    return 'Daily'
  }
  return 'Daily'
}

// Lifecycle classifier — status is the single source of truth.
// A group only flips to status='completed' when the server-side finalize
// sequencing gate passes (commit d5588fe): end_date past AND zero pending
// picks. That contract is enforced in one place (db.js finalizeGroup +
// server/index.js finalizeGroupServer) so the client doesn't need to
// recompute it from dates here.
//
// Caveat: a group whose end_date just passed but whose grader cron hasn't
// run yet (up to 15 min) stays in "Active" until status flips. This is
// correct — the group genuinely hasn't completed yet.
//
// Previous date-fallback parsed end_date='YYYY-MM-DD' as UTC midnight,
// which for negative-offset timezones flipped groups to historical a full
// calendar day before the local date actually ended.
function isHistoricalGroup(g) {
  if (!g) return false
  return g.status === 'completed' || g.status === 'archived'
}

// Default tab to land on when opening a group, by status.
//   archived  → standings  (live contest tab hides for archived; standings is the read-only home)
//   else      → contest    (Picks/Bracket for the live contest)
function defaultTabForStatus(status) {
  if (status === 'archived') return 'standings'
  return 'contest'
}

// ── DateSelector — 7-day strip used by daily Pick'em group creation ──────────
// `gameCounts` is an optional { iso: count } map — when present each button
// shows "Ng" beneath the date, and the day with the most games gets a star.
function DateSelector({ value, onChange, gameCounts = {} }) {
  const today = new Date(); today.setHours(0, 0, 0, 0)
  const days = []
  for (let i = 0; i < 7; i++) {
    const d = new Date(today); d.setDate(d.getDate() + i)
    const iso = d.toISOString().slice(0, 10)
    const label = i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : d.toLocaleDateString('en-US', { weekday: 'short' })
    days.push({ iso, label, dayNum: d.getDate(), picked: value === iso, count: gameCounts[iso] })
  }
  return (
    <div style={{ display:'flex', gap:6, overflowX:'auto', paddingBottom:4, scrollbarWidth:'none' }}>
      {days.map(d => {
        const countColor = d.count == null ? null
          : d.count >= 10 ? 'var(--green)'
          : d.count >= 5  ? 'var(--gold)'
          : 'var(--red)'
        return (
          <button key={d.iso} type="button" onClick={() => onChange(d.iso)}
            style={{ flexShrink:0, minWidth:62,
              background: d.picked ? 'var(--gold-bg2)' : 'var(--surface2)',
              border: `2px solid ${d.picked ? 'var(--gold)' : 'var(--border)'}`,
              borderRadius:10, padding:'8px 6px', cursor:'pointer', textAlign:'center', transition:'all .2s' }}>
            <div style={{ fontSize:11, color: d.picked ? 'var(--gold)' : 'var(--text-dim)', fontFamily:'Arial,sans-serif', marginBottom:2 }}>
              {d.label}
            </div>
            <div style={{ fontSize:18, fontWeight:'bold', color: d.picked ? 'var(--gold)' : 'var(--text)', fontFamily:'Arial,sans-serif', lineHeight:1 }}>
              {d.dayNum}
            </div>
            {d.count != null && (
              <>
                <div style={{ fontSize:9, color:countColor, fontFamily:'Arial,sans-serif', marginTop:3, fontWeight:'bold' }}>
                  {d.count}g
                </div>
                <div style={{ width:6, height:6, borderRadius:'50%', background:countColor, margin:'2px auto 0' }} />
              </>
            )}
          </button>
        )
      })}
    </div>
  )
}

// ── GroupImageUpload — circle photo picker with Supabase Storage upload ──────
// Module-scope so the file <input> doesn't remount on every CreateGroupModal
// render (which would dismiss the mobile photo picker mid-pick).
function GroupImageUpload({ imageUrl, onChange }) {
  const [uploading, setUploading] = useState(false)
  const [err,       setErr]       = useState('')

  async function handleFile(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setErr('')
    setUploading(true)
    try {
      const ext  = (file.name.split('.').pop() || 'jpg').toLowerCase()
      const path = `group-${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`
      const { error } = await supabase.storage.from('group-images')
        .upload(path, file, { cacheControl: '3600', upsert: false })
      if (error) throw error
      const { data: { publicUrl } } = supabase.storage.from('group-images').getPublicUrl(path)
      onChange?.(publicUrl)
    } catch (uploadErr) {
      log('[group image] upload failed:', uploadErr?.message)
      setErr('Upload failed')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'4px' }}>
      <label style={{
        width: 72, height: 72, borderRadius: '50%',
        background: imageUrl ? 'transparent' : 'var(--surface)',
        backgroundImage: imageUrl ? `url(${imageUrl})` : 'none',
        backgroundSize: 'cover', backgroundPosition: 'center',
        border: imageUrl ? '1px solid var(--border)' : '2px dashed rgba(255,255,255,0.2)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        cursor: uploading ? 'wait' : 'pointer',
        flexShrink: 0,
      }}>
        {!imageUrl && (
          <>
            <div style={{ fontSize: 22 }}>{uploading ? '⏳' : '📷'}</div>
            <span style={{ fontSize: 9, color: 'var(--text-dim)', marginTop: 2, letterSpacing: '0.3px' }}>
              {uploading ? 'Uploading' : 'Add Photo'}
            </span>
          </>
        )}
        <input type="file" accept="image/jpeg,image/png,image/gif,image/webp,image/heic" style={{ display: 'none' }}
          onChange={handleFile} disabled={uploading} />
      </label>
      {err && <div style={{ fontSize: 10, color: 'var(--red)' }}>{err}</div>}
    </div>
  )
}

// Home screen filter chips — replace the picker's prior sport-only jump-bar
// with a richer filter row: "Featured" (default — shows the upcoming-event
// hero plus every sport section), each primary sport as its own filter,
// and "More" for the sports gated to "Coming soon" today (NCAA Basketball,
// NCAA Football, Golf). The component is still named SportModePicker
// internally — the bottom-nav label flips to "Home" — to keep the rename
// surface small while the create-flow entry point evolves into a Home
// screen. PR 1 lands the filter / hero placeholder / pricing block; PR 2
// wires the upcoming-events data + image-backed browse cards.
const HOME_FILTERS = [
  { id: 'featured', label: 'Featured' },
  { id: 'UFC',      label: 'UFC' },
  { id: 'NFL',      label: 'NFL' },
  { id: 'NBA',      label: 'NBA' },
  { id: 'NHL',      label: 'NHL' },
  { id: 'Golf',     label: 'Golf' },
  { id: 'more',     label: 'More' },
]
const MORE_FILTER_SPORTS = new Set(['NCAA Basketball', 'NCAA Football'])

// ── FeaturedEventHero — operator-curated featured_events table ───────────────
// PR 3 replaces PR 2's getUpcomingEvents auto-feed (a single ESPN-pulled
// upcoming game) with a Supabase-backed featured_events table that the
// operator curates manually. Each row = one full-bleed graphic the
// operator designed in a separate tool; the app does NOT render text over
// it. The hero's content is the image itself (title, dates, venue, art
// all baked in). Only TWO overlaid buttons sit on top:
//
//   • Create Pool   → pre-fills sport + contest_type + start_date from
//                     the row, opens the create flow (reuses onSelect
//                     wiring from PR 2 — App.jsx onSelect handler
//                     accepts the optional startDate)
//   • Learn More    → expands the row's rules_text BELOW the carousel
//                     (sibling block; toggling does not reflow the
//                     image grid)
//
// Query: end_date >= today, sorted by display_order then start_date.
// Empty result → component returns null and the hero is hidden entirely
// (Home shows only chips + browse modes + pricing).
//
// Carousel: horizontal scroll with CSS scroll-snap; onScroll updates the
// active dot. Tapping a dot scrolls the active slide into view.
//
// Images live in /public/featured/<image_filename> — a NEW folder
// separate from /public/sport-bg/ (group cards) and /public/home-bg/
// (browse cards, kept for the PR 2 image set). The three sets do not mix.
//
// Migration: see migrations/029_featured_events.sql (operator runs
// manually in Supabase SQL editor; the file is in repo for archival).
function FeaturedEventHero({ onSelect, onPickSport }) {
  const [events, setEvents]                 = useState(null)  // null = loading; [] = empty
  const [activeIndex, setActiveIndex]       = useState(0)
  const [expandedEventId, setExpandedEventId] = useState(null)
  const scrollerRef = useRef(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!isSupabaseEnabled) {
        if (!cancelled) setEvents([])
        return
      }
      try {
        const today = new Date().toLocaleDateString('en-CA') // YYYY-MM-DD local
        const { data, error } = await supabase
          .from('featured_events')
          .select('id, title, sport, contest_type, start_date, end_date, image_filename, rules_text, display_order')
          .gte('end_date', today)
          .order('display_order', { ascending: true })
          .order('start_date',    { ascending: true })
        if (cancelled) return
        if (error) {
          // 42P01 = relation does not exist (migration not yet applied).
          // Treat that as empty so the hero hides until the table lands.
          setEvents([])
        } else {
          setEvents(data || [])
        }
      } catch {
        if (!cancelled) setEvents([])
      }
    })()
    return () => { cancelled = true }
  }, [])

  function handleScroll(e) {
    const el = e.currentTarget
    if (!el.clientWidth) return
    const i = Math.round(el.scrollLeft / el.clientWidth)
    if (i !== activeIndex) setActiveIndex(i)
  }
  function jumpToIndex(i) {
    const el = scrollerRef.current
    if (!el) return
    el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' })
  }

  // Loading: render a low-key skeleton so the page-body layout doesn't
  // jump when events arrive. Empty: return null per operator spec —
  // the entire FEATURED EVENTS section header is also gated on
  // events.length in the parent.
  if (events === null) {
    return (
      <div style={{
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface-elev)',
        border: '1px solid var(--color-border)',
        padding: '60px 20px',
        marginBottom: 18,
        textAlign: 'center',
        color: 'var(--color-text-dim)',
        fontSize: 13, fontFamily: 'Arial,sans-serif',
      }}>
        Loading featured events…
      </div>
    )
  }
  if (events.length === 0) return null

  const expandedEvent = expandedEventId
    ? events.find(ev => ev.id === expandedEventId)
    : null

  return (
    <>
      {/* FEATURED EVENTS section label — owned by the hero so it hides
          along with the carousel when the table is empty. */}
      <div style={{
        fontSize: 11, fontWeight: 700, letterSpacing: '0.7px',
        color: 'var(--accent)', textTransform: 'uppercase',
        fontFamily: 'inherit',
        marginBottom: 10,
      }}>
        Featured Events
      </div>

      {/* Carousel — horizontal scroll-snap, one slide per featured event.
          Slides are full-bleed images; the only overlaid UI is the two
          buttons (and the dots below). Padding on the inner container is
          intentionally zero — the image IS the content.
          BREAKOUT: negative horizontal margins cancel the .page-body
          16px side padding so the carousel goes edge-to-edge with the
          viewport. Combined with width: 100% on the slides and 16:9
          aspect-ratio, the slide is now as wide as the viewport — which
          scales the baked-in graphic text up substantially without
          reintroducing crop (slide ratio still matches source ratio).
          Border-radius dropped because rounded corners against the
          viewport edge read as a glitch. Section label + pagination
          dots stay inset (they're sibling elements outside this div). */}
      <div style={{
        position: 'relative',
        marginBottom: 12,
        marginLeft: -16,
        marginRight: -16,
        overflow: 'hidden',
      }}>
        <div
          ref={scrollerRef}
          onScroll={handleScroll}
          className="featured-carousel"
          style={{
            display: 'flex',
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            scrollbarWidth: 'none',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {events.map(event => {
            // Multi-mode routing is catalog-driven: a sport with two or more
            // IMPLEMENTED_COMBOS-gated modes in PICKER_CATALOG routes a
            // featured row through onPickSport → the filtered Browse Modes
            // grid where the user picks the mode themselves. Single-mode
            // sports keep going straight to create with the row's
            // contest_type pre-filled.
            //
            // canCreate is forced true on the multi-mode branch because the
            // per-mode tappability check happens in the Browse Modes grid
            // (each tile reads pickerModeAvailable independently). The
            // empty-event-window edge for UFC is caught downstream in
            // CreateGroupModal (the ufcSchedule.length === 0 error banner
            // + the ufcSubmitBlocked gate on the Create Group button), so
            // no extra guard is needed here.
            //
            // Today this auto-includes Golf (Pick'em + Pick 6) and UFC
            // (Pick'em + Main Card Pick'em). Any future sport that lands a
            // second implemented mode picks up the same routing with no
            // code change.
            const implementedModes = (PICKER_CATALOG.find(s => s.sport === event.sport)?.modes || [])
              .filter(m => IMPLEMENTED_COMBOS.has(`${event.sport}:${m.contest_type}`))
            const isMultiModeRouted = implementedModes.length >= 2
            const availability = pickerModeAvailable(event.sport, event.contest_type)
            const canCreate = isMultiModeRouted ? true : availability.tappable
            return (
            <div
              key={event.id}
              style={{
                flex: '0 0 100%',
                scrollSnapAlign: 'start',
                position: 'relative',
                // Match the source graphic's native ratio (1280x720 = 16:9)
                // so the image fills the slide edge-to-edge with NO cropping
                // and NO letterbox bars. Previously a fixed minHeight: 380
                // forced a taller-than-source slide and the center/cover
                // background cropped the left/right edges of the graphic —
                // chopping the baked-in title text (e.g. "FREEDOM" → "EEDOM"
                // on the Freedom 250 slide). With the ratio aligned to the
                // source, cover fills exactly; the whole graphic shows.
                width: '100%',
                aspectRatio: '16 / 9',
                background: `url('/featured/${event.image_filename}') center/cover no-repeat`,
                backgroundColor: 'var(--surface-elev)',
              }}
            >
              {/* Compact buttons anchored bottom-right corner. The graphic
                  IS the slide — title/dates/venue/art are baked into the
                  image, typically along the bottom-left. The CTA tucks
                  into the right corner at natural width (NOT full-width)
                  so it doesn't cover the baked text. Learn More sits to
                  the LEFT of Create Pool when present, both right-aligned. */}
              <div style={{
                position: 'absolute',
                right: 12, bottom: 12,
                display: 'flex', gap: 6,
                alignItems: 'center',
              }}>
                {event.rules_text && (
                  <button
                    type="button"
                    onClick={() => setExpandedEventId(prev => prev === event.id ? null : event.id)}
                    style={{
                      background: 'rgba(0,0,0,0.55)',
                      border: '1px solid rgba(255,255,255,0.22)',
                      borderRadius: 8,
                      padding: '7px 11px',
                      fontSize: 11.5, fontWeight: 700,
                      color: 'var(--color-text)',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      backdropFilter: 'blur(6px)',
                      WebkitBackdropFilter: 'blur(6px)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Learn More
                  </button>
                )}
                <button
                  type="button"
                  onClick={!canCreate
                    ? undefined
                    : isMultiModeRouted
                      ? () => onPickSport(event.sport)
                      : () => onSelect(event.sport, event.contest_type, event.start_date)}
                  disabled={!canCreate}
                  aria-disabled={!canCreate}
                  style={{
                    background: canCreate ? 'var(--accent)' : 'rgba(0,0,0,0.55)',
                    border: canCreate ? 'none' : '1px solid rgba(255,255,255,0.22)',
                    borderRadius: 8,
                    padding: '7px 12px',
                    fontSize: 11.5, fontWeight: 700,
                    color: canCreate ? '#000' : 'var(--color-text)',
                    cursor: canCreate ? 'pointer' : 'not-allowed',
                    opacity: canCreate ? 1 : 0.55,
                    fontFamily: 'inherit',
                    display: 'flex', alignItems: 'center',
                    gap: 5,
                    whiteSpace: 'nowrap',
                    backdropFilter: canCreate ? undefined : 'blur(6px)',
                    WebkitBackdropFilter: canCreate ? undefined : 'blur(6px)',
                  }}
                >
                  {canCreate ? (
                    <>
                      Create Pool
                      <span style={{ fontSize: 13, lineHeight: 1 }}>→</span>
                    </>
                  ) : (
                    'Coming Soon'
                  )}
                </button>
              </div>
            </div>
            )
          })}
        </div>
      </div>

      {/* Pagination dots — below the carousel, outside the overflow
          container so they aren't clipped. Only renders when ≥2 events. */}
      {events.length > 1 && (
        <div style={{
          display: 'flex', justifyContent: 'center', gap: 7,
          marginBottom: 16,
        }}>
          {events.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => jumpToIndex(i)}
              style={{
                width: 7, height: 7, borderRadius: '50%',
                background: i === activeIndex ? 'var(--accent)' : 'var(--color-border)',
                border: 'none', cursor: 'pointer', padding: 0,
                transition: 'background .2s',
              }}
              aria-label={`Show event ${i + 1}`}
            />
          ))}
        </div>
      )}

      {/* Learn More expansion — sibling block below the carousel. Sits
          here instead of overlaying the image so the rules_text reads
          comfortably without darkening the operator's graphic. */}
      {expandedEvent && (
        <div style={{
          background: 'var(--surface-elev)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-card)',
          padding: '14px 16px',
          marginBottom: 18,
          fontSize: 13,
          color: 'var(--color-text)',
          whiteSpace: 'pre-line',
          lineHeight: 1.55,
          fontFamily: 'Arial,sans-serif',
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', marginBottom: 10,
          }}>
            <div style={{
              fontSize: 11, fontWeight: 700, letterSpacing: '0.6px',
              color: 'var(--accent)', textTransform: 'uppercase',
              fontFamily: 'inherit',
            }}>
              {expandedEvent.title}
            </div>
            <button
              type="button"
              onClick={() => setExpandedEventId(null)}
              aria-label="Close"
              style={{
                background: 'transparent', border: 'none',
                color: 'var(--color-text-dim)',
                fontSize: 20, cursor: 'pointer', padding: '0 4px',
                lineHeight: 1,
              }}
            >×</button>
          </div>
          {expandedEvent.rules_text}
        </div>
      )}
    </>
  )
}

// ── PricingBlock — duration-tier pricing CTA at the bottom of Home ──────────
// PLACEHOLDER COPY. Every billing claim below ("first pool free", "no monthly
// fees", "cancel anytime", "25-person cap") MUST be reviewed against the
// final Apple IAP product catalog and App Store Review Guidelines compliance
// pass BEFORE LAUNCH.
//
// Two specific things the launch-time copy pass needs to address:
//
//   (1) Match billing mechanics. The duration-tier pricing model decided on
//       2026-06-02 (HANDOFF "Paid model — duration-tier pricing") is per-pool
//       NON-RENEWING CONSUMABLES at Apple's IAP catalog level. Current copy
//       here is subscription-flavored ("no monthly fees", "cancel anytime")
//       which does not match a consumable model. Final copy needs purchase-
//       language rewording — e.g., "one-time fee per pool" instead of "no
//       monthly fees"; drop "cancel anytime" entirely (consumables don't
//       cancel — you bought a pool's access for its duration).
//
//   (2) Numbers must match the IAP catalog. The "25-person cap" is a guess
//       — the duration tiers may have different seat caps per tier (single
//       event might cap at 10; full season at 100). Don't ship the specific
//       number until the catalog is decided.
//
// See HANDOFF "Paid model — duration-tier pricing DECIDED 2026-06-02" and
// "PLATFORM FEE — iOS PAYMENT STRATEGY (Apple IAP on iOS + Stripe on web)".
function PricingBlock() {
  return (
    <div style={{
      marginTop: 22,
      marginBottom: 8,
      padding: '20px 18px',
      background: 'var(--surface-elev)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--radius-card)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* LIMITED TIME pill */}
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        fontSize: 10, fontWeight: 700, letterSpacing: '0.7px',
        color: 'var(--accent)',
        background: 'var(--accent-soft)',
        border: '1px solid var(--accent-medium)',
        borderRadius: 4,
        padding: '3px 7px',
        textTransform: 'uppercase',
        marginBottom: 10,
        fontFamily: 'inherit',
      }}>
        <span style={{ fontSize: 11 }}>🎁</span>
        Limited Time
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* FIRST POOL FREE title */}
          <div style={{
            fontSize: 22, fontWeight: 900,
            color: 'var(--accent)',
            lineHeight: 1.05, marginBottom: 6,
            fontFamily: 'inherit',
            letterSpacing: -0.3,
          }}>
            FIRST POOL FREE
          </div>
          {/* PLACEHOLDER COPY — final number ("25") must match the per-tier
              IAP seat cap once the catalog is finalized. */}
          <div style={{
            fontSize: 13,
            color: 'var(--color-text-dim)',
            marginBottom: 14,
            fontFamily: 'Arial,sans-serif',
            lineHeight: 1.4,
          }}>
            Try a daily pool free for up to 25 people. No cost.
          </div>

          {/* Check list — PLACEHOLDER COPY (subscription-flavored vs. the
              non-renewing-consumables IAP model; final purchase-language
              rewording at billing-implementation time per the comment
              above the component). */}
          <div style={{ display: 'grid', gap: 6, marginBottom: 4 }}>
            {[
              'No subscriptions',
              'No monthly fees',
              'Cancel anytime',
            ].map(line => (
              <div key={line} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                fontSize: 12.5,
                color: 'var(--color-text)',
                fontFamily: 'Arial,sans-serif',
              }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 16, height: 16, borderRadius: '50%',
                  background: 'var(--accent-soft)',
                  color: 'var(--accent)',
                  fontSize: 11, fontWeight: 700,
                  flexShrink: 0,
                }}>✓</span>
                {line}
              </div>
            ))}
          </div>
        </div>

        {/* "25" cap visual — green dashed-border square, mockup right side */}
        <div style={{
          flexShrink: 0,
          width: 72, height: 72,
          borderRadius: 10,
          border: '2px dashed var(--accent-medium)',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          background: 'var(--accent-soft)',
        }}>
          <div style={{
            fontSize: 26, fontWeight: 900,
            color: 'var(--accent)',
            lineHeight: 1, fontFamily: 'inherit',
            letterSpacing: -0.5,
          }}>25</div>
          <div style={{
            fontSize: 9, fontWeight: 700, letterSpacing: '0.6px',
            color: 'var(--accent)',
            marginTop: 2,
            textTransform: 'uppercase',
            fontFamily: 'inherit',
          }}>people</div>
        </div>
      </div>

      {/* Learn more link — bottom right */}
      <div style={{
        display: 'flex', justifyContent: 'flex-end',
        marginTop: 14,
        paddingTop: 12,
        borderTop: '1px solid var(--row-divider)',
      }}>
        <span style={{
          fontSize: 12, fontWeight: 700,
          color: 'var(--accent)',
          fontFamily: 'Arial,sans-serif',
        }}>
          Learn more →
        </span>
      </div>

      {/* Placeholder disclaimer — italic, smaller, separate row */}
      <div style={{
        fontSize: 10,
        color: 'var(--color-text-muted)',
        marginTop: 8,
        fontFamily: 'Arial,sans-serif',
        fontStyle: 'italic',
        textAlign: 'center',
      }}>
        Pricing copy is placeholder — final terms land with the Apple IAP catalog.
      </div>
    </div>
  )
}

// ── SportModePicker — Home screen (Modes tab renamed 2026-06-02) ────────────
// Internal name retained to avoid a 4-site rename surface. Tapping a mode
// pre-fills createForm via defaultCreateForm({ sport, contest_type }), closes
// itself, and opens the existing CreateGroupModal with preFilled=true (which
// hides the sport/contest_type selectors). Coming-soon and out-of-season modes
// are visible but non-tappable. Filter chips at top swap which sport sections
// render; "Learn more" expands an inline explainer per row.

function SportModePicker({ onSelect }) {
  const [expanded, setExpanded] = useState(null)  // key of expanded explainer row
  const [homeFilter, setHomeFilter] = useState('featured')

  // Stage 8 cleanup 2026-06-12: the prior golfUnlocked branch + local
  // homeFilters/moreFilterSports overrides retired with resolveGolfUnlocked()
  // — Golf is now permanently a top-level chip via HOME_FILTERS, and
  // MORE_FILTER_SPORTS no longer includes it. The constants below bind
  // directly to the module-scope arrays.

  // Resolve availability once per render — three-state, sourced from
  // IMPLEMENTED_COMBOS + getBracketAvailability/getPickemAvailability.
  const allSections = PICKER_CATALOG.map(sec => ({
    sport: sec.sport,
    modes: sec.modes.map(m => {
      const key = `${sec.sport}:${m.contest_type}`
      return { ...m, key, ...pickerModeAvailable(sec.sport, m.contest_type) }
    }),
  }))

  // Filter chip → which sections render. 'featured' shows everything; a
  // sport-specific filter narrows to that sport; 'more' surfaces the
  // sports that are all-coming-soon today.
  const sections =
    homeFilter === 'featured'
      ? allSections.filter(s => !MORE_FILTER_SPORTS.has(s.sport))
      : homeFilter === 'more'
        ? allSections.filter(s => MORE_FILTER_SPORTS.has(s.sport))
        : allSections.filter(s => s.sport === homeFilter)

  // Flatten visible sections into a sport-ordered list of mode entries.
  // The mockup's Browse Modes section is a 2-column grid where each row
  // typically holds one sport's two modes side-by-side; the grid here
  // preserves PICKER_CATALOG order so that grouping happens naturally.
  const browseModes = sections.flatMap(sec =>
    sec.modes.map(m => ({ ...m, sport: sec.sport }))
  )

  return (
    <div className="page">

        {/* Home sticky header — title + subtitle + How-it-works link, plus
            filter chips below. Title matches the mockup's "Create a pool"
            with the word "pool" tinted green. */}
        <div className="page-header" style={{ paddingBottom: 10 }}>
          <div>
            <h1 style={{
              fontSize: 26, fontWeight: 900,
              margin: 0, marginBottom: 2,
              color: 'var(--color-text)',
              fontFamily: 'inherit',
              letterSpacing: -0.4,
            }}>
              Create a <span style={{ color: 'var(--accent)' }}>pool</span>
            </h1>
            <div style={{
              fontSize: 12.5,
              color: 'var(--color-text-dim)',
              fontFamily: 'Arial,sans-serif',
            }}>
              Choose a sport or event to get started
            </div>
          </div>

          <div className="group-filter-bar" style={{ marginTop: 12 }}>
            {HOME_FILTERS.map(f => (
              <button
                key={f.id}
                type="button"
                className={`group-filter-chip${homeFilter === f.id ? ' is-active' : ''}`}
                onClick={() => setHomeFilter(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="page-body">
          {/* FeaturedEventHero owns its own section label and hides itself
              (returns null) when featured_events is empty. */}
          {homeFilter === 'featured' && (
            <FeaturedEventHero
              onSelect={onSelect}
              onPickSport={(sport) => {
                // Multi-mode routing from a featured row: filter the
                // picker to just that sport's modes. The chip bar
                // doesn't have a chip for every sport (Golf lives in
                // 'More'), but the filter logic at homeFilter ===
                // sport-name handles this regardless of chip presence.
                // Browse Modes section will show the sport's modes
                // with Coming Soon / live state per IMPLEMENTED_COMBOS.
                setHomeFilter(sport)
                if (typeof window !== 'undefined') {
                  window.scrollTo({ top: 0, behavior: 'smooth' })
                }
              }}
            />
          )}

          {/* BROWSE MODES section label */}
          <div style={{ marginTop: 6, marginBottom: 10 }}>
            <div style={{
              fontSize: 11, fontWeight: 700, letterSpacing: '0.7px',
              color: 'var(--accent)', textTransform: 'uppercase',
              fontFamily: 'inherit',
            }}>
              Browse Modes
            </div>
          </div>

          {/* Image-backed mode cards, chunked into rows of 2 so the inline
              Learn More explainer can slot in directly under the row
              containing the tapped card (rather than at the bottom of the
              whole grid, which disconnected the action from the result).
              Each row is its own grid; the expanded explainer renders as a
              full-width sibling row immediately after the row it belongs to. */}
          <div style={{ marginBottom: 14 }}>
            {(() => {
              const rows = []
              for (let i = 0; i < browseModes.length; i += 2) {
                rows.push(browseModes.slice(i, i + 2))
              }
              return rows.map((row, rowIdx) => {
                const expandedInRow = row.find(m => m.key === expanded)
                return (
                  <Fragment key={`row-${rowIdx}`}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 10,
                      marginBottom: 10,
                    }}>
                      {row.map(m => (
                        <BrowseModeCard
                          key={m.key}
                          mode={m}
                          expanded={expanded === m.key}
                          onToggleExpand={() => setExpanded(prev => prev === m.key ? null : m.key)}
                          onSelect={onSelect}
                        />
                      ))}
                    </div>
                    {expandedInRow && (
                      <div style={{
                        background: 'var(--surface-elev)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-card)',
                        padding: '14px 16px',
                        marginBottom: 10,
                        fontSize: 12.5,
                        color: 'var(--color-text-dim)',
                        whiteSpace: 'pre-line',
                        lineHeight: 1.6,
                        fontFamily: 'Arial,sans-serif',
                      }}>
                        <div style={{
                          display: 'flex', justifyContent: 'space-between',
                          alignItems: 'center', marginBottom: 8,
                        }}>
                          <div style={{
                            fontSize: 11, fontWeight: 700, letterSpacing: '0.6px',
                            color: 'var(--accent)', textTransform: 'uppercase',
                            fontFamily: 'inherit',
                          }}>
                            {expandedInRow.sport} {expandedInRow.label}
                          </div>
                          <button
                            type="button"
                            onClick={() => setExpanded(null)}
                            style={{
                              background: 'transparent', border: 'none',
                              color: 'var(--color-text-dim)',
                              fontSize: 16, cursor: 'pointer', padding: 0,
                              lineHeight: 1,
                            }}
                          >
                            ×
                          </button>
                        </div>
                        {EXPLAINERS[expandedInRow.key] || '(no explainer text yet)'}
                      </div>
                    )}
                  </Fragment>
                )
              })
            })()}
          </div>

          {/* PricingBlock removed 2026-06-10 per operator — "first pool
              free" promo banner pulled until pricing copy is finalized
              with the Apple IAP catalog. Component left defined above
              for easy re-mount if/when copy is ready. */}
        </div>
    </div>
  )
}

// ── BrowseModeCard — image-backed mode card for the Home Browse Modes grid ──
// One card per (sport × contest_type). Background is the sport's
// /public/home-bg/<sport>.webp under a light dark scrim (pre-tinted images
// already carry the sport color; double-darkening risks crushing them, so
// the scrim is tuned LIGHTER than the group-card 78%/65% floor). The
// hexagonal ModeEmblem on the left is sport-accent-tinted via SPORT_ACCENT.
// Coming-soon modes get the LockHex emblem and a dimmed card opacity.
function BrowseModeCard({ mode, expanded, onToggleExpand, onSelect }) {
  const sportKey = mode.sport.toLowerCase().replace(/\s+/g, '-')
  // Map the catalog sport name to the /public/home-bg file name. Only the
  // top-level sports today have home-bg files; sports not in the file set
  // (NCAA, Golf, etc.) fall back to a polished surface with no image.
  const homeBgFile = ({
    'UFC': 'ufc', 'NFL': 'nfl', 'NBA': 'nba',
    'NHL': 'nhl', 'MLB': 'mlb', 'Golf': 'golf',
  })[mode.sport]
  const hasImage = !!homeBgFile
  const bgImage = hasImage ? `url('/home-bg/${homeBgFile}.webp')` : ''
  // Light scrim — pre-tinted source images don't need much.
  const scrim = 'linear-gradient(180deg, rgba(0,0,0,0.50) 0%, rgba(0,0,0,0.75) 100%)'
  const background = hasImage
    ? `${scrim}, ${bgImage} center/cover no-repeat`
    : 'var(--surface-elev)'

  // PR 3 fix — the prior tap structure had a zero-padding Info button
  // inside a card-wide clickable div, so taps near the button often
  // landed on the parent (which opened the create flow) instead of
  // toggling the explainer. The fix: card itself is NO LONGER click-
  // wide; the title+description block has its own button that triggers
  // create-flow, and the right column houses two clearly-sized tap
  // targets (Info ~ 36×24 minimum, chevron its own button). Both have
  // proper backgrounds + padding so the tap zone is visible.
  return (
    <div
      style={{
        position: 'relative',
        background,
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-card)',
        padding: '12px 12px',
        minHeight: 92,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        opacity: mode.tappable ? 1 : 0.7,
        overflow: 'hidden',
        transition: 'border-color .2s, transform .12s',
      }}
    >
      {/* Hexagonal mode emblem — sport-tinted */}
      <ModeEmblem
        contest_type={mode.contest_type}
        sport={mode.sport}
        tappable={mode.tappable}
        size={40}
      />

      {/* Title + description — primary tap zone. This is what opens the
          create flow on a tappable mode; the right-column buttons are
          siblings, not nested, so their taps don't fight this one. */}
      <button
        type="button"
        onClick={() => mode.tappable && onSelect(mode.sport, mode.contest_type)}
        disabled={!mode.tappable}
        style={{
          flex: 1, minWidth: 0,
          background: 'transparent', border: 'none', padding: 0,
          textAlign: 'left',
          cursor: mode.tappable ? 'pointer' : 'default',
          fontFamily: 'inherit',
        }}
      >
        <div className="browse-mode-title" style={{
          fontWeight: 800,
          color: 'var(--color-text)',
          marginBottom: 3,
          fontFamily: 'inherit',
          lineHeight: 1.15,
          textShadow: '0 1px 4px rgba(0,0,0,0.6)',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          wordBreak: 'break-word',
        }}>
          {mode.sport} {mode.label}
        </div>
        <div style={{
          fontSize: 11.5,
          color: 'rgba(255,255,255,0.75)',
          fontFamily: 'Arial,sans-serif',
          lineHeight: 1.3,
          textShadow: '0 1px 3px rgba(0,0,0,0.5)',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
        }}>
          {SHORT_MODE_DESCRIPTIONS[mode.key] || (mode.tag || 'Coming soon')}
        </div>
      </button>

      {/* Right column — explicit Info button (visible chip-style tap
          target) and chevron (separate tappable area too, opens create
          flow for symmetry with the title button). */}
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6,
        flexShrink: 0,
      }}>
        <button
          type="button"
          onClick={onToggleExpand}
          style={{
            background: 'rgba(0,0,0,0.45)',
            border: '1px solid rgba(255,255,255,0.18)',
            borderRadius: 6,
            padding: '4px 10px',
            fontSize: 10.5, fontWeight: 700,
            color: 'var(--color-text)',
            cursor: 'pointer',
            fontFamily: 'Arial,sans-serif',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            minHeight: 26,
          }}
          aria-label={`${expanded ? 'Hide' : 'Show'} explainer for ${mode.sport} ${mode.label}`}
        >
          {expanded ? 'Hide' : 'Info'}
        </button>
        {mode.tappable && (
          <button
            type="button"
            onClick={() => onSelect(mode.sport, mode.contest_type)}
            aria-label={`Create ${mode.sport} ${mode.label} pool`}
            style={{
              background: 'transparent', border: 'none', padding: '4px 6px',
              color: 'var(--color-text)',
              fontSize: 18, lineHeight: 1,
              textShadow: '0 1px 4px rgba(0,0,0,0.6)',
              cursor: 'pointer',
            }}
          >›</button>
        )}
      </div>
    </div>
  )
}

// ── Create-Group visual frame helpers (Phase 1 redesign 2026-06-11) ─────────
// Inline SVG paths (lucide-style; no new deps per the redesign plan).
// Use `currentColor` so the consumer's text color carries through —
// section headers paint these gold via the gold-themed wrapper below.

const CREATE_ICON_USERS = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
)
const CREATE_ICON_TROPHY = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/>
    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
    <path d="M4 22h16"/>
    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>
    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/>
    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
  </svg>
)
const CREATE_ICON_CALENDAR = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
)

// Numbered section wrapper for the redesigned Create-Group modal. Pure
// JSX frame — children are passed through verbatim; no state, no
// handlers. The gold accent matches the rest of the app's selected-
// state language (operator-confirmed: adapt the reference's green to
// gold, no broader brand shift).
function CreateSection({ number, title, icon, badge, children }) {
  return (
    <div style={{
      background: 'rgba(20,24,32,0.55)',
      border: '1px solid rgba(255,255,255,0.06)',
      borderRadius: '14px',
      padding: '14px 14px 12px',
      marginBottom: '14px',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        marginBottom: '14px',
      }}>
        <span style={{ color: 'var(--gold)', display: 'inline-flex', alignItems: 'center' }}>
          {icon}
        </span>
        <span style={{
          fontSize: '12px', fontWeight: 700,
          color: 'var(--gold)',
          letterSpacing: '0.06em',
          fontFamily: 'Arial,sans-serif',
        }}>
          {number}. {title}
        </span>
        {badge && (
          <span style={{
            marginLeft: 'auto',
            fontSize: '9px', padding: '2px 6px', borderRadius: '3px',
            background: 'var(--surface2)', color: 'var(--text-dim)',
            fontFamily: 'Arial,sans-serif', letterSpacing: '0.5px',
          }}>
            {badge}
          </span>
        )}
      </div>
      {children}
    </div>
  )
}

// ── CreateGroupModal (standalone — avoids re-mount on every keystroke) ────────

function CreateGroupModal({ createForm, setCreateForm, createdGroup, setCreatedGroup, createError, setCreateError, handleCreateGroup, setShowCreate, openGroup, stripeEnabled, paidPathActive, preFilled }) {
  function setField(k) { return e => setCreateForm(f => ({ ...f, [k]: e.target.value })) }

  // Wrap submit so we can hard-block daily groups that fall under the per-sport
  // game-count minimum. Tie-prone slates make for unsatisfying contests.
  async function onFormSubmit(e) {
    if (isUFC && !ufcEvent) {
      e.preventDefault()
      setCreateError?.('No UFC event scheduled in the next 30 days. Check back closer to the next event.')
      return
    }
    // Hard floor: <10 pickable games always blocks for daily mode, every
    // sport. NFL gets an extra hint about Weekly mode in the error since
    // Thursday/Monday NFL only schedules 1 game and Daily mode genuinely
    // doesn't work there.
    if (showHardBlock) {
      e.preventDefault()
      const nflHint = createForm.sport === 'NFL'
        ? ' Use Weekly mode for Thursday/Monday NFL games.'
        : ''
      setCreateError?.(`Only ${selectedDateCount} ${createForm.sport} game${selectedDateCount !== 1 ? 's' : ''} available today. Daily groups require ${MIN_DAILY_GAMES}+ games to prevent ties.${nflHint}`)
      return
    }
    // Agreement gate (Enter-key defense — disabled button blocks click but
    // form-submit via Enter on a text input would otherwise bypass it).
    if (!agreementChecked) {
      e.preventDefault()
      setCreateError?.('You must agree to the pool terms before creating')
      return
    }
    // Live slate gate: even when the date/event has games on paper, every
    // one might already be locked/finished (operator created a UFC pool 30
    // minutes after the prelims started, or an MLB daily pool after first
    // pitch on every game). Fetch the actual slate and count !isGameLocked
    // — zero pickable means the pool would be dead on arrival. Submit-time
    // network call rather than continuous-state because the lock window
    // moves with wall-clock time, not just user input. Skipped for brackets
    // (lockout gate already enforced) and golf (own picker).
    if (isUFC || (isPickem && !isGolf && !isBracket)) {
      e.preventDefault()
      // Calendar-only UFC events: when the selected event is on ESPN's calendar
      // but not yet in scoreboard events[] (typical 1-2 weeks before fight
      // night), getGames('UFC',{eventId}) resolves to [] because
      // fetchUFCEventById only sees events[]. odds.js:1235-1240 documents this
      // as the EXPECTED pre-publish state — pools created now reconcile when
      // ESPN promotes the card. Skip the pickable gate for that case so the
      // banner doesn't claim "every fight finished" against a future event
      // whose fights haven't been published yet. ufcSchedule entries carry
      // hasCompetitions: false from fromCalendarEntry (odds.js:1273-1285).
      //
      // Default-on-missing is deliberate: if scheduleEntry is undefined
      // (event not found in schedule — shouldn't happen post-V1 verification
      // but defensive), the guard is false → falls through to the existing
      // pickable check → blocks. "Can't confirm calendar-only" must not
      // auto-skip.
      if (isUFC) {
        const scheduleEntry = ufcSchedule.find(
          x => String(x.id) === String(createForm.ufc_config?.eventId)
        )
        if (scheduleEntry && scheduleEntry.hasCompetitions === false) {
          handleCreateGroup(e, agreementChecked)
          return
        }
      }
      try {
        const isDaily = isPickem && createForm.duration_type === 'daily'
        // Thread the user's selected eventId into the UFC pickable check so
        // we evaluate the right event's fights rather than whatever
        // fetchNextUFCEvent picks (which can disagree with the dropdown
        // selection — e.g. when the user picks a later event in the
        // schedule). For calendar-only events the path above caught it
        // already, so this only matters for hasCompetitions=true events
        // the user explicitly selected past list[0].
        const games = isUFC
          ? await getGames('UFC', { eventId: createForm.ufc_config?.eventId })
          : (isDaily && createForm.start_date)
            ? await getGamesByDate(createForm.sport, createForm.start_date)
            : await getGames(createForm.sport)
        const pickable = (games || []).filter(g => !isGameLocked(g)).length
        if (pickable === 0) {
          const slate = isUFC
            ? 'fight'
            : isDaily ? "game on this date" : 'upcoming game'
          setCreateError?.(`No pickable ${slate}s — every ${isUFC ? 'fight' : 'game'} on the current slate has already started or finished. Try a later date or check back when the next event is scheduled.`)
          return
        }
      } catch (err) {
        // Network failure — degrade to permissive (don't block create on a
        // transient ESPN/odds outage); the lock check still runs per-game
        // at pick time via isGameLocked, so a dead pool isn't catastrophic.
        log('[create-pool] pickable-count check failed:', err?.message || err)
      }
      handleCreateGroup(e, agreementChecked)
      return
    }
    handleCreateGroup(e, agreementChecked)
  }

  // State must be declared before any derived values that read from it
  const [bracketStatus,  setBracketStatus]  = useState(null)
  const [statusLoading,  setStatusLoading]  = useState(false)
  const [bracketLockout, setBracketLockout] = useState(null)   // Build A: per-sport window state
  const [gameCounts,    setGameCounts]    = useState({})
  const [ufcEvent,      setUfcEvent]      = useState(null)
  const [ufcSchedule,   setUfcSchedule]   = useState([])  // UFC dropdown Phase 1: today+30-day window
  const [ufcLoading,    setUfcLoading]    = useState(false)
  // Tracks Promise.all rejection in the isUFC fetch effect below. Distinguishes
  // "fetch failed" from "fetch resolved with empty list" — both leave
  // createForm.ufc_config null, but the submit-button reason text + retry
  // hint should differ. Reset on every re-run of the effect (success path
  // sets it false implicitly via the unconditional pre-fetch reset).
  const [ufcError,      setUfcError]      = useState(false)
  const [golfSchedule,  setGolfSchedule]  = useState([])
  const [golfLoading,   setGolfLoading]   = useState(false)
  // Agreement checkbox state. Resets when an input the agreement text reads
  // changes: tier (creator-fee shift) or paidPathActive (creator-fee sentence
  // appears/disappears). entry_fee was dropped from the deps with the neutral-
  // app refactor — it no longer appears in the agreement text.
  const [agreementChecked, setAgreementChecked] = useState(false)
  useEffect(() => { setAgreementChecked(false) }, [createForm.tier, paidPathActive])

  // Main-Card-Only mode (contest_type 'pick_em_main_card_only') inherits
  // every pickem code path in the create modal — UFC event picker, agreement,
  // tier selector, submit button. The new mode behaves as a UFC pickem
  // variant at create-time; the picker-side mode-locked behavior lives in
  // PickemMode (Phase B half 2). Phase A's MAIN_CARD_COMP_IDS + eligibility
  // helpers are dormant until contest_type flips at the picker.
  const isMainCardOnly = createForm.contest_type === 'pick_em_main_card_only'
  const isPickem  = createForm.contest_type === 'pick_em' || isMainCardOnly
  const isBracket = createForm.contest_type === 'bracket'
  const isUFC     = createForm.sport === 'UFC'
  const isGolf    = createForm.sport === 'Golf'
  const isPlayoffPickem = isPickem && createForm.duration_type === 'playoffs'
  const pickemAvail  = isPickem  && createForm.sport ? getPickemAvailability(createForm.sport, createForm.duration_type) : null
  // Selected tier's fee (in dollars), or null when no tier picked yet. Used
  // by the agreement-checkbox text when paidPathActive is true to disclose
  // the creator fee the user is agreeing to be charged.
  const tierFee = TIERS.find(t => t.id === createForm.tier)?.fee

  // Daily-pickem floor: 10 pickable games for EVERY sport including NFL.
  // The previous NFL carve-out (5 games) let Thursday/Monday NFL slates
  // through and those single-game days create ties immediately. Users who
  // want to play Thursday/Monday NFL should use Weekly mode (no minimum)
  // instead of Daily.
  // selectedDateCount comes from getGameCountsByDay which filters today's
  // events to only those still >10min from start — so this counter already
  // reflects pickable games, not total scheduled.
  const MIN_DAILY_GAMES = 10
  const selectedDateCount = (Object.keys(gameCounts).length > 0 && createForm.start_date)
    ? (gameCounts[createForm.start_date] ?? null)
    : null
  const gateActive    = isPickem && createForm.duration_type === 'daily' && createForm.sport && selectedDateCount != null
  const showHardBlock = gateActive && selectedDateCount < MIN_DAILY_GAMES

  // UFC override: force duration_type='daily', skip game-count fetch, fetch
  // the next event metadata (for the banner) and sync start_date to it so
  // PickemMode's per-day filter catches the fight card.
  //
  // UFC dropdown Phase 1 (2026-06-03): also fetch the today+30-day upcoming-
  // events list via getUpcomingEvents('UFC', 30) (reuses the existing
  // staleness-guard window — does NOT introduce a new fetch layer), store
  // in ufcSchedule, and pre-select list[0] into createForm.ufc_config so
  // an untouched form still has a valid event reference. Mirrors golf's
  // pattern at lines 1610-1629. Persistence (basePayload threading, RPC
  // arg, migration, SELECT widening) lands in Phase 4 — ufc_config lives
  // in component state only for now.
  useEffect(() => {
    if (!isUFC) {
      setUfcEvent(null)
      setUfcSchedule([])
      // Hygiene: clear stale loading/error state from a prior UFC selection
      // so a fresh return to UFC doesn't start in a misleading state.
      setUfcLoading(false)
      setUfcError(false)
      return
    }
    setGameCounts({})
    setCreateForm(f => f.duration_type === 'daily' ? f : { ...f, duration_type: 'daily' })
    let cancelled = false
    setUfcLoading(true)
    setUfcError(false)
    // Parallel fetches against the same /api/espn/ufc/events proxy. ESPN's
    // response is small enough that two requests are cheaper than a
    // refactor of getNextUFCEvent to share the scoreboard payload.
    // Phase 3 may consolidate when the banner UI is finalized.
    Promise.all([
      getNextUFCEvent(),
      getUpcomingEvents('UFC', 30),
    ])
      .then(([ev, schedule]) => {
        if (cancelled) return
        setUfcEvent(ev)
        const list = Array.isArray(schedule) ? schedule : []
        setUfcSchedule(list)
        setUfcLoading(false)
        // list[0] pre-select. Mirrors golf at line 1619-1626. eventDate is
        // YYYY-MM-DD ET-anchored — toISOString().slice(0,10) would shift
        // evening-ET UFC cards (every main card) to the next UTC day (a
        // 10pm ET fight night on June 14 = 2:00 UTC June 15 → ISO slice
        // gives "2026-06-15"). toLocaleDateString('en-CA', {timeZone:
        // 'America/New_York'}) anchors to ET regardless of the viewer's
        // browser timezone, matching how UFC publicly labels its cards.
        // getUpcomingEvents (odds.js:1036) returns date as ms-epoch
        // (verified by source read, not the function's docstring).
        //
        // start_date deliberately uses the UTC slice (toISOString) NOT the
        // ET label: calendar-only entries (events[] not yet hydrated) carry
        // a date-only `date` field that JS parses as midnight UTC. ET-
        // shifting a midnight-UTC value lands on the day BEFORE the
        // published card (Kape `2026-06-21T00:00:00Z` → ET '2026-06-20').
        // The UTC slice preserves the published date for calendar entries
        // and matches the value the legacy ev.date path already produced
        // for current/events[] entries (Freedom 250 evening-ET = next-UTC-
        // day was already stored as '2026-06-15'). The asymmetry with
        // ufc_config.eventDate is intentional — that field is metadata for
        // display, start_date drives lockout / past-day / bucketing math.
        const firstEvent = list[0]
        if (firstEvent) {
          const startIso = new Date(firstEvent.date).toISOString().slice(0, 10)
          setCreateForm(f => ({
            ...f,
            start_date: startIso,
            ufc_config: {
              eventId:   firstEvent.id,
              eventName: firstEvent.name,
              eventDate: new Date(firstEvent.date).toLocaleDateString('en-CA', { timeZone: 'America/New_York' }),
            },
          }))
        }
        // If list[0] is undefined (empty schedule), ufc_config stays at its
        // initial null. The submit button is gated on createForm.ufc_config?.eventId
        // below so this path can't reach the create RPC with a null ufc_config.
      })
      .catch(err => {
        // Without this .catch, a rejection (ESPN proxy 5xx, network outage,
        // Avast TLS interception in dev) leaves ufcLoading stuck at true and
        // ufc_config at null with no signal to the user. The submit button's
        // ufcError-aware guard below blocks creation in this state; this
        // handler is the only path that flips ufcError true.
        if (cancelled) return
        log('[create-pool] UFC events fetch failed:', err?.message || err)
        setUfcLoading(false)
        setUfcError(true)
      })
    return () => { cancelled = true }
  }, [isUFC])

  // Golf override (Stage 4B): fetch the tournament schedule and auto-populate
  // createForm.golf_config with the first event + the always-on [1,2,3,4]
  // rounds preset. Every golf pool is full-tournament — the round-preset
  // selector was removed; the format invariant (R1-2 = 3ball, R3-4 = 2ball)
  // is still produced via deriveFormat. Same cancel pattern as UFC.
  // Critical: golf_config must be
  // populated on initial load (not just on user change) so a creator who
  // touches nothing still submits a valid config — Stage 3's basePayload
  // reads `createForm.golf_config ?? null`, and null on a Golf pool means
  // "config never got assembled," which is silent corruption.
  useEffect(() => {
    if (!isGolf) { setGolfSchedule([]); return }
    let cancelled = false
    setGolfLoading(true)
    fetchGolfSchedule().then(events => {
      if (cancelled) return
      const list = Array.isArray(events) ? events : []
      setGolfSchedule(list)
      setGolfLoading(false)
      const firstEvent = list[0]
      if (firstEvent) {
        const rounds = [1, 2, 3, 4]   // hardcoded — every golf pool is full-tournament
        setCreateForm(f => ({
          ...f,
          // Phase 3.5 deferred-snapshot: thread eventName + eventStartDate
          // into golf_config from the schedule. handleCreateGroup needs
          // eventStartDate for the start_date ≤ today hard-block, and the
          // divisions picker reads eventName + eventStartDate from
          // golf_config when rendering the awaiting-odds / tournament-
          // started placeholders.
          golf_config: {
            eventId:        firstEvent.eventId,
            eventName:      firstEvent.name        || '',
            eventStartDate: firstEvent.startDate   || null,
            rounds,
            format: deriveFormat(rounds),
          },
        }))
      }
    })
    return () => { cancelled = true }
  }, [isGolf])

  // Smart default: when sport+daily are picked, fetch per-day game counts for
  // the next 7 days and auto-select the busiest day. Skipped for UFC since
  // UFC has its own dedicated start_date logic above.
  useEffect(() => {
    if (isUFC) return
    if (!isPickem || createForm.duration_type !== 'daily' || !createForm.sport) {
      setGameCounts({})
      return
    }
    let cancelled = false
    getGameCountsByDay(createForm.sport, 7).then(counts => {
      if (cancelled) return
      setGameCounts(counts)
      let bestDay = null, bestCount = 0
      for (const [day, c] of Object.entries(counts)) {
        if (c > bestCount) { bestCount = c; bestDay = day }
      }
      if (bestDay) setCreateForm(f => ({ ...f, start_date: bestDay }))
    })
    return () => { cancelled = true }
  }, [isPickem, createForm.duration_type, createForm.sport, isUFC])

  useEffect(() => {
    if (!isBracket || !createForm.sport) {
      setBracketStatus(null)
      setBracketLockout(null)
      return
    }
    let cancelled = false
    setStatusLoading(true)
    // Parallel fetch — both helpers internally call fetchPlayoffBracket;
    // unmemoized double-fetch is acceptable for Build A per snapshot §4.
    // Future polish: 60s sport-keyed cache in playoffs.js dedups them.
    Promise.all([
      getPlayoffStatus(createForm.sport),
      getBracketLockout(createForm.sport),
    ]).then(([status, lockout]) => {
      if (cancelled) return
      setBracketStatus(status)
      setBracketLockout(lockout)
      setStatusLoading(false)
      if (status?.availableEntries?.length > 0) {
        const first = status.availableEntries[0]
        setCreateForm(f => ({ ...f, startRound: first.startRound, entryId: first.id }))
      }
    })
    return () => { cancelled = true }
  }, [isBracket, createForm.sport])

  if (createdGroup) return (
    <div className="modal-backdrop">
      <div className="modal">
        <div className="modal-handle" />
        <div style={{ textAlign:'center', padding:'16px 0' }}>
          <div style={{ fontSize:'48px', marginBottom:'12px' }}>🎉</div>
          <div style={{ fontSize:'22px', color:'var(--gold)', marginBottom:'8px' }}>{createdGroup.name}</div>
          <div style={{ color:'var(--text-dim)', marginBottom:'20px' }}>Group created!</div>
          <div style={{ background:'var(--gold-bg)', border:'1px solid rgba(0,230,118,0.3)', borderRadius:'12px', padding:'16px', marginBottom:'20px' }}>
            <div style={{ fontSize:'12px', color:'var(--text-dim)', marginBottom:'6px' }}>Share this invite code</div>
            <div style={{ fontSize:'32px', fontWeight:'bold', color:'var(--gold)', letterSpacing:'6px', fontFamily:'Arial,sans-serif' }}>{createdGroup.invite_code}</div>
          </div>
          <button className="btn btn-gold" onClick={() => { setShowCreate(false); setCreatedGroup(null); openGroup(createdGroup) }}>
            Go to Group
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && (setCreateError(''), setShowCreate(false))}>
      <div className="modal">
        <div className="modal-handle" />

        {/* Hero header — replaces the plain modal-title. Generic panorama
            (MLB twilight stadium, the existing asset that best matches
            the reference's stadium-silhouette vibe) anchored right with a
            left-fading scrim so title + subtitle stay readable in the
            dark left zone. Per-sport swap deferred per the redesign plan. */}
        <div style={{
          position: 'relative',
          borderRadius: '14px',
          overflow: 'hidden',
          background: `
            linear-gradient(90deg,
              rgba(13,16,22,0.96) 0%,
              rgba(13,16,22,0.92) 35%,
              rgba(13,16,22,0.55) 70%,
              rgba(13,16,22,0.20) 100%),
            url('/sport-bg/mlb.webp') right center/cover no-repeat
          `,
          padding: '22px 18px 18px',
          marginBottom: '16px',
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '26px', fontWeight: 800,
            color: 'var(--text)',
            fontFamily: 'inherit', letterSpacing: '-0.5px',
            lineHeight: 1.05,
          }}>
            Create Group
          </h2>
          <div style={{
            marginTop: '6px',
            fontSize: '13px',
            color: 'var(--text-dim)',
            fontFamily: 'Arial,sans-serif',
          }}>
            Set up your pool and challenge your crew
          </div>
        </div>

        <form onSubmit={onFormSubmit}>
          {/* §1 GROUP DETAILS — name + photo upload */}
          <CreateSection number={1} title="GROUP DETAILS" icon={CREATE_ICON_USERS}>
          <div className="form-group">
            <label className="form-label">Group Name</label>
            <div style={{ display:'flex', gap:'12px', alignItems:'center' }}>
              <GroupImageUpload
                imageUrl={createForm.image_url || ''}
                onChange={url => setCreateForm(f => ({ ...f, image_url: url }))}
              />
              <input placeholder="Office Pick'em Challenge" value={createForm.name} onChange={setField('name')} required style={{ flex: 1 }} />
            </div>
          </div>
          </CreateSection>

          {/* §2 CONTEST SETUP — contest type / sport / mode / event selectors */}
          <CreateSection number={2} title="CONTEST SETUP" icon={CREATE_ICON_TROPHY}>
          {/* Contest type — locked summary when picker pre-filled; selector otherwise */}
          {preFilled ? (
            <div className="form-group">
              <label className="form-label">Contest Type</label>
              <div style={{
                background:'var(--surface2)', border:'1px solid var(--border)',
                borderRadius:'10px', padding:'12px 14px',
                display:'flex', alignItems:'center', gap:'10px',
                color:'var(--text)',
              }}>
                <span style={{ fontSize:'14px', color:'var(--text-dim)' }}>🔒</span>
                <span style={{ fontSize:'18px' }}>{CONTEST_EMOJI[createForm.contest_type]}</span>
                <span style={{ fontSize:'14px', fontFamily:'inherit' }}>{CONTEST_LABEL[createForm.contest_type]}</span>
              </div>
            </div>
          ) : (
            <div className="form-group">
              <label className="form-label">Contest Type</label>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                {['pick_em','bracket'].map(ct => (
                  <button key={ct} type="button" onClick={() => { setCreateForm(f => ({ ...f, contest_type:ct, sport:'' })); setCreateError('') }}
                    style={{ background: createForm.contest_type===ct ? 'var(--gold-bg2)' : 'var(--surface2)', border:`2px solid ${createForm.contest_type===ct ? 'var(--gold)' : 'var(--border)'}`, borderRadius:'10px', padding:'12px 8px', cursor:'pointer', textAlign:'center', transition:'all .2s' }}>
                    <div style={{ fontSize:'20px', marginBottom:'4px' }}>{CONTEST_EMOJI[ct]}</div>
                    <div style={{ fontSize:'13px', color: createForm.contest_type===ct ? 'var(--gold)' : 'var(--text)', fontFamily:'inherit' }}>{CONTEST_LABEL[ct]}</div>
                    <div style={{ fontSize:'11px', color:'var(--text-muted)', marginTop:'2px' }}>
                      {ct === 'pick_em' ? 'Pick game winners' : 'Pick series winners'}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Sport — locked summary when picker pre-filled; selector otherwise */}
          {preFilled ? (
            <div className="form-group">
              <label className="form-label">Sport</label>
              <div style={{
                background:'var(--surface2)', border:'1px solid var(--border)',
                borderRadius:'10px', padding:'12px 14px',
                display:'flex', alignItems:'center', gap:'10px',
                color:'var(--text)',
              }}>
                <span style={{ fontSize:'14px', color:'var(--text-dim)' }}>🔒</span>
                <span style={{ fontSize:'14px', fontFamily:'inherit' }}>{createForm.sport}</span>
              </div>
            </div>
          ) : (
            <div className="form-group">
              <label className="form-label">
                Sport {isBracket && <span style={{ color:'var(--text-muted)', fontSize:'12px' }}>(must have active playoffs)</span>}
              </label>
              <select value={createForm.sport} onChange={e => { setField('sport')(e); setCreateError('') }} required>
                <option value="">Choose a sport…</option>
                {(isPickem ? PICKEM_SPORTS : BRACKET_SPORTS).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}

          {/* Bracket: entry point selector */}
          {isBracket && createForm.sport && (
            <div className="form-group">
              <label className="form-label">Entry Point</label>
              {statusLoading ? (
                <div style={{ textAlign:'center', padding:'12px', color:'var(--text-dim)', fontSize:'13px' }}>
                  Loading playoff status…
                </div>
              ) : bracketLockout?.locked ? (
                <div className="alert alert-error" style={{ fontSize:'12px' }}>
                  🔒 {bracketLockout.reason}
                </div>
              ) : bracketStatus?.availableEntries?.length > 0 ? (
                <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                  {bracketStatus.availableEntries.map(entry => {
                    const selected = createForm.entryId === entry.id
                    return (
                      <button key={entry.id} type="button"
                        onClick={() => setCreateForm(f => ({ ...f, startRound: entry.startRound, entryId: entry.id }))}
                        style={{ background: selected ? 'var(--gold-bg2)' : 'var(--surface2)',
                          border:`2px solid ${selected ? 'var(--gold)' : 'var(--border)'}`,
                          borderRadius:'10px', padding:'12px 14px', cursor:'pointer', textAlign:'left', transition:'all .2s' }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'4px' }}>
                          <span style={{ fontSize:'14px', fontFamily:'inherit', color: selected ? 'var(--gold)' : 'var(--text)', fontWeight: selected ? 'bold' : 'normal' }}>
                            {entry.label}
                          </span>
                          <span style={{ fontSize:'12px', color:'var(--gold)', fontFamily:'Arial,sans-serif', fontWeight:'bold' }}>
                            Max {entry.maxPoints} pts
                          </span>
                        </div>
                        <div style={{ fontSize:'12px', color:'var(--text-dim)' }}>{entry.description}</div>
                      </button>
                    )
                  })}
                </div>
              ) : bracketStatus ? (
                <div className="alert alert-error" style={{ fontSize:'12px' }}>
                  🚫 No active playoff entries for {createForm.sport}
                </div>
              ) : null}
            </div>
          )}

          {/* Pick'em: duration type selector — hidden for UFC + Golf (single-event configs).
              Weekly and Playoffs are disabled at the form layer because they ride a
              duration_type DB column that does not exist (no migration creates it).
              Supabase silently drops the unknown field on insert, so selecting either
              one produced a single-day Daily pool with a misleading label. The dormant
              PickemMode branches that handle 'playoffs' / weekly fetch (lines 240-241,
              267, 285, 314) stay in place — they're harmless and re-activate when the
              real feature lands (schema migration + multi-day getGamesInRange +
              PickemMode weekly branch + rolling per-game lockout). */}
          {isPickem && !isUFC && !isGolf && (
            <div className="form-group">
              <label className="form-label">Pick'em Mode</label>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'6px' }}>
                {[
                  { id:'daily',    label:'Daily',    sub:"Pick today's games",        available: true  },
                  { id:'weekly',   label:'Weekly',   sub:'Coming for NFL season',     available: false },
                  { id:'playoffs', label:'Playoffs', sub:'Available in Bracket mode', available: false },
                ].map(opt => {
                  const isActive = createForm.duration_type === opt.id
                  const disabled = !opt.available
                  return (
                    <button key={opt.id} type="button"
                      disabled={disabled}
                      onClick={disabled ? undefined : () => setCreateForm(f => ({ ...f, duration_type: opt.id, sport: opt.id === 'playoffs' ? '' : f.sport }))}
                      style={{ background: isActive ? 'var(--gold-bg2)' : 'var(--surface2)', border:`2px solid ${isActive ? 'var(--gold)' : 'var(--border)'}`, borderRadius:'10px', padding:'10px 6px', cursor: disabled ? 'not-allowed' : 'pointer', textAlign:'center', transition:'all .2s', opacity: disabled ? 0.45 : 1 }}>
                      <div style={{ fontSize:'13px', color: isActive ? 'var(--gold)' : 'var(--text)', fontFamily:'inherit', fontWeight: isActive ? 'bold' : 'normal' }}>{opt.label}</div>
                      <div style={{ fontSize:'10px', color:'var(--text-muted)', marginTop:'3px', lineHeight:'1.3' }}>{opt.sub}</div>
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* UFC card info — replaces both the duration and date selectors. */}
          {/* UFC event picker — mirrors golf's tournament dropdown at lines
              ~1922-1996. Dropdown replaces the old fixed UFCEventCard banner
              (two event surfaces in one modal read as duplication / bug-when-
              they-disagree). ufcEvent + getNextUFCEvent kept under-the-hood
              for the start_date sync at lines 1638-1641 — Phase 5 will
              redirect that to the dropdown's selected event. */}
          {isUFC && (
            <>
              {ufcLoading && (
                <div style={{ marginBottom:'14px', padding:'10px 14px', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:8, color:'var(--text-dim)', fontSize:12, textAlign:'center' }}>
                  Loading UFC schedule…
                </div>
              )}
              {!ufcLoading && ufcSchedule.length > 0 && (
                <>
                  <div className="form-group">
                    <label className="form-label">UFC Event</label>
                    <select
                      value={createForm.ufc_config?.eventId || ''}
                      onChange={e => {
                        const eventId = e.target.value
                        const ev = ufcSchedule.find(x => String(x.id) === String(eventId))
                        if (!ev) return
                        // Same ET-anchored YYYY-MM-DD normalization as the
                        // Phase 1 list[0] pre-select at line 1635 — so a user-
                        // selected event and an untouched-default event store
                        // identical ufc_config shape. start_date uses the UTC
                        // slice (see init-effect comment above) so both write
                        // sites can't drift: default and manual selection
                        // produce the SAME start_date for the SAME event.
                        const startIso = new Date(ev.date).toISOString().slice(0, 10)
                        setCreateForm(f => ({
                          ...f,
                          start_date: startIso,
                          ufc_config: {
                            eventId:   ev.id,
                            eventName: ev.name,
                            eventDate: new Date(ev.date).toLocaleDateString('en-CA', { timeZone: 'America/New_York' }),
                          },
                        }))
                      }}
                    >
                      {ufcSchedule.map(ev => {
                        // Same ET anchor for the visible label — keeps dropdown
                        // display consistent with the stored eventDate so
                        // "what did I pick?" matches "what got saved."
                        const labelDate = new Date(ev.date).toLocaleDateString('en-CA', { timeZone: 'America/New_York' })
                        return (
                          <option key={ev.id} value={ev.id}>
                            {ev.name} · {labelDate}
                          </option>
                        )
                      })}
                    </select>
                  </div>
                  <div className="alert alert-info" style={{ marginBottom:'14px', fontSize:'12px' }}>
                    Prelim: 1pt · Main Card: 2pts · Co-Main: 3pts · Main Event: 5pts · Title Fight: 7pts
                  </div>
                </>
              )}
              {!ufcLoading && ufcSchedule.length === 0 && (
                <div className="alert alert-error" style={{ marginBottom:'14px', fontSize:'12px', lineHeight:'1.5' }}>
                  🥊 No UFC event scheduled in the next 30 days. Check back closer to the next event.
                </div>
              )}
            </>
          )}

          {/* Golf tournament selector — replaces duration/date for golf pools.
              Every pool is full-tournament (rounds=[1,2,3,4]); no per-pool
              round selection. The 3ball/2ball format invariant is derived
              via deriveFormat on each setCreateForm call. */}
          {isGolf && (
            <>
              {golfLoading && (
                <div style={{ marginBottom:'14px', padding:'10px 14px', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:8, color:'var(--text-dim)', fontSize:12, textAlign:'center' }}>
                  Loading tournament schedule…
                </div>
              )}
              {!golfLoading && golfSchedule.length > 0 && (
                <>
                  <div className="form-group">
                    <label className="form-label">Tournament</label>
                    <select
                      value={createForm.golf_config?.eventId || ''}
                      onChange={e => {
                        const eventId = e.target.value
                        // Pull eventName + eventStartDate from the schedule
                        // entry the user just picked — needed by Phase 3.5
                        // deferred-snapshot (hard-block + picker placeholders).
                        const selected = golfSchedule.find(ev => ev.eventId === eventId) || {}
                        setCreateForm(f => ({
                          ...f,
                          golf_config: {
                            eventId,
                            eventName:      selected.name      || '',
                            eventStartDate: selected.startDate || null,
                            rounds: [1, 2, 3, 4],
                            format: deriveFormat([1, 2, 3, 4]),
                          },
                        }))
                      }}
                    >
                      {golfSchedule.map(ev => (
                        <option key={ev.eventId} value={ev.eventId}>
                          {ev.name}{ev.startDate ? ` · ${ev.startDate}` : ''}
                        </option>
                      ))}
                    </select>
                    {/* Mode-specific subtitle. Was a single hardcoded matchup
                        line ("R1/2 play in 3-balls; R3/4 play in 2-balls") that
                        bled onto divisions pools — gated on isGolf with no
                        contest_type branch. Now each mode has its own subtitle,
                        and neither renders for the wrong mode. Tournament
                        <select> and golf_config write above are unchanged. */}
                    {createForm.contest_type === 'pick_em' && (
                      <div style={{ fontSize:'11px', color:'var(--text-muted)', marginTop:'6px', fontFamily:'Arial,sans-serif' }}>
                        Covers all four rounds — R1/2 play in 3-balls; R3/4 play in 2-balls (post-cut).
                      </div>
                    )}
                    {createForm.contest_type === 'divisions' && (
                      <div style={{ fontSize:'11px', color:'var(--text-muted)', marginTop:'6px', fontFamily:'Arial,sans-serif' }}>
                        6 tiers by odds — pick one golfer per tier (T1–T5 = 10 each, T6 = remainder); best 4 of 6 rounds count.
                      </div>
                    )}
                  </div>
                </>
              )}
              {!golfLoading && golfSchedule.length === 0 && (
                <div className="alert alert-error" style={{ marginBottom:'14px', fontSize:'12px', lineHeight:'1.5' }}>
                  ⛳ No upcoming tournaments. Check back closer to the next event.
                </div>
              )}
            </>
          )}
          </CreateSection>

          {/* §3 SCHEDULE — conditional: only renders for Daily Pick'em on
              non-UFC / non-Golf. UFC and Golf have their event selectors
              inside §2 CONTEST SETUP (the event IS the schedule for those).
              Predicate appears ONCE on the outer wrapper; inner content is
              the unconditional date selector form-group. */}
          {isPickem && !isUFC && !isGolf && createForm.duration_type === 'daily' && (
          <CreateSection number={3} title="SCHEDULE" icon={CREATE_ICON_CALENDAR}>
            <div className="form-group">
              <label className="form-label">Pick Date</label>
              <DateSelector value={createForm.start_date} onChange={iso => setCreateForm(f => ({ ...f, start_date: iso }))} gameCounts={gameCounts} />
              <div style={{ fontSize:'11px', color:'var(--text-muted)', marginTop:'6px', fontFamily:'Arial,sans-serif' }}>
                {Object.keys(gameCounts).length > 0
                  ? '🟢 10+  ·  🟡 5–9  ·  🔴 <5 games scheduled'
                  : 'Group runs for the games on this day.'}
              </div>
              {showHardBlock && (
                <div className="alert alert-error" style={{ marginTop:'8px', fontSize:'12px', lineHeight:'1.5' }}>
                  🚫 Only {selectedDateCount} {createForm.sport} game{selectedDateCount !== 1 ? 's' : ''} available today.
                  Daily groups require {MIN_DAILY_GAMES}+ games to prevent ties.
                  {createForm.sport === 'NFL' && (
                    <> Use Weekly mode for Thursday/Monday NFL games.</>
                  )}
                </div>
              )}
            </div>
          </CreateSection>
          )}

          {/* Playoff Pick'em: points info */}
          {isPlayoffPickem && (
            <div className="alert alert-info" style={{ marginBottom:'14px', fontSize:'12px', lineHeight:'1.5' }}>
              🏆 Pick the winner of every playoff game. Points increase each round.<br/>
              Round 1: 1pt · Round 2: 2pts · Conf Finals: 4pts · Finals: 8pts
            </div>
          )}

          {/* Availability feedback */}
          {isPickem && createForm.sport && pickemAvail && !pickemAvail.available && (
            <div className="alert alert-error" style={{ marginBottom:'14px', fontSize:'12px' }}>
              🚫 {pickemAvail.reason}
            </div>
          )}
          {isPlayoffPickem && createForm.sport && pickemAvail?.available && (
            <div className="alert alert-info" style={{ marginBottom:'14px', fontSize:'12px' }}>
              ✅ Active playoffs detected for {createForm.sport}
            </div>
          )}

          {/* Bracket: duration auto-set */}
          {isBracket && (
            <div className="form-group">
              <label className="form-label">Duration</label>
              <div style={{ background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'13px 16px', color:'var(--text-dim)', fontSize:'15px', display:'flex', alignItems:'center', gap:'8px' }}>
                🔒 Playoffs (auto-set)
              </div>
            </div>
          )}

          {/* Agreement checkbox — Pool Party is a scorekeeper. Creation is
              free; fee disclosure lives at the unlock surface (LockedShell)
              because anyone in the pool can be the one who pays. */}
          <div style={{ marginBottom:'14px' }}>
            <label style={{
              display:'flex', alignItems:'flex-start', gap:'8px', cursor:'pointer',
              fontSize:'12px', color:'var(--text-dim)', lineHeight:1.5,
              fontFamily:'Arial,sans-serif',
              padding:'10px 12px',
              borderRadius: 8,
              border: `1px solid ${agreementChecked ? 'var(--gold)' : 'transparent'}`,
              background: agreementChecked ? 'var(--gold-bg)' : 'transparent',
              transition: 'background 0.15s, border-color 0.15s',
            }}>
              <input
                type="checkbox"
                checked={agreementChecked}
                onChange={e => setAgreementChecked(e.target.checked)}
                style={{ width:18, height:18, marginTop:'2px', flexShrink:0, accentColor:'var(--gold)' }}
              />
              <span>
                I understand Pool Party is a scorekeeper. I agree to the{' '}<a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gold)' }}>Terms</a>.
              </span>
            </label>
          </div>

          {createError && <div className="alert alert-error" style={{ marginBottom:'14px' }}>{createError}</div>}

          {/* UFC create-time pre-select guard. Blocks submit when ufc_config
              would land in the DB as null — see HANDOFF "🟡 UNMERGED 2026-06-04
              — UFC event dropdown" and the broader bait-and-switch analysis:
              a NULL groups.ufc_config causes PickemMode to silently fall back
              to fetchNextUFCEvent() on every load, which can flip the pool's
              displayed card mid-life when ESPN's "next event" rolls over. The
              fix is to never write null at create time. The three paths that
              produced null pre-this-guard were (1) ESPN proxy returned empty
              list, (2) Promise.all rejected with no .catch, (3) user clicked
              Create before the fetch resolved. All three now hold the button
              disabled with the reason text below.
          */}
          {(() => {
            const ufcButtonReason = isUFC
              ? (
                ufcLoading                          ? 'Loading events…'
                : ufcError                          ? "Couldn't load events"
                : !createForm.ufc_config?.eventId   ? 'No upcoming UFC events'
                : null
              )
              : null
            const ufcSubmitBlocked = ufcButtonReason != null
            return (
              <>
                <button
                  className="btn btn-gold"
                  type="submit"
                  disabled={
                    !agreementChecked
                    || (createForm.contest_type === 'bracket' && bracketLockout?.locked)
                    || ufcSubmitBlocked
                  }
                >Create Group</button>
                {ufcButtonReason && (
                  <div style={{ fontSize:12, color:'var(--text-dim)', textAlign:'center', marginTop:6, fontFamily:'Arial,sans-serif' }}>
                    {ufcButtonReason}
                  </div>
                )}
              </>
            )
          })()}
          <button className="btn btn-ghost" type="button" onClick={() => { setShowCreate(false); setCreateError('') }} style={{ marginTop:'8px' }}>Cancel</button>
        </form>
      </div>
    </div>
  )
}

// ── Join modal (module-scope so its identity is stable across App re-renders)
// Defining it inside App previously caused React to remount the entire modal
// (and its <input>) on every keystroke, which dismissed the mobile keyboard.

// ── Password reset modal — opened after a recovery-link click. Module-scope
// so the controlled inputs don't remount on every keystroke (the same
// keyboard-dismiss footgun that JoinGroupModal had before it was hoisted).

function PasswordResetModal({ onClose, onDone }) {
  const [newPwd, setNewPwd] = useState('')
  const [confirm, setConfirm] = useState('')
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    setErr('')
    if (newPwd.length < 6) { setErr('Password must be at least 6 characters'); return }
    if (newPwd !== confirm) { setErr('Passwords do not match'); return }
    setLoading(true)
    try {
      const { error } = await supabase.auth.updateUser({ password: newPwd })
      if (error) setErr(error.message)
      else onDone?.()
    } catch (e2) {
      setErr(e2?.message || 'Could not update password')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-handle" />
        <div className="modal-title">Set New Password</div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">New password</label>
            <input type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)}
              placeholder="At least 6 characters" autoComplete="new-password" />
          </div>
          <div className="form-group">
            <label className="form-label">Confirm password</label>
            <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
              placeholder="Repeat the new password" autoComplete="new-password" />
          </div>
          {err && <div className="alert alert-error">{err}</div>}
          <button className="btn btn-gold" type="submit" disabled={loading}>
            {loading ? 'Updating…' : 'Update Password'}
          </button>
          <button className="btn btn-ghost" type="button" onClick={onClose} style={{ marginTop:'8px' }}>Cancel</button>
        </form>
      </div>
    </div>
  )
}

// In-app account deletion confirmation. Type-DELETE friction gates the
// confirm button; the actual DELETE call lives in src/lib/db.js
// deleteAccount(). Server side is DELETE /api/account (server/index.js).
// Required for App Store 5.1.1(v) + Google Play data-deletion compliance.
function DeleteAccountModal({ onClose, onDeleted }) {
  const [typed, setTyped]     = useState('')
  const [err, setErr]         = useState('')
  const [loading, setLoading] = useState(false)

  // Exact uppercase match. Any other value (lowercase, padded, suffixed)
  // keeps the confirm disabled.
  const armed = typed === 'DELETE'

  async function handleConfirm() {
    if (!armed || loading) return
    setErr('')
    setLoading(true)
    const result = await deleteAccount()
    // 200 ok, 401 (session already invalid), or 'no-session' (token was
    // gone before we even fetched) all mean the goal state is reached
    // OR can only be reached by signing out. Either way, route to Auth.
    // 'no-session' specifically prevents a dead-end retry loop on a
    // stranded session.
    if (result.ok || result.status === 401 || result.status === 'no-session') {
      await onDeleted()
      return
    }
    setLoading(false)
    if (result.status === 403) {
      // Sentinel refusal. A real user can't trip this; surface a
      // generic message and keep the modal open without signing out.
      setErr('Something went wrong. Please contact support.')
      return
    }
    // 500, 503, 'network', 'no-supabase'. Endpoint is retry-safe.
    setErr("Couldn't delete your account, please try again.")
  }

  return (
    <div className="modal-backdrop"
         onClick={e => e.target === e.currentTarget && !loading && onClose()}>
      <div className="modal">
        <div className="modal-handle" />
        <div className="modal-title">Delete Account</div>

        <div style={{ fontSize:'14px', lineHeight:1.5, color:'var(--text)', marginBottom:'14px' }}>
          This action is <strong>permanent and cannot be undone</strong>. Your account
          and all personal data will be removed from Pool Party Sports.
        </div>
        <div style={{ fontSize:'13px', lineHeight:1.5, color:'var(--text-dim)', marginBottom:'18px' }}>
          Pools you created that others joined will remain active for those members,
          but your name will be removed from them. Solo pools (no other members) will
          be deleted.
        </div>

        <div className="form-group">
          <label className="form-label">Type DELETE to confirm</label>
          <input
            type="text"
            value={typed}
            onChange={e => setTyped(e.target.value)}
            placeholder="DELETE"
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            disabled={loading}
          />
        </div>

        {err && <div className="alert alert-error">{err}</div>}

        <button
          className="btn btn-danger"
          type="button"
          disabled={!armed || loading}
          onClick={handleConfirm}
        >
          {loading ? 'Deleting…' : 'Delete My Account'}
        </button>
        <button
          className="btn btn-ghost"
          type="button"
          onClick={onClose}
          disabled={loading}
          style={{ marginTop:'8px' }}
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

function JoinGroupModal({ inviteInput, setInviteInput, joinError, setJoinError, setShowJoin, handleJoinGroup }) {
  // Pool preview state — local, derived from inviteInput. Fetched via
  // get_pool_preview RPC (migration 016) when the input reaches a
  // full-length invite code; reset when the input shrinks below.
  // Lives here rather than App body because it's purely session-disposable
  // and the modal already manages other input-derived UI state implicitly.
  const [preview,          setPreview]          = useState(null)
  const [previewLoading,   setPreviewLoading]   = useState(false)
  const [previewError,     setPreviewError]     = useState(null)
  // Build A — per-sport bracket-window lockout state for the previewed
  // pool. Fetched only when preview reveals contest_type === 'bracket'
  // for a known BRACKET_SPORTS sport. Null when not-yet-fetched, not-
  // applicable, or pre-preview.
  const [joinLockout,      setJoinLockout]      = useState(null)
  // Agreement checkbox state — backlog 1(c). Reset every time the
  // preview re-fetches: the user agreed to the prior pool, not this one.
  const [agreementChecked, setAgreementChecked] = useState(false)

  useEffect(() => {
    // Trigger on length >= 6 (production codes are exactly 6; demo seed
    // includes one 7-char code). Below 6, reset and bail.
    // Agreement always resets — fresh preview = fresh agreement.
    setAgreementChecked(false)
    setJoinLockout(null)
    if (inviteInput.length < 6) {
      setPreview(null)
      setPreviewError(null)
      setPreviewLoading(false)
      return
    }

    let cancelled = false
    setPreviewLoading(true)
    setPreviewError(null)

    const timer = setTimeout(async () => {
      try {
        const result = await getPoolPreview(inviteInput)
        if (cancelled) return
        if (result?.ok) {
          setPreview(result)
        } else {
          setPreview(null)
          setPreviewError(
            result?.reason === 'group missing'
              ? 'Group not found — check the invite code'
              : result?.reason === 'not authenticated'
                ? 'Please sign in to join a group'
                : 'Could not load pool preview'
          )
        }
      } catch (err) {
        if (cancelled) return
        setPreview(null)
        setPreviewError('Could not load pool preview')
      } finally {
        if (!cancelled) setPreviewLoading(false)
      }
    }, 250)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [inviteInput])

  // Build A — fire the bracket lockout check only for bracket pools on a
  // bracketable sport. NCAA sports appear in BRACKET_SPORTS as dropdown
  // options but lack real bracket data (see HANDOFF key-conventions);
  // for those, getBracketLockout would return pre-matchups every time.
  // Other contest_types (pick_em) are entirely out of scope for Build A.
  useEffect(() => {
    if (!preview || preview.contest_type !== 'bracket' || !BRACKET_SPORTS.includes(preview.sport)) {
      setJoinLockout(null)
      return
    }
    let cancelled = false
    getBracketLockout(preview.sport).then(lockout => {
      if (cancelled) return
      setJoinLockout(lockout)
    })
    return () => { cancelled = true }
  }, [preview])

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && setShowJoin(false)}>
      <div className="modal">
        <div className="modal-handle" />
        <div className="modal-title">Join Group</div>
        <p style={{ color:'var(--text-dim)', fontSize:'14px', marginBottom:'16px' }}>
          Enter the 6-character invite code shared by the group creator.
        </p>
        <div className="form-group">
          <label className="form-label">Invite Code</label>
          <input
            placeholder="e.g. ABC123"
            value={inviteInput}
            onChange={e => setInviteInput(e.target.value.toUpperCase())}
            maxLength={6}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="characters"
            spellCheck="false"
            inputMode="text"
            style={{ letterSpacing:'4px', fontSize:'20px', textAlign:'center', fontFamily:'Arial,sans-serif' }}
          />
        </div>

        {previewLoading && (
          <div style={{
            background:'var(--surface2)', border:'1px solid var(--border)',
            borderRadius:10, padding:'12px 14px', marginBottom:'12px',
            fontSize:'13px', color:'var(--text-dim)', textAlign:'center',
          }}>
            Loading pool preview…
          </div>
        )}
        {previewError && (
          <div className="alert alert-error" style={{ marginBottom:'12px' }}>{previewError}</div>
        )}
        {preview && (
          <div style={{
            background:'var(--surface2)', border:'1px solid var(--border)',
            borderRadius:10, padding:'12px 14px', marginBottom:'12px',
          }}>
            <div style={{ fontSize:'17px', fontWeight:'bold', marginBottom:'4px' }}>{preview.name}</div>
            <div style={{ fontSize:'12px', color:'var(--text-dim)', marginBottom:'10px' }}>
              {CONTEST_EMOJI[preview.contest_type] || ''} {CONTEST_LABEL[preview.contest_type] || preview.contest_type} · {preview.sport}
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end', fontSize:'13px', color:'var(--text-dim)' }}>
              <span style={{ fontFamily:'Arial,sans-serif' }}>
                {preview.member_count}{preview.max_players ? ` / ${preview.max_players}` : ''} {preview.member_count === 1 ? 'member' : 'members'}
              </span>
            </div>
            {preview.status === 'archived' && (
              <div style={{
                marginTop:'10px', background:'rgba(255,255,255,0.04)',
                border:'1px solid var(--border)', borderRadius:8,
                padding:'8px 10px', fontSize:'12px',
                color:'var(--text-dim)', textAlign:'center',
              }}>
                This group has been archived.
              </div>
            )}
            {preview.already_member && (
              <div style={{
                marginTop:'10px', background:'var(--gold-bg)',
                border:'1px solid var(--gold)', borderRadius:8,
                padding:'8px 10px', fontSize:'12px',
                color:'var(--gold)', textAlign:'center',
              }}>
                You're already in this group.
                {/* TODO C4: "Open Group" action button — needs openGroup prop
                    + group id surfaced from the preview RPC. */}
              </div>
            )}
          </div>
        )}

        {/* Agreement checkbox — backlog 1(c). Renders only for an active
            non-already-member preview; text branches on entry_fee. */}
        {preview && !preview.already_member && preview.status === 'active' && (
          <div style={{ marginBottom:'12px' }}>
            <label style={{
              display:'flex', alignItems:'flex-start', gap:'8px', cursor:'pointer',
              fontSize:'12px', color:'var(--text-dim)', lineHeight:1.5,
              fontFamily:'Arial,sans-serif',
              padding:'10px 12px',
              borderRadius: 8,
              border: `1px solid ${agreementChecked ? 'var(--gold)' : 'transparent'}`,
              background: agreementChecked ? 'var(--gold-bg)' : 'transparent',
              transition: 'background 0.15s, border-color 0.15s',
            }}>
              <input
                type="checkbox"
                checked={agreementChecked}
                onChange={e => setAgreementChecked(e.target.checked)}
                style={{ width:18, height:18, marginTop:'2px', flexShrink:0, accentColor:'var(--gold)' }}
              />
              <span>
                I understand Pool Party is a scorekeeper. I agree to the{' '}
                <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gold)' }}>Terms</a>.
              </span>
            </label>
          </div>
        )}

        {joinLockout?.locked && (
          <div className="alert alert-error" style={{ marginBottom:'12px', fontSize:'12px' }}>
            🔒 {joinLockout.reason}
          </div>
        )}
        {joinError && <div className="alert alert-error" style={{ marginBottom:'12px' }}>{joinError}</div>}
        <button
          className="btn btn-gold"
          onClick={() => handleJoinGroup(agreementChecked)}
          disabled={
            inviteInput.length < 6 ||
            !preview ||
            preview.already_member ||
            preview.status !== 'active' ||
            !agreementChecked ||
            joinLockout?.locked
          }
        >Join Group</button>
        <button className="btn btn-ghost" onClick={() => { setShowJoin(false); setJoinError(''); setInviteInput('') }} style={{ marginTop:'8px' }}>Cancel</button>
      </div>
    </div>
  )
}

// ── Share modal ────────────────────────────────────────────────────────────────

function ShareModal({ group, onClose }) {
  const [copied, setCopied] = useState('')
  const code = group.invite_code
  const link = `Join my Pool Party pool! Go to poolpartysports.org and use code: ${code}`

  async function copyText(text, kind) {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(kind)
      setTimeout(() => setCopied(''), 1800)
    } catch {}
  }

  async function shareLink() {
    if (navigator.share) {
      try { await navigator.share({ title: 'Pool Party', text: link }); return } catch {}
    }
    copyText(link, 'link')
  }

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-handle" />
        <div className="modal-title">Share Invite</div>
        <div style={{ background:'var(--gold-bg)', border:'1px solid rgba(0,230,118,0.3)', borderRadius:'12px', padding:'18px', textAlign:'center', marginBottom:'16px' }}>
          <div style={{ fontSize:'12px', color:'var(--text-dim)', marginBottom:'6px' }}>Invite code for {group.name}</div>
          <div style={{ fontSize:'34px', fontWeight:'bold', color:'var(--gold)', letterSpacing:'6px', fontFamily:'Arial,sans-serif' }}>{code}</div>
        </div>
        <button className="btn btn-gold" onClick={() => copyText(code, 'code')}>
          {copied === 'code' ? '✅ Copied' : 'Copy Invite Code'}
        </button>
        <button className="btn btn-ghost" onClick={shareLink} style={{ marginTop:'8px' }}>
          {copied === 'link' ? '✅ Link Copied' : '📤 Share Link'}
        </button>
        <button className="btn btn-ghost" onClick={onClose} style={{ marginTop:'8px' }}>Close</button>
      </div>
    </div>
  )
}

// ── Results: single-pick card render ─────────────────────────────────────────
// Pure JSX-returning helper (not a React component — lighter, no props
// interface). Shared by ResultsFeed (View 1, own picks) and
// MemberResultsSheet (View 2, another member's picks via the cross-member
// graded SELECT policy from migration 025). Keeping the defensive winner
// inference here means the two views can't drift apart.
function renderResultPick(p) {
  const isCorrect = p.result === 'correct'
  // Binary-outcome winner inference (snapshot §16), defensive against
  // name drift: only infer "the other side" when picked_team_name matches
  // exactly one of home/away. If neither matches (ESPN name-order drift —
  // same fragility the grader-matching fix handles server-side), leave
  // winner null — a confidently-wrong name is worse than none.
  const matchesHome = p.picked_team_name === p.home_team
  const matchesAway = p.picked_team_name === p.away_team
  let actualWinner = null
  if (isCorrect) {
    actualWinner = p.picked_team_name
  } else if (matchesHome && !matchesAway) {
    actualWinner = p.away_team
  } else if (matchesAway && !matchesHome) {
    actualWinner = p.home_team
  }  // else: ambiguous — leave null, render result without winner name
  const earnedPts = isCorrect ? (p.points_earned || p.round_points || 1) : 0

  return (
    <div key={p.id} className="result-card">
      <div className="result-card__header">
        <span className="result-card__final">FINAL</span>
        {p.game_date && <span className="result-card__date">{p.game_date}</span>}
      </div>

      {/* Matchup — winner highlighted in accent. When game_results is joined,
          the center column shows the score (team sports) or method/round/time
          (UFC). When null, falls through to "@" — preserves the pre-game-
          results layout byte-identical so any pre-existing graded pick still
          renders the same way it always did. */}
      {(() => {
        const r = p.game_results
        const isUFC = r?.sport === 'ufc' || r?.sport === 'UFC'
        let centerLine = '@'
        if (r) {
          if (isUFC) {
            const m = r.details?.method
            const round = r.details?.round
            const time  = r.details?.time_of_finish
            if (m && round != null && time) {
              centerLine = `${m} · R${round} · ${time}`
            } else if (m) {
              centerLine = m
            } else {
              centerLine = '@'
            }
          } else {
            const hs = r.home_score
            const as = r.away_score
            if (hs != null && as != null) {
              centerLine = `${as} — ${hs}`  // away first to match away @ home column order
            }
          }
        }
        return (
          <div className="result-card__matchup">
            <div className={`result-card__team${p.away_team === actualWinner ? ' winner' : ''}`}>
              {p.away_team}
            </div>
            <div className={`result-card__center${isUFC ? ' ufc' : ''}`}>
              {centerLine}
            </div>
            <div className={`result-card__team${p.home_team === actualWinner ? ' winner' : ''}`}>
              {p.home_team}
            </div>
          </div>
        )
      })()}

      {/* Result banner — colors flow from --success-bg/--error-bg tokens. */}
      <div className={`result-card__banner ${isCorrect ? 'correct' : 'wrong'}`}>
        <span>
          {isCorrect
            ? `✓ CORRECT — Picked ${p.picked_team_name}`
            : `✗ WRONG — Picked ${p.picked_team_name}${actualWinner ? ` · Winner: ${actualWinner}` : ''}`}
        </span>
        {isCorrect && <span className="result-card__banner-pts">+{earnedPts}pt</span>}
      </div>
    </div>
  )
}

// ── Results feed ──────────────────────────────────────────────────────────────
// View 1 of the Results tab: shows the CURRENT USER's graded picks for this
// group, sourced from the picks table (permanent, ESPN-independent) instead
// of getGames(sport) (current-window only — aged-out events disappeared and
// produced the "always empty Results" symptom). See
// .claude/results-view-snapshot.md §11-§18 for the full investigation, and
// §13/§15/§16 for the read-side rationale.
//
// View 2 (the cross-member drill-down) lives in MemberResultsSheet —
// triggered from MembersList row clicks, backed by migration 025's
// cross-member SELECT policy.

function ResultsFeed({ group, user, members: _members }) {
  const [picks, setPicks]     = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user?.id || !group?.id) { setPicks([]); setLoading(false); return }
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const data = await loadGradedPicks(group.id, user.id)
      if (cancelled) return
      setPicks(data)
      setLoading(false)
    })()
    return () => { cancelled = true }
  }, [group?.id, user?.id])

  if (loading) return (
    <div style={{ textAlign:'center', padding:'40px', color:'var(--text-dim)' }}>
      <div style={{ width:'24px', height:'24px', border:'2px solid var(--border)', borderTopColor:'var(--gold)', borderRadius:'50%', margin:'0 auto 12px', animation:'spin .8s linear infinite' }} />
      Loading results…
    </div>
  )

  if (picks.length === 0) return (
    <div style={{ textAlign:'center', padding:'40px 16px', color:'var(--text-dim)' }}>
      <div style={{ fontSize:'40px', marginBottom:'10px' }}>📭</div>
      <div style={{ fontSize:'15px', fontWeight:'bold', color:'var(--text)' }}>No graded results yet</div>
      <div style={{ fontSize:'12px', marginTop:'6px' }}>Check back after games finish</div>
    </div>
  )

  const correctCount = picks.filter(p => p.result === 'correct').length
  const totalPoints  = picks.reduce((sum, p) => sum + (p.points_earned || 0), 0)

  return (
    <div>
      <div className="card" style={{ marginBottom:'10px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <span style={{ fontSize:'12px', color:'var(--text-dim)', fontFamily:'Arial,sans-serif', letterSpacing:'0.5px' }}>YOUR RESULTS</span>
        <span style={{ fontSize:'13px', fontFamily:'Arial,sans-serif' }}>
          <span style={{ color:'var(--text)' }}>{correctCount} of {picks.length}</span>
          <span style={{ color:'var(--gold)', fontWeight:'bold', marginLeft:'12px' }}>{totalPoints}pt</span>
        </span>
      </div>

      {picks.map(renderResultPick)}
    </div>
  )
}

// ── Game detail bottom sheet ──────────────────────────────────────────────────

function GameDetailSheet({ game, groups, user, setTab, onClose, promptAuth = () => {} }) {
  const [pickSide, setPickSide] = useState(null)              // 'away' | 'home' | null
  const [selectedGroups, setSelectedGroups] = useState(() => new Set())
  const [submitState, setSubmitState] = useState('idle')      // 'idle' | 'saving' | 'saved'
  if (!game) return null
  const isLive     = game.status === 'live'
  const isFinal    = game.status === 'final'
  const isUpcoming = game.status === 'upcoming'
  const sportGroups = groups.filter(g => g.sport === game.sport && g.contest_type === 'pick_em')
  const canPick     = isUpcoming && user

  function toggleGroup(id) {
    setSelectedGroups(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  async function submitPick() {
    // Anonymous-browsing gate — a logged-out user CAN reach this sheet
    // (tap a game in GamesTab), so guard the save path explicitly.
    if (!user?.id) return promptAuth()
    if (!pickSide || selectedGroups.size === 0) return
    setSubmitState('saving')
    const teamName = pickSide === 'away' ? game.away?.team : game.home?.team
    // Local-time YYYY-MM-DD via toLocaleDateString('en-CA') — matches the
    // chain used in PickemMode.jsx (Fix 1b). Drops game.startTime: that
    // field is always a display string ('07:05 PM EDT' / 'Final' / 'LIVE'),
    // never a date. Routes commenceTime → epoch → null, no display-string poison.
    const dateSource = game.commenceTime
                    || (game.startTimeMs && !Number.isNaN(game.startTimeMs) ? game.startTimeMs : null)
    const gameDate   = dateSource ? new Date(dateSource).toLocaleDateString('en-CA') : null
    const pick = {
      side: pickSide, team: teamName,
      status: 'pending', pointsAwarded: 0,
      homeTeam: game.home?.team, awayTeam: game.away?.team, gameDate,
    }
    for (const groupId of selectedGroups) {
      const saved = await loadPicks(groupId, user.id, 'daily')
      const existing = saved?.picks || {}
      await savePicks(groupId, user.id, {
        picks: { ...existing, [game.id]: pick },
        submitted: false,
        dayKey: getDayKey(),
      }, 'daily')
    }
    setSubmitState('saved')
  }
  return (
    <div style={{ position:'fixed', inset:0, zIndex:200, display:'flex', flexDirection:'column', justifyContent:'flex-end' }}>
      <div onClick={onClose} style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.72)' }} />
      <div style={{ position:'relative', zIndex:1, background:'var(--surface)', borderRadius:'18px 18px 0 0',
        paddingBottom:'40px', maxHeight:'90vh', overflowY:'auto', width:'100%', maxWidth:'520px',
        margin:'0 auto', boxSizing:'border-box', WebkitOverflowScrolling:'touch' }}>
        <div style={{ width:'40px', height:'4px', background:'rgba(255,255,255,0.2)', borderRadius:'2px', margin:'14px auto 0' }} />
        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 20px 0' }}>
          <div>
            <div style={{ fontSize:'15px', fontWeight:'bold', color:'var(--gold)', fontFamily:'inherit' }}>
              {game.away.team} @ {game.home.team}
            </div>
            <div style={{ fontSize:'12px', color:'var(--text-dim)', marginTop:'2px', fontFamily:'Arial,sans-serif' }}>
              {game.sport} · {isLive ? `LIVE ${game.period || ''} ${game.clock || ''}`.trim() : isFinal ? 'FINAL' : game.startTime}
              {game.seriesInfo && ` · ${game.seriesInfo}`}
            </div>
          </div>
          <button onClick={onClose} style={{ background:'rgba(255,255,255,0.08)', border:'none', color:'var(--text-dim)', cursor:'pointer', width:'28px', height:'28px', borderRadius:'50%', fontSize:'18px', display:'flex', alignItems:'center', justifyContent:'center' }}>×</button>
        </div>
        {/* Live / final score */}
        {(isLive || isFinal) && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr auto 1fr', gap:'8px', alignItems:'center', padding:'14px 20px', textAlign:'center' }}>
            <div>
              <div style={{ fontSize:'13px', color:'var(--text-dim)', marginBottom:'4px' }}>{game.away.abbr}</div>
              <div style={{ fontSize:'36px', fontWeight:'bold', fontFamily:'Arial,sans-serif',
                color: (game.away.score > game.home.score) ? 'var(--gold)' : 'var(--text)' }}>{game.away.score ?? '—'}</div>
              {isFinal && game.away.score > game.home.score && <div style={{ fontSize:'10px', color:'var(--green)', fontWeight:'bold' }}>WINNER</div>}
            </div>
            <div style={{ fontSize:'11px', color:'var(--text-muted)', fontFamily:'Arial,sans-serif', fontWeight:'bold' }}>
              {isLive ? `${game.period || ''} ${game.clock || ''}`.trim() : 'FINAL'}
            </div>
            <div>
              <div style={{ fontSize:'13px', color:'var(--text-dim)', marginBottom:'4px' }}>{game.home.abbr}</div>
              <div style={{ fontSize:'36px', fontWeight:'bold', fontFamily:'Arial,sans-serif',
                color: (game.home.score > game.away.score) ? 'var(--gold)' : 'var(--text)' }}>{game.home.score ?? '—'}</div>
              {isFinal && game.home.score > game.away.score && <div style={{ fontSize:'10px', color:'var(--green)', fontWeight:'bold' }}>WINNER</div>}
            </div>
          </div>
        )}
        {/* Odds (upcoming) */}
        {isUpcoming && (game.spread != null || game.awayML != null || game.total != null) && (
          <div style={{ padding:'0 20px 14px' }}>
            <div style={{ background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:'10px', padding:'12px 14px' }}>
              <div style={{ fontSize:'9px', color:'var(--text-muted)', letterSpacing:'1.5px', marginBottom:'8px', fontFamily:'Arial,sans-serif' }}>ODDS</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr auto 1fr', gap:'8px', alignItems:'center', textAlign:'center' }}>
                <div>
                  <div style={{ fontSize:'11px', color:'var(--text-dim)', marginBottom:'4px' }}>{game.away.abbr}</div>
                  {game.spread != null && <div style={{ fontSize:'15px', fontWeight:'bold' }}>{spreadLabel(game.spread, 'away')}</div>}
                  {game.awayML != null && <div style={{ fontSize:'12px', color: game.awayML < 0 ? 'var(--gold)' : 'var(--green)' }}>{fmtML(game.awayML)}</div>}
                </div>
                <div>{game.total != null && <><div style={{ fontSize:'10px', color:'var(--text-muted)' }}>O/U</div><div style={{ fontSize:'18px', fontWeight:'bold' }}>{game.total}</div></>}</div>
                <div>
                  <div style={{ fontSize:'11px', color:'var(--text-dim)', marginBottom:'4px' }}>{game.home.abbr}</div>
                  {game.spread != null && <div style={{ fontSize:'15px', fontWeight:'bold' }}>{spreadLabel(game.spread, 'home')}</div>}
                  {game.homeML != null && <div style={{ fontSize:'12px', color: game.homeML < 0 ? 'var(--gold)' : 'var(--green)' }}>{fmtML(game.homeML)}</div>}
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Team comparison */}
        <div style={{ padding:'0 20px' }}>
          <TeamComparison game={game} />
        </div>
        {/* Make Your Pick — works for upcoming games only */}
        {canPick && sportGroups.length > 0 && (
          <div style={{ padding:'18px 20px 0' }}>
            <div style={{ fontSize:'11px', color:'var(--text-dim)', letterSpacing:'0.8px', marginBottom:'10px', fontFamily:'Arial,sans-serif' }}>MAKE YOUR PICK</div>
            {/* Side selector */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'12px' }}>
              {['away', 'home'].map(side => {
                const t = side === 'away' ? game.away : game.home
                const active = pickSide === side
                return (
                  <button key={side} onClick={() => setPickSide(side)}
                    style={{ background: active ? 'var(--gold-bg2)' : 'var(--surface2)',
                      border: `2px solid ${active ? 'var(--gold)' : 'var(--border)'}`,
                      borderRadius:'10px', padding:'10px 8px', cursor:'pointer', textAlign:'center' }}>
                    <div style={{ fontSize:'11px', color:'var(--text-dim)', marginBottom:'2px' }}>{side === 'away' ? 'AWAY' : 'HOME'}</div>
                    <div style={{ fontSize:'13px', fontWeight:'bold', color: active ? 'var(--gold)' : 'var(--text)' }}>{t.team}</div>
                  </button>
                )
              })}
            </div>
            {/* Group checkboxes */}
            <div style={{ fontSize:'10px', color:'var(--text-muted)', letterSpacing:'0.6px', marginBottom:'6px', fontFamily:'Arial,sans-serif' }}>APPLY TO GROUPS</div>
            {sportGroups.map(g => {
              const checked = selectedGroups.has(g.id)
              return (
                <label key={g.id} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 12px',
                  background: checked ? 'var(--gold-bg)' : 'var(--surface2)',
                  border: `1px solid ${checked ? 'rgba(0,230,118,0.4)' : 'var(--border)'}`,
                  borderRadius:'10px', marginBottom:'6px', cursor:'pointer' }}>
                  <input type="checkbox" checked={checked} onChange={() => toggleGroup(g.id)}
                    style={{ width:18, height:18, accentColor:'#00e676' }} />
                  <span style={{ fontSize:'13px', fontWeight: checked ? 'bold' : 'normal', color: checked ? 'var(--gold)' : 'var(--text)' }}>{g.name}</span>
                </label>
              )
            })}
            <button className="btn btn-gold" onClick={submitPick}
              disabled={!pickSide || selectedGroups.size === 0 || submitState !== 'idle'}
              style={{ marginTop:'10px' }}>
              {submitState === 'saved'  ? '✅ Pick Saved'
              : submitState === 'saving' ? 'Saving…'
              : `Submit Pick${selectedGroups.size > 1 ? ` (${selectedGroups.size} groups)` : ''}`}
            </button>
            <div style={{ textAlign:'center', fontSize:'11px', color:'var(--text-muted)', marginTop:'8px' }}>
              You can revise this pick from the group page
            </div>
          </div>
        )}
        {!canPick && sportGroups.length > 0 && (
          <div style={{ padding:'14px 20px 0' }}>
            <div style={{ fontSize:'11px', color:'var(--text-dim)', textAlign:'center' }}>
              Picks unavailable — game has started
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Pull-to-refresh hook ──────────────────────────────────────────────────────
// Listens on document touch events; only engages when the user is at the top
// of the page (scrollY === 0) and pulls down. Past the threshold (60px) it
// invokes onRefresh and shows the indicator until the promise resolves.
function usePullToRefresh(onRefresh) {
  const [pull, setPull]             = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  const startY = useRef(null)

  useEffect(() => {
    function topOfPage() {
      return (window.scrollY || document.documentElement.scrollTop || 0) <= 0
    }
    function onTouchStart(e) {
      if (!topOfPage()) return
      startY.current = e.touches[0].clientY
    }
    function onTouchMove(e) {
      if (refreshing || startY.current == null) return
      const delta = e.touches[0].clientY - startY.current
      if (delta > 0 && topOfPage()) {
        setPull(Math.min(delta * 0.6, 100))
      }
    }
    async function onTouchEnd() {
      if (startY.current == null) return
      const triggered = pull > 60 && !refreshing
      startY.current = null
      if (triggered) {
        setRefreshing(true)
        try { await onRefresh() } finally {
          setRefreshing(false)
          setPull(0)
        }
      } else {
        setPull(0)
      }
    }
    document.addEventListener('touchstart', onTouchStart, { passive: true })
    document.addEventListener('touchmove',  onTouchMove,  { passive: true })
    document.addEventListener('touchend',   onTouchEnd)
    return () => {
      document.removeEventListener('touchstart', onTouchStart)
      document.removeEventListener('touchmove',  onTouchMove)
      document.removeEventListener('touchend',   onTouchEnd)
    }
  }, [pull, refreshing, onRefresh])

  return { pull, refreshing }
}

function PullIndicator({ pull, refreshing }) {
  if (pull === 0 && !refreshing) return null
  const visible = refreshing || pull > 12
  if (!visible) return null
  return (
    <div style={{ position:'fixed', top:'calc(env(safe-area-inset-top, 0px) + 8px)', left:0, right:0, display:'flex', justifyContent:'center', pointerEvents:'none', zIndex:120 }}>
      <div style={{
        background:'var(--surface2)', border:'1px solid var(--border)',
        borderRadius:'999px', padding:'6px 14px', display:'flex', alignItems:'center', gap:8,
        fontSize:11, color:'var(--gold)', fontFamily:'Arial,sans-serif',
        opacity: Math.min(pull / 60, 1),
      }}>
        <span style={{
          display:'inline-block', width:12, height:12,
          border:'2px solid var(--border)', borderTopColor:'var(--gold)',
          borderRadius:'50%',
          animation: refreshing ? 'spin .8s linear infinite' : 'none',
          transform: refreshing ? 'none' : `rotate(${pull * 4}deg)`,
        }} />
        {refreshing ? 'Refreshing…' : pull > 60 ? 'Release to refresh' : 'Pull to refresh'}
      </div>
    </div>
  )
}

// ── Hoisted from App's body ──────────────────────────────────────────────────
// Declaring components inside App made their function references unstable
// across renders, causing React to remount their entire subtrees on every
// App state update. Internal state (BracketMode picks) got destroyed each
// cycle. These components live at module level for stable identity; prop
// plumbing is the cost.

function SectionLabel({ text }) {
  return (
    <div style={{ fontSize:'11px', fontFamily:'Arial,sans-serif', color:'var(--text-dim)', letterSpacing:'0.8px', marginBottom:'8px', marginTop:'4px' }}>
      {text}
    </div>
  )
}

// Per-sport photographic background images. Files live in /public/sport-bg/
// (Vercel-served; owned AI-generated assets; no external image requests).
// Keying is (sport[.contest_type]) → URL with the fallback chain below; a
// sport-level entry covers both pick_em and bracket pools unless an exact
// (sport.contest_type) override is set. Bracket-specific images were
// considered (footballbracket, basketballbracket) but dropped pre-merge
// over trademark exposure (recognizable Lombardi Trophy silhouette in the
// football one); the fallback chain catches bracket pools and shows them
// the sport's pick_em image instead. Sports without an image fall through
// to the existing sportTintAlpha gradient + watermark treatment below.
const SPORT_BG_IMAGES = {
  UFC:  '/sport-bg/ufc.webp',
  NFL:  '/sport-bg/nfl.webp',
  NBA:  '/sport-bg/nba.webp',
  MLB:  '/sport-bg/mlb.webp',
  NHL:  '/sport-bg/nhl.webp',
  Golf: '/sport-bg/golf.webp',
  // soccer.webp ships to /public/sport-bg/ but no Soccer sport exists yet —
  // one map line activates it later if/when that sport is added.
}

// NCAA + WNBA cascade to their parent professional sport so derivative-sport
// pools still get a sport-flavored image instead of the gradient fallback.
const PARENT_SPORT = {
  'NCAA Basketball': 'NBA',
  'NCAA Football':   'NFL',
  'WNBA':            'NBA',
}

// Fallback chain: (sport.contest_type) → (sport) → (parent.contest_type) →
// (parent) → null. Null means "no image available; use the gradient +
// watermark treatment further down."
function getCardImage(sport, contestType) {
  if (!sport) return null
  const exact = SPORT_BG_IMAGES[`${sport}.${contestType}`]
  if (exact) return exact
  if (SPORT_BG_IMAGES[sport]) return SPORT_BG_IMAGES[sport]
  const parent = PARENT_SPORT[sport]
  if (!parent) return null
  const parentExact = SPORT_BG_IMAGES[`${parent}.${contestType}`]
  if (parentExact) return parentExact
  return SPORT_BG_IMAGES[parent] || null
}

// Card scrim — directional left→right gradient per operator spec
// (2026-06-11 panorama redesign). Universal across all 5 sports; the
// brightest-image stress test (golf daytime) drives the alpha floor.
//
//   0% ─────── 35% ─────── ~52.5% ─────── 70% ─────── 100%
//   85% black  85% black   ~52% black     20% black   20% black
//   └─ text zone (left)    midpoint       right edge ─┘
//
// 0%→35% holds 85% solid — the title/sport-chip/end-text live here
// and must pass readability on golf's daytime sky.
// 35%→70% linearly fades to 20% — the gradient itself becomes the
// design tone.
// 70%→100% holds 20% — image breathes through on the right edge.
//
// Per-sport tuning was removed in this pass: the old top/bottom-alpha
// map (UFC 0.45/0.32, MLB 225deg angled, etc.) was the previous
// top-to-bottom scrim, abandoned for the new directional design.
// Single source-of-truth gradient now ships across all 5 sports.
const CARD_SCRIM = 'linear-gradient(90deg, ' +
  'rgba(0,0,0,0.85) 0%, ' +
  'rgba(0,0,0,0.85) 35%, ' +
  'rgba(0,0,0,0.20) 70%, ' +
  'rgba(0,0,0,0.20) 100%' +
')'

// Per-sport background-position override. The panorama WebPs are ~5.1:1
// while group cards render at ~2.1:1, so CSS `cover` center-crops the
// WebP's sides. When a focal element sits near a WebP edge (golf's flag
// on the right), anchor the position to that edge so it stays visible.
// Default 'center' for everything else.
const SPORT_BG_POSITION = {
  Golf: 'right center',
}

// Per-card background composition. When a photo is available, stack the
// universal CARD_SCRIM over the sport's panorama image. When no photo
// applies (unmapped sport — NFL still has its own image; CFB etc. fall
// here), fall back to the sport-tint gradient that shipped in 2855613.
function cardBackgroundFor(group) {
  const sport = group.sport
  const contestType = group.contest_type || group.contestType || 'pick_em'
  const imagePath = getCardImage(sport, contestType)
  if (imagePath) {
    const pos = SPORT_BG_POSITION[sport] || 'center'
    return `${CARD_SCRIM}, url('${imagePath}') ${pos}/cover no-repeat`
  }
  const tintAlpha = sportTintAlpha(sport)
  return `linear-gradient(135deg, var(--surface-elev) 0%, var(--surface-elev) 45%, ${tintAlpha} 100%)`
}

// Per-card background gradient: deepest --surface-elev at top-left fading to
// the sport's accent tint at low alpha in the bottom-right corner — same
// corner the SportIcon watermark sits in. Hex accents in SPORT_ACCENT get
// an 8% alpha hex suffix; var(--green) (UFC) gets an equivalent rgba. All
// values trace to existing tokens or the existing SPORT_ACCENT map; no new
// hand-coded colors are introduced.
function sportTintAlpha(sport) {
  const accent = SPORT_ACCENT[sport]
  if (!accent) return 'var(--accent-soft)'
  if (accent === 'var(--green)') return 'rgba(0, 230, 118, 0.08)'
  return `${accent}14`
}

function GroupCard({ group, cardState, user, loadGroups, openGroup, setShareGroup, setPendingImageGroupId, groupFileInputRef }) {
  const contestType = group.contest_type || group.contestType || 'pick_em'
  const groupName   = group.name?.trim() || 'Untitled Group'
  const customImgUrl = group.image_url
    ? `${group.image_url}${group.image_url.includes('?') ? '&' : '?'}t=${group.updated_at || group.image_url.length}`
    : null
  const isCreator   = group.created_by === user?.id
  const isArchived  = group.status === 'archived'
  const isCompleted = cardState === 'completed'

  const [editingName, setEditingName] = useState(false)
  const [newName,     setNewName]     = useState(group.name || '')

  const isBracketLike =
    contestType === 'bracket' || (group.duration && group.duration > 30)

  const bracketTarget = () => {
    const year = new Date().getFullYear()
    if (group.sport === 'NBA' || group.sport === 'NHL') return new Date(`${year}-06-20`)
    if (group.sport === 'NFL')                          return new Date(`${year}-02-15`)
    if (group.sport === 'MLB')                          return new Date(`${year}-11-05`)
    return null
  }

  const fallbackEndDate = () => {
    const d = new Date(group.created_at || Date.now())
    d.setDate(d.getDate() + (group.duration || 1))
    return d
  }

  const endDate = isBracketLike
    ? (bracketTarget() || (group.end_date ? new Date(group.end_date) : fallbackEndDate()))
    : (group.end_date ? new Date(group.end_date) : fallbackEndDate())

  const daysLeft = Math.ceil((endDate.getTime() - Date.now()) / 86_400_000)
  const endText  = isCompleted    ? (group.end_date ? `Ended ${new Date(group.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : 'Completed')
                 : daysLeft < 0   ? `Ended ${timeAgo(endDate)}`
                 : daysLeft === 0 ? 'Ends today'
                 : daysLeft === 1 ? 'Ends tomorrow'
                 : isBracketLike  ? `~${endDate.toLocaleString('default', { month: 'short' })} ${endDate.getDate()}`
                 :                  `Ends in ${daysLeft}d`

  async function saveName() {
    const trimmed = newName.trim()
    setEditingName(false)
    if (!trimmed || trimmed === group.name) return
    try {
      const { error } = await supabase.from('groups')
        .update({ name: trimmed }).eq('id', group.id)
      if (error) throw error
      await loadGroups(true)
    } catch (err) {
      log('[group rename] failed:', err?.message)
      setNewName(group.name || '')
    }
  }

  // Per-pool stat row — RANK · PTS · CORRECT%. CORRECT% = correct ÷ resolved,
  // where resolved is group_members.total_picks (grade-time additive — counts
  // picks that have flipped pending→correct|incorrect, NOT picks-submitted).
  // Verified at server/index.js:1500. Same field, same semantic, used for
  // both pickem and bracket — the bracket grader follows the identical
  // delta-additive pattern (db.js:1409 per HANDOFF TRACKED HIGH #1).
  const points    = group.user_points        ?? 0
  const correct   = group.user_correct_picks ?? 0
  const resolved  = group.user_total_picks   ?? 0
  const rank      = group.user_rank
  const correctPct = resolved > 0
    ? Math.min(100, Math.round((correct / resolved) * 100))
    : null

  const members      = group.members || []
  const memberCount  = group.member_count ?? members.length
  const overflowCount = Math.max(0, memberCount - members.length)

  const cardBg = cardBackgroundFor(group)
  const hasPhotoBg = !!getCardImage(group.sport, contestType)

  const pillLabel = cardState === 'live' ? 'LIVE' : 'COMPLETED'

  const emblemContent = customImgUrl ? (
    <img src={customImgUrl} alt={group.sport || 'group'}
      onError={e => { e.currentTarget.style.display = 'none' }} />
  ) : group.sport ? (
    <SportIcon sport={group.sport} size={28} />
  ) : (
    <span style={{ fontSize:'24px' }}>{SPORT_EMOJI[group.sport] || '🎮'}</span>
  )

  return (
    <div
      className={`group-card is-${cardState}${hasPhotoBg ? ' has-photo-bg' : ''}`}
      style={{ background: cardBg }}
      onClick={() => openGroup(group)}
    >
      {group.sport && (
        <div className="group-card__watermark" aria-hidden="true">
          <SportIcon sport={group.sport} size={160} />
        </div>
      )}

      <div className="group-card__content">
        <div className="group-card__header">
          <div className="group-card__emblem">
            <div className="group-card__emblem-circle">{emblemContent}</div>
            {isCreator && !isArchived && (
              <button
                type="button"
                className="group-card__emblem-edit"
                title="Change group image"
                onClick={e => {
                  e.stopPropagation()
                  e.preventDefault()
                  setPendingImageGroupId(group.id)
                  groupFileInputRef.current?.click()
                }}
              >
                ✏️
              </button>
            )}
          </div>

          <div className="group-card__title-block">
            <div className="group-card__title-row">
              {isCreator && !isArchived && editingName ? (
                <input
                  className="group-card__name-input"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  onBlur={saveName}
                  onKeyDown={e => {
                    if (e.key === 'Enter')  e.currentTarget.blur()
                    if (e.key === 'Escape') { setNewName(group.name || ''); setEditingName(false) }
                  }}
                  onClick={e => e.stopPropagation()}
                  autoFocus
                />
              ) : (
                <div
                  className="group-card__name"
                  style={{ cursor: isCreator && !isArchived ? 'text' : 'default' }}
                  onClick={e => {
                    if (!isCreator || isArchived) return
                    e.stopPropagation()
                    setNewName(group.name || '')
                    setEditingName(true)
                  }}
                >
                  {groupName}
                  {isCreator && !isArchived && (
                    <span className="group-card__rename-hint">✏️</span>
                  )}
                </div>
              )}
              <span className={`group-card__pill is-${cardState}`}>{pillLabel}</span>
            </div>
            <div className="group-card__subline">
              {CONTEST_LABEL[contestType]} · {group.sport}
            </div>
          </div>
        </div>

        <div className="group-card__stats">
          <div className="group-card__stat">
            <div className="group-card__stat-value">{ordinal(rank)}</div>
            <div className="group-card__stat-label">Rank</div>
          </div>
          <div className="group-card__stat">
            <div className="group-card__stat-value">{(points || 0).toLocaleString()}</div>
            <div className="group-card__stat-label">Pts</div>
          </div>
          <div className="group-card__stat">
            <div className="group-card__stat-value">{correctPct == null ? '—' : `${correctPct}%`}</div>
            <div className="group-card__stat-label">Correct%</div>
          </div>
        </div>

        <div className="group-card__footer">
          <div className="group-card__footer-left">
            <div className="group-card__avatar-stack">
              {members.map(m => (
                m.avatar_url ? (
                  <div key={m.user_id} className="group-card__avatar">
                    <img src={m.avatar_url} alt={m.username || ''}
                      onError={e => { e.currentTarget.style.display = 'none' }} />
                  </div>
                ) : (
                  <div key={m.user_id} className="group-card__avatar is-initial">
                    {(m.username || '?').slice(0, 1).toUpperCase()}
                  </div>
                )
              ))}
              {overflowCount > 0 && (
                <div className="group-card__avatar is-overflow">
                  +{overflowCount}
                </div>
              )}
            </div>
            <div className="group-card__member-count">
              <span>👥</span>
              <span>
                {group.max_players
                  ? `${memberCount || 1}/${group.max_players}`
                  : `${memberCount || 1}`}
              </span>
            </div>
            <div className="group-card__end-text">{endText}</div>
          </div>
          <div className="group-card__footer-actions">
            {!isArchived && !isCompleted && (
              <button
                type="button"
                className="group-card__invite-btn"
                title="Share invite"
                onClick={e => { e.stopPropagation(); setShareGroup(group) }}
              >
                Invite ↗
              </button>
            )}
            <span className="group-card__chevron">›</span>
          </div>
        </div>
      </div>
    </div>
  )
}

function GroupsTab({
  groups, groupFilter, setGroupFilter, loadingGroups, loadGroups,
  setShowJoin, setJoinError, setShowCreate, setTab,
  user, openGroup, setShareGroup, setPendingImageGroupId, groupFileInputRef,
}) {
  const { pull, refreshing } = usePullToRefresh(async () => {
    await loadGroups(true)
  })

  // Local calendar date as YYYY-MM-DD via the shared getDayKey() helper —
  // matches how group.start_date / end_date are written (Fix 3) and the
  // past-day guard in PickemMode. Local time, NOT UTC.
  //
  // Two-bucket model (Active / Completed). The previous "Locked" middle
  // bucket exposed plumbing ("ended but not yet finalize-swept") that
  // users don't think in terms of. Past-end-date pools now fold into
  // Completed alongside the status-flipped ones. From the user's POV,
  // a pool past its end_date is over regardless of whether finalize
  // has run yet — preserves Saturday's launch-surface clarity.
  //
  // Active still keys on end_date with start_date fallback (cb70b05
  // RBC re-bury fix): multi-day pools stay Active through the tournament
  // even though start_date sits in the past.
  const todayIso = getDayKey()
  const bucketDate = g => g.end_date || g.start_date
  const activeGroups    = groups.filter(g => !isHistoricalGroup(g) && (!bucketDate(g) || bucketDate(g) >= todayIso))
  const completedGroups = groups.filter(g => isHistoricalGroup(g) || (bucketDate(g) && bucketDate(g) < todayIso))

  // Stale-state fallback: if user's saved groupFilter is 'locked' (the
  // chip that's being removed in this commit), it falls through to
  // activeGroups instead of rendering an empty list.
  const shown = groupFilter === 'completed' ? completedGroups
              :                                activeGroups

  return (
    <div className="page">
      <PullIndicator pull={pull} refreshing={refreshing} />
      <div className="page-header">
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px' }}>
          <h1 className="page-title">My Groups</h1>
          <div style={{ display:'flex', gap:'8px' }}>
            <button className="btn btn-ghost btn-sm" style={{ width:'auto' }} onClick={() => { setShowJoin(true); setJoinError('') }}>Join</button>
            <button className="btn btn-gold btn-sm" style={{ width:'auto', borderRadius:'20px' }} onClick={() => setTab('modes')}>
              + Create
            </button>
          </div>
        </div>

        <div className="group-filter-bar">
          {[
            { id: 'active',    label: 'Active',    n: activeGroups.length },
            { id: 'completed', label: 'Completed', n: completedGroups.length },
          ].map(t => (
            <button
              key={t.id}
              type="button"
              className={`group-filter-chip${groupFilter === t.id ? ' is-active' : ''}`}
              onClick={() => setGroupFilter(t.id)}
            >
              {t.label}
              {t.n > 0 && <span className="group-filter-chip__count">· {t.n}</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="page-body">
        {loadingGroups ? (
          <div>
            <SectionLabel text="LOADING GROUPS…" />
            <SkeletonCard height={160} /><SkeletonCard height={160} />
          </div>
        ) : groups.length === 0 ? (
          <div style={{ textAlign:'center', padding:'48px 0' }}>
            <div style={{ fontSize:'48px', marginBottom:'16px' }}>🏆</div>
            <div style={{ fontSize:'18px', color:'var(--gold)', marginBottom:'8px' }}>No groups yet</div>
            <div style={{ color:'var(--text-dim)', fontSize:'14px', marginBottom:'24px' }}>Create a Pick'em or Bracket group with friends</div>
            <button className="btn btn-gold" onClick={() => setTab('modes')} style={{ maxWidth:'220px', margin:'0 auto' }}>
              Create Group
            </button>
          </div>
        ) : shown.length === 0 ? (
          <div style={{ textAlign:'center', padding:'40px 0', color:'var(--text-dim)', fontSize:'13px' }}>
            {/* Label tracks what `shown` actually contains, not the raw
                filter state — a stale 'locked' filter shows activeGroups
                via the `shown` ternary, so the empty-state label normalizes
                to 'active' too. */}
            No {groupFilter === 'completed' ? 'completed' : 'active'} groups.
          </div>
        ) : (
          shown.map(g => (
            <GroupCard
              key={g.id} group={g}
              cardState={groupFilter === 'completed' ? 'completed' : 'live'}
              user={user} loadGroups={loadGroups} openGroup={openGroup}
              setShareGroup={setShareGroup}
              setPendingImageGroupId={setPendingImageGroupId}
              groupFileInputRef={groupFileInputRef}
            />
          ))
        )}
      </div>
    </div>
  )
}

// Standings podium + ranked list. Single component, two consumers:
//   • Persistent compact header above the group tab bar (`compact` true,
//     `showRankedList` false) — ambient glance, hidden when the active tab
//     is 'standings' so the user doesn't see two podiums on the same screen.
//   • The Standings tab body (default props — full-size podium + ranked list
//     for #4+). This is the standings home.
// Behaviour is byte-equivalent to the inline IIFE that shipped in c7a44ac:
// always renders 3 pillars in [#2, #1, #3] order so #1 stays center; empty
// slots get is-ghost treatment (non-interactive, no member fields rendered).
// Real pillars and ranked-list rows both open MemberResultsSheet via
// setOpenedMember. Framing stays rank + avatar + name + points only.
function StandingsPodium({
  groupMembers, user, setOpenedMember,
  compact = false, showRankedList = true,
  contestType, eventPar = null,
  roundScoresAll = null, picksByUserId = null,
}) {
  // Divisions pools score in raw strokes (low total wins); every other mode
  // scores in points (high total wins). Branch the three display sites
  // (podium aria-label, podium pillar value+label, ranked-list row
  // value+label) through these helpers so non-divisions pools remain
  // byte-identical (default branch hits `m.points || 0` + toLocaleString +
  // 'PTS' — same as before this refactor). Sort order is owned by
  // getGroupMembers (lib/db.js); this component only renders.
  //
  // Divisions display unit: relative-to-par if eventPar is known
  // (lookup hit on KNOWN_EVENT_PARS via /api/golf/event-meta), raw
  // strokes if eventPar is null (fetch pending, fetch failed, or event
  // not in KNOWN_EVENT_PARS — falling back to "guessed par × 16" would
  // produce a silent off-by-a-constant if the grader's PGA_DEFAULT_PAR
  // ever diverges from a future lookup entry, so we hold to raw until
  // par is confirmed). Conversion: divisions_score - eventPar × 16
  // (best-4 × 4 rounds each).
  // All display decisions (text, label, aria, sortKey) come from the
  // module-scope divisionsMemberScore helper, so this component, the
  // GroupDetail summary header, and MembersList produce byte-identical
  // output for the same inputs. Per-render Map cache so divisionsMember-
  // Score (and the computeCurrentBestFour call inside it) fires at most
  // once per member — text/label/aria/sortKey all share the same result.
  const ctx = { contestType, eventPar, picksByUserId, roundScoresAll }
  const _scoreCache = new Map()
  const scoreOf = m => {
    if (!_scoreCache.has(m.user_id)) _scoreCache.set(m.user_id, divisionsMemberScore(m, ctx))
    return _scoreCache.get(m.user_id)
  }
  const scoreText  = m => scoreOf(m).text
  const scoreLabel = m => scoreOf(m).label
  const scoreAria  = m => scoreOf(m).aria

  // Re-sort locally for divisions so live numbers drive the ranking.
  // Non-divisions passes through (getGroupMembers already sorted by points).
  const isDivisions = contestType === 'divisions'
  const sortedMembers = isDivisions
    ? [...groupMembers].sort((a, b) => scoreOf(a).sortKey - scoreOf(b).sortKey)
    : groupMembers
  const PillarClass = { 1: 'is-first', 2: 'is-second', 3: 'is-third' }
  const podiumSlots = [
    { rank: 2, member: sortedMembers[1] || null },
    { rank: 1, member: sortedMembers[0] || null },
    { rank: 3, member: sortedMembers[2] || null },
  ]
  const rest = showRankedList ? sortedMembers.slice(3) : []
  const keyOpen = (e, m) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      setOpenedMember(m)
    }
  }

  return (
    <>
      {sortedMembers.length > 0 && (
        <div className={`podium${compact ? ' podium--compact' : ''}`}>
          {podiumSlots.map(({ rank, member }) => {
            const pillarClass = PillarClass[rank]

            if (!member) {
              return (
                <div
                  key={`ghost-${rank}`}
                  className={`podium__pillar ${pillarClass} is-ghost`}
                  aria-hidden="true"
                >
                  <div className="podium__crown-spacer" aria-hidden="true" />
                  <div className="podium__avatar" aria-hidden="true" />
                  <div className="podium__name" aria-hidden="true" />
                  <div className="podium__points" aria-hidden="true" />
                  <div className="podium__points-lbl" aria-hidden="true" />
                  <div className="podium__block">{rank}</div>
                </div>
              )
            }

            const isMe = member.user_id === user.id
            const initial = member.username?.[0]?.toUpperCase()
            return (
              <button
                key={member.id}
                type="button"
                className={`podium__pillar ${pillarClass}`}
                onClick={(e) => { e.stopPropagation(); setOpenedMember(member) }}
                onKeyDown={(e) => keyOpen(e, member)}
                aria-label={`${member.username}, rank ${rank}, ${scoreAria(member)}`}
              >
                {rank === 1
                  ? <div className="podium__crown" aria-hidden="true">👑</div>
                  : <div className="podium__crown-spacer" aria-hidden="true" />}
                <div className="podium__avatar">
                  {member.avatar_url ? (
                    <img src={member.avatar_url} alt=""
                      onError={e => { e.currentTarget.style.display = 'none' }} />
                  ) : (
                    initial
                  )}
                </div>
                <div className="podium__name">
                  {member.username}
                  {isMe && <span className="podium__name-you">(you)</span>}
                </div>
                <div className="podium__points">{scoreText(member)}</div>
                <div className="podium__points-lbl">{scoreLabel(member)}</div>
                <div className="podium__block">{rank}</div>
              </button>
            )
          })}
        </div>
      )}
      {rest.map(m => {
        const rank = sortedMembers.indexOf(m) + 1
        const isMe = m.user_id === user.id
        const initial = m.username?.[0]?.toUpperCase()
        return (
          <div
            key={m.id}
            className={`podium-list__row${isMe ? ' is-me' : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => setOpenedMember(m)}
            onKeyDown={(e) => keyOpen(e, m)}
          >
            <div className="podium-list__rank">{rank}</div>
            <div className="podium-list__avatar">
              {m.avatar_url ? (
                <img src={m.avatar_url} alt=""
                  onError={e => { e.currentTarget.style.display = 'none' }} />
              ) : (
                initial
              )}
            </div>
            <div className="podium-list__name">
              {m.username}
              {isMe && <span className="podium-list__name-you">(you)</span>}
            </div>
            <div className="podium-list__points-col">
              <div className="podium-list__points">{scoreText(m)}</div>
              <div className="podium-list__points-lbl">{scoreLabel(m)}</div>
            </div>
            <span className="podium-list__chev">›</span>
          </div>
        )
      })}
    </>
  )
}

function GroupDetail({
  selGroup, groupMembers, setGroupMembers, user, gradeKey,
  groupTab, setGroupTab,
  confirmDelete, setConfirmDelete, deleteError, setDeleteError,
  setSelGroup, setShareGroup, handleDeleteGroup,
  paidPathActive, showToast,
  blockedUserIds, onUserBlocked,
}) {
  const g       = selGroup
  const myMem   = groupMembers.find(m => m.user_id === user.id)
  const leader  = groupMembers[0]
  const myPts   = myMem?.points ?? 0
  const leadPts = leader?.points ?? 1
  const pct     = Math.min(Math.round((myPts / Math.max(leadPts, 1)) * 100), 100)
  const isArchived = g.status === 'archived'

  // Single openedMember state shared across the two entry points to
  // MemberResultsSheet: live standings rows (below) AND MembersList rows.
  // Lifted from MembersList so both surfaces drive ONE sheet instance.
  const [openedMember, setOpenedMember] = useState(null)

  // Event par for divisions pools — sourced from the same lookup the
  // grader uses (KNOWN_EVENT_PARS via /api/golf/event-meta). Threaded
  // into StandingsPodium AND DivisionsMemberResultsSheet so a single
  // fetch per pool view powers all three relative-to-par display sites
  // (leaderboard total, sheet header, per-pick totals). null until
  // fetch resolves OR if the event isn't in the lookup — both cases
  // are handled by the display branches (fall back to raw strokes
  // rather than guess a par and risk silent off-by-a-constant).
  const [eventPar, setEventPar] = useState(null)
  useEffect(() => {
    if (g?.contest_type !== 'divisions' || !g?.golf_config?.eventId) {
      setEventPar(null)
      return
    }
    let cancelled = false
    ;(async () => {
      const meta = await fetchGolfEventMeta(g.golf_config.eventId)
      if (cancelled) return
      setEventPar(typeof meta?.par === 'number' ? meta.par : null)
    })()
    return () => { cancelled = true }
  }, [g?.contest_type, g?.golf_config?.eventId])

  // Live per-round score poll (hoisted from DivisionsMemberResultsSheet).
  // One poll per pool view, shared by the open entry sheet AND by future
  // running-leaderboard consumers (no duplicate fetches). Mirrors matchup
  // Poller 2 in STRUCTURE: tick() on mount, interval at 75s, document.hidden
  // gate, visibilitychange re-poll. Failure handling: increments local
  // failureCount; after MAX_FAILURES sets pollError=true and empties
  // roundScoresAll so stale data can't be shown as current.
  //
  // Gate: divisions+golf only (other modes don't use per-round scores).
  // Single-tick optimization: when EVERY member has divisions_score set
  // (tournament fully graded), fire one tick to populate the R1-R4 strip
  // and skip the interval — matches the sheet's prior post-grade behavior
  // generalized to all members.
  const [roundScoresAll, setRoundScoresAll] = useState({ scoresByDg: {}, rounds: {}, fetchedAt: null })
  const [pollError,      setPollError]      = useState(false)
  // Derive isPostGradeAll OUTSIDE the effect so the effect re-runs only
  // when the all-graded state flips (true↔false), not on every
  // groupMembers reference change (handlePtsUpdate updates the array
  // frequently — depending on the raw array would tear down/restart the
  // poll on every render that touches points, fetching extra ticks).
  const isPostGradeAll = groupMembers.length > 0
    && groupMembers.every(m => m.divisions_score != null)
  useEffect(() => {
    if (g?.contest_type !== 'divisions') return
    if (g?.sport !== 'Golf') return
    const eventId = g?.golf_config?.eventId
    if (!eventId) return
    let cancelled    = false
    let timer        = null
    let failureCount = 0

    async function tick() {
      if (document.hidden) return
      const result = await fetchGolfRoundScores(eventId)
      if (cancelled) return
      if (result.fetchError) {
        failureCount++
        if (failureCount >= DIVISIONS_LIVE_POLL_MAX_FAILURES) {
          setRoundScoresAll({ scoresByDg: {}, rounds: {}, fetchedAt: null })
          setPollError(true)
        }
        return
      }
      failureCount = 0
      setPollError(false)
      setRoundScoresAll({
        scoresByDg: result.scoresByDg,
        rounds:     result.rounds,
        fetchedAt:  result.fetchedAt,
      })
    }

    tick()  // immediate first poll (also the single fetch when post-grade-all)
    if (!isPostGradeAll) {
      timer = setInterval(tick, DIVISIONS_LIVE_POLL_INTERVAL_MS)
    }

    function onVisChange() {
      if (!document.hidden && !cancelled && !isPostGradeAll) tick()
    }
    document.addEventListener('visibilitychange', onVisChange)

    return () => {
      cancelled = true
      if (timer) clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisChange)
    }
  }, [g?.contest_type, g?.sport, g?.golf_config?.eventId, isPostGradeAll])

  // Per-member divisions picks for the running leaderboard (Commit 2 of
  // running-leaderboard work). Fetches every member's picks in parallel
  // and stores them as { userId: picks } so Commit 3's StandingsPodium
  // can compute each member's running best-4-of-6 against the shared
  // round-scores poll above. Loaded but unused this commit.
  //
  // Same self/other branch the entry sheet uses: own picks via direct
  // supabase (own-RLS, cheap); other members' via the reveal-gated
  // server endpoint. Promise.allSettled so one member's failure doesn't
  // tank the whole batch — failed members are simply omitted from the
  // map and render '—' downstream.
  //
  // Stable-primitive dep key: a sorted, joined string of member ids. Raw
  // groupMembers churns on every points update (same trap Commit 1 avoided
  // for isPostGradeAll); keying on a string means we only re-fetch when
  // the MEMBERSHIP set actually changes, not when scores tick.
  const [picksByUserId, setPicksByUserId] = useState({})
  const memberIdsKey = groupMembers.map(m => m.user_id).sort().join(',')
  useEffect(() => {
    if (g?.contest_type !== 'divisions') return
    if (g?.sport !== 'Golf') return
    const eventId = g?.golf_config?.eventId
    if (!eventId) return
    if (groupMembers.length === 0) { setPicksByUserId({}); return }
    let cancelled = false
    ;(async () => {
      const results = await Promise.allSettled(
        groupMembers.map(async m => {
          if (m.user_id === user?.id) {
            const picks = await loadDivisionGradedPicks(g.id, m.user_id, eventId)
            return { userId: m.user_id, picks }
          }
          const result = await fetchMemberDivisionPicks(g.id, m.user_id)
          return { userId: m.user_id, picks: result?.picks || [] }
        })
      )
      if (cancelled) return
      const map = {}
      for (const r of results) {
        if (r.status === 'fulfilled' && r.value) {
          map[r.value.userId] = r.value.picks
        }
      }
      setPicksByUserId(map)
    })()
    return () => { cancelled = true }
  }, [memberIdsKey, g?.contest_type, g?.sport, g?.golf_config?.eventId, g?.id, user?.id])

  // ── Phase 1.ζ-2 unlock attribution ────────────────────────────────────
  // Renders "✓ Unlocked by {name}" sub-line near the tab bar (§10.4) when
  // a winning pool_unlocks row exists for this pool. Grandfathered pools
  // (migration 040 backfill) have NO pool_unlocks row by construction —
  // attribution returns null and the line is omitted. Never render a
  // visible "Unlocked by Someone" fallback.
  const [unlockAttribution, setUnlockAttribution] = useState(null)
  useEffect(() => {
    setUnlockAttribution(null)
    if (!g?.id) return
    if (!deriveUnlockState(g).unlocked) return  // locked pool — no winning row possible
    let cancelled = false
    getPoolUnlockAttribution(g.id)
      .then(name => { if (!cancelled) setUnlockAttribution(name) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [g?.id, g?.unlocked_at])

  // Golf pools don't have a Results tab — Picks already serves that role
  // (the post-tournament render shows graded picks with scores + ✓/✗).
  // If a user lands on a golf pool with `groupTab === 'results'` from
  // stale state (e.g. they had Results open on a non-golf pool, then
  // navigated to a golf pool with the same groupTab carried forward, or
  // they had Results open on this golf pool from before the tab was
  // removed), fall back to 'contest' so they don't see a blank body.
  // Fires on group switch or tab change; only acts on the bad combo.
  useEffect(() => {
    if (g?.sport === 'Golf' && groupTab === 'results') setGroupTab('contest')
  }, [g?.id, g?.sport, groupTab, setGroupTab])

  // ── Phase 1.ζ-3 unblock — Shape A dispatcher-level freemium gate ─────
  // When paidPathActive is TRUE and the pool is locked, the four contest-
  // tab dispatcher lines below route to <LockedShell/> instead of the
  // mode component. When paidPathActive is FALSE (prod-today), the
  // ternaries always pick the mode component → byte-identical to the
  // rolled-back build, no LockedShell mount, no dead-pool render.
  //
  // The in-component gates in PickemMode/GolfPicksMode/GolfDivisionsMode/
  // BracketMode remain in place from 1.ζ-2 — they're now defense-in-
  // depth on the gated branch and dormant on the un-gated branch. Plan
  // is to remove them as a follow-up cleanup once the dispatcher path
  // proves out at launch; not removing in this commit because that's
  // additional UI surface to re-verify and this fix is unblock-first.
  //
  // tierPreview for divisions+frozen: the per-tier label list lives at
  // module scope in GolfDivisionsMode (TIER_LABELS_BY_KEY) and isn't
  // re-exported. Passing null here means LockedShell's Cell 3 (blurred
  // tier preview) falls through to Cell 4 (plain CTA) for divisions
  // pools in the locked+frozen state. Acceptable for unblock — wiring
  // the full tier preview is a launch-completion task (either hoist
  // TIER_LABELS_BY_KEY to src/lib or re-export from the divisions
  // module).
  const [unlocking, setUnlocking] = useState(false)
  const handleUnlock = async () => {
    // Client-side lockout gate — server has the authoritative check (with
    // its own too-loose date-only fallback for pools missing lockout_at,
    // separate follow-up). Belt-and-suspenders here so we never open Stripe
    // checkout for a pool whose event has already started. Silent return:
    // the "Event started" notice above the tab bar (see render below)
    // explains the state to the user; this guard just prevents the API call
    // if a caller somehow reaches handleUnlock despite the render gate.
    if (deriveUnlockState(g).lockoutPassed) return
    setUnlocking(true)

    // Native iOS path — Apple IAP is required by App Store guidelines for
    // in-app digital-content purchases. Fully in-app synchronous flow:
    // StoreKit purchase → jws → POST /api/apple-iap/verify → server calls
    // unlock_group RPC. No redirect, no webhook race, no polling.
    if (Capacitor.isNativePlatform()) {
      try {
        const result = await purchaseAndVerifyUnlock({ group_id: g.id, pool: g })
        if (result?.cancelled) {
          // Normal user gesture — silent reset, matching the Stripe path's
          // treatment of a Stripe Checkout back-navigation.
          setUnlocking(false)
          return
        }
        if (result?.pending) {
          // Ask-to-Buy (child account) / SCA — Apple will confirm later.
          showToast('Purchase pending Apple approval. Try again once approved.', 6000)
          setUnlocking(false)
          return
        }
        // Success — server-side unlock_group already fired. Flip the local
        // pool's unlocked_at so the render transitions immediately; parent
        // groups[] resyncs on the next natural refetch.
        setSelGroup(prev => prev ? { ...prev, unlocked_at: new Date().toISOString() } : prev)
      } catch (err) {
        // Surface the actual error to the user via the same toast the
        // Stripe path uses. Error messages from appleIapClient.js are
        // stage-tagged (e.g. '[stage=plugin-call] ...') so an operator
        // can tell WHICH layer failed without a debug session.
        console.error('[unlock] Apple IAP failed:', err?.message)
        showToast(`Unlock failed: ${err?.message || 'unknown error'}`, 10000)
        setUnlocking(false)
      }
      return
    }

    // Web path — UNCHANGED from before the native branch was added.
    try {
      const { checkoutUrl } = await createCheckoutSessionUnlock({ group_id: g.id })
      window.location.href = checkoutUrl
    } catch (err) {
      console.error('[unlock] checkout failed:', err.message)
      setUnlocking(false)
    }
  }
  const lockedUs = deriveUnlockState(g)
  const lockedCc = deriveContestClass({
    sport: g.sport, contest_type: g.contest_type,
    start_date: g.start_date, end_date: g.end_date,
  })
  const lockedPriceCents = lockedCc ? CONTEST_CLASS_BASE_CENTS[lockedCc] : null
  const showLockedShell = paidPathActive && !lockedUs.unlocked

  function handlePtsUpdate(delta) {
    setGroupMembers(prev =>
      prev.map(m => m.user_id === user.id ? { ...m, points: (m.points||0) + delta } : m)
          .sort((a,b) => b.points - a.points)
    )
  }

  return (
    <div className="page">
      <div className="page-header">
        <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'8px' }}>
          <button onClick={() => { setSelGroup(null); setConfirmDelete(false); setDeleteError('') }} style={{ background:'none', border:'none', color:'var(--gold)', cursor:'pointer', fontSize:'22px', lineHeight:1, padding:0 }}>
            ←
          </button>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:'17px', fontWeight:'bold' }}>{g.name}</div>
            <div style={{ fontSize:'12px', color:'var(--text-dim)' }}>
              {CONTEST_EMOJI[g.contest_type || g.contestType || 'pick_em']} {CONTEST_LABEL[g.contest_type || g.contestType || 'pick_em']} · {g.sport}
            </div>
          </div>
          {!isArchived && (
            <button onClick={() => setShareGroup(g)} title="Share invite"
              style={{ background:'rgba(0,230,118,0.12)', border:'1px solid rgba(0,230,118,0.35)', color:'var(--gold)', borderRadius:'8px', padding:'6px 10px', cursor:'pointer', fontSize:'14px' }}>
              📤 Share
            </button>
          )}
        </div>
      </div>

      <div className="page-body">
        {/* Persistent-UI stacking-context wrapper — elevates the
            archived banner, You/Leader scoreboard card, compact
            StandingsPodium (crown/avatar), divider, and tab bar
            above GolfPicksMode's pinned bg-layer (which is a
            position:fixed z:0 element inside GolfPicksMode that
            paints to the full viewport and would otherwise cover
            these in-flow siblings).

            The wrapper takes position:relative + z-index:1 so its
            stacking context paints at root z:1, just above the bg
            layer (root z:0) and well below all overlay UI:
            .page-header (sticky z:20), .bottom-nav (fixed z:50),
            modal-backdrop (z:100), modal screens (z:200), top
            notification (z:120), bet-slip-fab (z:40), GolfPicksMode's
            own sticky-submit (z:90) and toast (z:100).

            Tab CONTENT (GolfPicksMode, PickemMode, BracketMode,
            ResultsFeed, etc.) is kept OUTSIDE this wrapper so that
            GolfPicksMode's bg layer + sticky-submit + toast stay
            root-context-anchored — wrapping the tab content would
            clamp the sticky-submit's effective z below .bottom-nav
            (z:50) and hide it behind the nav. Only the persistent
            chrome above the tab content is wrapped.

            OPAQUE + FULL-WIDTH BG COVERAGE — the wrapper has its own
            solid var(--color-bg) background AND extends to full
            .page-body container width via negative horizontal margins
            (-16px each side escapes .page-body's 16px padding) +
            restored 16px horizontal padding for content inset. The
            top margin is -12px (escapes .page-body's 12px top
            padding) with restored 12px top padding. Padding-bottom
            stays 0 so tab-bar's marginBottom:12px collapses out
            normally. The result: a solid dark dark-bg band that
            covers the pinned bg-layer EVERYWHERE in the persistent-
            UI region — through inter-element gaps, page-body gutters,
            above the wrapper, and below the wrapper down to where
            the picker content wrapper starts. Scoreboard, podium,
            tabs render visually on plain dark; the course image
            becomes visible only in the picks content area (below
            the tab bar). */}
        <div style={{
          position: 'relative',
          zIndex: 1,
          background: 'var(--color-bg)',
          margin: '-12px -16px 0',
          padding: '12px 16px 0',
        }}>
        {isArchived && (
          <div style={{
            background: 'var(--surface2)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            padding: '10px 12px',
            marginBottom: '12px',
            fontSize: '13px',
            color: 'var(--text-dim)',
            textAlign: 'center',
          }}>
            This group has been archived. View-only mode.
          </div>
        )}
        <div className="card" style={{ marginBottom:'12px' }}>
          {g.contest_type === 'divisions' ? (() => {
            // Divisions: helper-derived to-par display, no % bar (meaningless
            // for strokes — lower is better, so a fill that grows as you
            // catch up would invert). Leader = member with the minimum
            // sortKey across all members. If no member has a signal yet
            // (all Infinity), leader and "you" fall back to '—'.
            const ctx = { contestType: g.contest_type, eventPar, picksByUserId, roundScoresAll }
            const youScore = myMem ? divisionsMemberScore(myMem, ctx) : null
            let leaderScore = null
            let bestKey = Number.POSITIVE_INFINITY
            for (const m of groupMembers) {
              const s = divisionsMemberScore(m, ctx)
              if (s.sortKey < bestKey) { bestKey = s.sortKey; leaderScore = s }
            }
            return (
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:'13px' }}>
                <span style={{ color:'var(--text-dim)' }}>You: <strong style={{ color:'var(--text)' }}>{youScore?.text ?? '—'} {youScore?.label ?? ''}</strong></span>
                <span style={{ color:'var(--text-dim)' }}>Leader: <strong style={{ color:'var(--gold)' }}>{leaderScore?.text ?? '—'} {leaderScore?.label ?? ''}</strong></span>
              </div>
            )
          })() : (
            /* Pickem (and everything else) — VERBATIM prior behavior:
               points-based numbers, progress bar, "% of leader" sub-line. */
            <>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:'13px', marginBottom:'6px' }}>
                <span style={{ color:'var(--text-dim)' }}>You: <strong style={{ color:'var(--text)' }}>{myPts.toLocaleString()} pts</strong></span>
                <span style={{ color:'var(--text-dim)' }}>Leader: <strong style={{ color:'var(--gold)' }}>{leadPts.toLocaleString()} pts</strong></span>
              </div>
              <div className="progress-bar">
                <div className="progress-bar-fill" style={{ width:`${pct}%` }} />
              </div>
              <div style={{ fontSize:'11px', color:'var(--text-dim)', marginTop:'4px' }}>{pct}% of leader</div>
            </>
          )}
        </div>

        {/* Compact podium header — persistent above the tab bar on every
            tab EXCEPT 'standings' (where the full-size podium lives in the
            tab body, so showing both would duplicate). No SectionLabel here;
            it moved to the Standings tab. Ghost slots + tap-to-open behavior
            are preserved by reusing StandingsPodium. */}
        {groupTab !== 'standings' && (
          <StandingsPodium
            groupMembers={groupMembers}
            user={user}
            setOpenedMember={setOpenedMember}
            compact
            showRankedList={false}
            contestType={g.contest_type}
            eventPar={eventPar}
            roundScoresAll={roundScoresAll}
            picksByUserId={picksByUserId}
          />
        )}

        <div className="divider" />

        {/* §10.4 unlock attribution — subtle sub-line. Omitted when null
            (grandfathered pools, attribution fetch failed, or pool not
            unlocked). Never falls back to "Unlocked by Someone" — that
            broken-state string lives in getPoolUnlockAttribution's
            success branch only when the join resolved but profile is null. */}
        {unlockAttribution && (
          <div style={{
            fontSize: 11,
            color: 'var(--text-dim)',
            fontFamily: 'Arial, sans-serif',
            textAlign: 'center',
            margin: '-4px 0 10px',
            letterSpacing: 0.3,
          }}>
            ✓ Unlocked by {unlockAttribution}
          </div>
        )}

        {/* Unlock closed — the event has started AND nobody paid to unlock,
            so the pool can no longer be unlocked (would leave the user with
            no picks). Shown regardless of tab so the state is visible even
            if the user isn't on the contest tab. Mutually exclusive with
            the "✓ Unlocked by" attribution above (that only renders when
            the pool IS unlocked). handleUnlock is also guarded above as
            defense in depth. */}
        {paidPathActive && !lockedUs.unlocked && lockedUs.lockoutPassed && (
          <div style={{
            background: 'rgba(255, 23, 68, 0.08)',
            border: '1px solid rgba(255, 23, 68, 0.25)',
            borderRadius: 10,
            padding: '10px 12px',
            marginBottom: '12px',
            fontSize: 12,
            color: 'var(--text-dim)',
            fontFamily: 'Arial, sans-serif',
            lineHeight: 1.5,
            textAlign: 'center',
          }}>
            This event has already started — this pool can no longer be unlocked.
          </div>
        )}

        <div className="tab-bar" style={{ marginBottom:'12px' }}>
          <button className={`tab-item ${groupTab==='standings'?'active':''}`} onClick={() => setGroupTab('standings')}>
            Standings
          </button>
          {!isArchived && (
            <button className={`tab-item ${groupTab==='contest'?'active':''}`} onClick={() => setGroupTab('contest')}>
              {g.contest_type === 'bracket' ? 'Bracket' : 'Picks'}
            </button>
          )}
          {g.sport !== 'Golf' && (
            <button className={`tab-item ${groupTab==='results'?'active':''}`} onClick={() => setGroupTab('results')}>
              Results
            </button>
          )}
          <button className={`tab-item ${groupTab==='members'?'active':''}`} onClick={() => setGroupTab('members')}>
            Members
          </button>
          <button className={`tab-item ${groupTab==='chat'?'active':''}`} onClick={() => setGroupTab('chat')}>
            Chat
          </button>
        </div>
        </div>{/* end persistent-UI z:1 wrapper — tab content (GolfPicksMode etc.) stays UNWRAPPED below */}

        {groupTab === 'standings' && (
          <>
            <SectionLabel text="STANDINGS" />
            <StandingsPodium
              groupMembers={groupMembers}
              user={user}
              setOpenedMember={setOpenedMember}
              contestType={g.contest_type}
              eventPar={eventPar}
              roundScoresAll={roundScoresAll}
              picksByUserId={picksByUserId}
            />
          </>
        )}
        {/* UFC has TWO pick_em contest_types (regular + Main-Card-Only).
            Both route to PickemMode — its isMainCardOnly branch (PickemMode.jsx
            ~L786) handles the MCO-specific filter + curated main-card list
            via mainCard.js. Strict equality to 'pick_em' would render the
            contest tab BLANK for MCO pools (no other block in the L4067-4080
            chain matches), since MCO is UFC-only and the Golf/divisions/
            bracket blocks below all exclude UFC. Mirrors the pattern at
            getGroupLabel (L483) which already handles both. */}
        {groupTab === 'contest' && !isArchived && (g.contest_type === 'pick_em' || g.contest_type === 'pick_em_main_card_only') && g.sport !== 'Golf' && (
          /* Pattern #2 freemium tease — pickem prototype (after divisions
             66f2a6b and chat 52dd228). Always render real PickemMode; pass
             `locked` so the per-tap auto-save and handleSubmit both gate
             to onUnlockRequest. GolfPicks + Bracket branches below still
             use the showLockedShell ternary. */
          <PickemMode
            group={g} user={user} members={groupMembers}
            onPointsUpdate={handlePtsUpdate} gradeKey={gradeKey}
            locked={showLockedShell}
            onUnlockRequest={handleUnlock}
            unlocking={unlocking}
            unlockPriceCents={lockedPriceCents}
            promptAuth={() => setShowAuthPrompt(true)}
          />
        )}
        {groupTab === 'contest' && !isArchived && g.contest_type === 'pick_em' && g.sport === 'Golf' && (
          /* Pattern #2 freemium tease — golf-picks prototype (after
             divisions 66f2a6b, chat 52dd228, pickem 4b3e78b). Always
             render real GolfPicksMode; pass `locked` so the per-tap
             auto-save and handleSubmit both gate to onUnlockRequest, and
             the existing sticky-submit is suppressed in favor of the
             sticky unlock bar (single bottom CTA, z:90). Bracket branch
             below still uses the showLockedShell ternary. */
          <GolfPicksMode
            group={g} user={user} members={groupMembers}
            onPointsUpdate={handlePtsUpdate} gradeKey={gradeKey}
            locked={showLockedShell}
            onUnlockRequest={handleUnlock}
            unlocking={unlocking}
            unlockPriceCents={lockedPriceCents}
            promptAuth={() => setShowAuthPrompt(true)}
          />
        )}
        {groupTab === 'contest' && !isArchived && g.contest_type === 'divisions' && g.sport === 'Golf' && (
          /* Pattern #2 freemium tease — divisions prototype. Always render
             the real GolfDivisionsMode picker (no full-screen LockedShell
             on the contest tab); pass `locked` so the picker can render an
             inline unlock CTA + intercept at handleSubmit. The other 3
             modes still use the showLockedShell ? <LockedShell/> : <Mode/>
             ternary above/below. */
          <GolfDivisionsMode
            group={g} user={user} members={groupMembers}
            onPointsUpdate={handlePtsUpdate} gradeKey={gradeKey}
            locked={showLockedShell}
            onUnlockRequest={handleUnlock}
            unlocking={unlocking}
            unlockPriceCents={lockedPriceCents}
            promptAuth={() => setShowAuthPrompt(true)}
          />
        )}
        {groupTab === 'contest' && !isArchived && g.contest_type === 'bracket' && (
          /* Pattern #2 freemium tease — bracket prototype (the LAST mode;
             after divisions 66f2a6b, chat 52dd228, pickem 4b3e78b,
             golf-picks da599af). Always render real BracketMode; pass
             `locked` so runSave (centralized save path; intercepts per-
             tap auto-save AND legacy v1 migration) AND handleSubmit both
             gate to onUnlockRequest. In-flow Lock In button stays
             visible — its onClick fires the gated handleSubmit. With
             this commit, ALL contest tab branches use the always-render
             pattern; no LockedShell ternary remains on the contest
             tabs. */
          <BracketMode
            group={g} user={user} members={groupMembers}
            onPointsUpdate={handlePtsUpdate}
            locked={showLockedShell}
            onUnlockRequest={handleUnlock}
            unlocking={unlocking}
            unlockPriceCents={lockedPriceCents}
            promptAuth={() => setShowAuthPrompt(true)}
          />
        )}
        {groupTab === 'results' && g.sport !== 'Golf' && (
          g.contest_type === 'bracket'
            ? <BracketResultsFeed group={g} user={user} />
            : <ResultsFeed group={g} user={user} members={groupMembers} />
        )}
        {groupTab === 'members' && (
          <MembersList
            members={groupMembers}
            userId={user.id}
            onMemberClick={setOpenedMember}
            contestType={g.contest_type}
            eventPar={eventPar}
            roundScoresAll={roundScoresAll}
            picksByUserId={picksByUserId}
          />
        )}
        {groupTab === 'chat' && (
          /* Pattern #2 freemium tease — chat gate. Thread the same 4 props
             the divisions branch uses (commit 66f2a6b); GroupChat intercepts
             handleSend / toggleReaction's add path / handleDelete on locked
             pools, disables the input, and renders a sticky unlock bar
             matching the GolfDivisionsMode one. 041's RESTRICTIVE policies
             on group_messages + message_reactions are the real enforcement;
             this is the graceful client gate so users don't see raw 42501. */
          <GroupChat
            group={g} user={user} members={groupMembers}
            locked={showLockedShell}
            onUnlockRequest={handleUnlock}
            unlocking={unlocking}
            unlockPriceCents={lockedPriceCents}
            promptAuth={() => setShowAuthPrompt(true)}
            blockedUserIds={blockedUserIds}
            onUserBlocked={onUserBlocked}
          />
        )}

        {g.created_by === user.id && (!g.status || g.status === 'active') && (
          <div style={{ marginTop:'24px', paddingTop:'16px', borderTop:'1px solid var(--border)' }}>
            {groupMembers.length > 1 ? (
              <div className="alert alert-info" style={{ fontSize:'13px' }}>
                You cannot delete a group once other players have joined. Contact support to resolve this.
              </div>
            ) : confirmDelete ? (
              <>
                <div style={{ fontSize:'14px', color:'var(--text)', marginBottom:'12px', textAlign:'center' }}>
                  Delete this group? This cannot be undone.
                </div>
                {deleteError && (
                  <div className="alert alert-error" style={{ marginBottom:'12px' }}>{deleteError}</div>
                )}
                <div style={{ display:'flex', gap:'8px' }}>
                  <button className="btn btn-ghost"  onClick={() => { setConfirmDelete(false); setDeleteError('') }} style={{ flex:1 }}>Cancel</button>
                  <button className="btn btn-danger" onClick={handleDeleteGroup} style={{ flex:1 }}>Delete</button>
                </div>
              </>
            ) : (
              <button className="btn btn-danger" onClick={() => setConfirmDelete(true)}>
                Delete Group
              </button>
            )}
          </div>
        )}
      </div>

      {/* Drill-down sheet — driven by openedMember state above. Both the
          live Standings rows (top of this page) and the MembersList tab's
          rows call setOpenedMember(member) to open it. Dispatch by
          contest_type so divisions pools open the divisions-specific sheet
          (per-tier list, kept/dropped indicator) while every other mode
          continues to open the byte-identical pick'em MemberResultsSheet. */}
      {openedMember && (
        g.contest_type === 'divisions'
          ? <DivisionsMemberResultsSheet
              group={g}
              member={openedMember}
              currentUserId={user?.id || null}
              eventPar={eventPar}
              roundScoresAll={roundScoresAll}
              pollError={pollError}
              onClose={() => setOpenedMember(null)}
            />
          : <MemberResultsSheet
              group={g}
              member={openedMember}
              onClose={() => setOpenedMember(null)}
            />
      )}
    </div>
  )
}

// ── Members tab ──────────────────────────────────────────────────────────────
// Reads the already-loaded groupMembers list (same query that drives Standings
// — no separate fetch) and renders it sorted alphabetically by username.
// Each row: avatar + name + "(you)" marker if self. Deliberately omits
// points/rank — Standings owns that surface.
//
// Each row is also an entry point to View 2 of the Results rebuild: clicking
// a member opens MemberResultsSheet. The openedMember state lives in
// GroupDetail (lifted) so the live Standings rows can also open the same
// sheet instance via the same handler. Self-rows are clickable (opens own
// results — harmless and consistent).
function MembersList({
  members, userId, onMemberClick,
  contestType, eventPar = null,
  roundScoresAll = null, picksByUserId = null,
}) {
  const sorted = [...members].sort((a, b) =>
    (a.username || '').localeCompare(b.username || '')
  )
  // Identity-organized tab — sort stays alphabetical regardless of contest_type.
  // Only the per-row VALUE/LABEL changes for divisions; row ORDER does not.
  // Standings ranking lives on the Standings tab via StandingsPodium.
  const isDivisionsPool = contestType === 'divisions'
  const ctx = isDivisionsPool
    ? { contestType, eventPar, picksByUserId, roundScoresAll }
    : null
  return (
    <>
      <div style={{ fontSize:'12px', color:'var(--text-dim)', fontFamily:'Arial,sans-serif', marginBottom:'10px' }}>
        Tap a member to see their results.
      </div>
      {sorted.map(m => {
        const isMe = m.user_id === userId
        const initial = m.username?.[0]?.toUpperCase()
        // Divisions: helper-derived to-par display (matches StandingsPodium
        // + summary header). Pickem: verbatim prior render — m.points + PTS.
        const score = isDivisionsPool ? divisionsMemberScore(m, ctx) : null
        return (
          <div
            key={m.id}
            className={`member-row${isMe ? ' is-me' : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => onMemberClick(m)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onMemberClick(m)
              }
            }}
          >
            <div className="member-row__avatar">
              {m.avatar_url ? (
                <img
                  src={m.avatar_url}
                  alt=""
                  onError={e => { e.currentTarget.style.display = 'none' }}
                />
              ) : (
                initial
              )}
            </div>
            <div className="member-row__main">
              <div className="member-row__name">
                {m.username}
                {isMe && <span className="member-row__name-you">(you)</span>}
              </div>
            </div>
            <div className="member-row__points">
              <div className="member-row__points-val">{score ? score.text : (m.points ?? 0).toLocaleString()}</div>
              <div className="member-row__points-lbl">{score ? score.label : 'PTS'}</div>
            </div>
            <span className="member-row__chev">›</span>
          </div>
        )
      })}
    </>
  )
}

// ── Member results bottom sheet ──────────────────────────────────────────────
// View 2 of the Results rebuild — drilldown from MembersList. Loads the
// clicked member's graded picks via loadGradedPicks(group.id, member.user_id)
// and renders them with the shared renderResultPick helper. For non-self
// members, the read is allowed by migration 025's cross-member SELECT policy;
// pending picks are filtered at the DB layer (anti-cheating guarantee), so a
// member without graded picks renders the empty state.
//
// Bottom-sheet structure mirrors GameDetailSheet (App.jsx ~1937) — same
// backdrop, drag handle, max-width, close affordances (tap-outside + × button).
function MemberResultsSheet({ group, member, onClose }) {
  const [picks, setPicks] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!group?.id || !member?.user_id) return
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const data = await loadGradedPicks(group.id, member.user_id)
      if (cancelled) return
      setPicks(data)
      setLoading(false)
    })()
    return () => { cancelled = true }
  }, [group?.id, member?.user_id])

  const correctCount = picks.filter(p => p.result === 'correct').length
  const totalPoints  = picks.reduce((sum, p) => sum + (p.points_earned || 0), 0)

  return (
    <div style={{ position:'fixed', inset:0, zIndex:200, display:'flex', flexDirection:'column', justifyContent:'flex-end' }}>
      <div onClick={onClose} style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.72)' }} />
      <div style={{ position:'relative', zIndex:1, background:'var(--surface)', borderRadius:'18px 18px 0 0',
        paddingBottom:'40px', maxHeight:'90vh', overflowY:'auto', width:'100%', maxWidth:'520px',
        margin:'0 auto', boxSizing:'border-box', WebkitOverflowScrolling:'touch' }}>
        <div style={{ width:'40px', height:'4px', background:'rgba(255,255,255,0.2)', borderRadius:'2px', margin:'14px auto 0' }} />

        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 20px 0' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'10px', minWidth:0 }}>
            {member.avatar_url ? (
              <img src={member.avatar_url} alt="" className="lb-avatar"
                style={{ objectFit:'cover', display:'block' }}
                onError={e => { e.currentTarget.style.display = 'none' }} />
            ) : (
              <div className="lb-avatar">{member.username?.[0]?.toUpperCase()}</div>
            )}
            <div style={{ minWidth:0 }}>
              <div style={{ fontSize:'15px', fontWeight:'bold', color:'var(--gold)', fontFamily:'inherit', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {member.username}'s results
              </div>
              {!loading && picks.length > 0 && (
                <div style={{ fontSize:'12px', color:'var(--text-dim)', marginTop:'2px', fontFamily:'Arial,sans-serif' }}>
                  {correctCount} of {picks.length} · <span style={{ color:'var(--gold)', fontWeight:'bold' }}>{totalPoints}pt</span>
                </div>
              )}
            </div>
          </div>
          <button onClick={onClose} style={{ background:'rgba(255,255,255,0.08)', border:'none', color:'var(--text-dim)', cursor:'pointer', width:'28px', height:'28px', borderRadius:'50%', fontSize:'18px', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>×</button>
        </div>

        {/* Body */}
        <div style={{ padding:'14px 20px 0' }}>
          {loading ? (
            <div style={{ textAlign:'center', padding:'40px', color:'var(--text-dim)' }}>
              <div style={{ width:'24px', height:'24px', border:'2px solid var(--border)', borderTopColor:'var(--gold)', borderRadius:'50%', margin:'0 auto 12px', animation:'spin .8s linear infinite' }} />
              Loading results…
            </div>
          ) : picks.length === 0 ? (
            <div style={{ textAlign:'center', padding:'40px 16px', color:'var(--text-dim)' }}>
              <div style={{ fontSize:'40px', marginBottom:'10px' }}>📭</div>
              <div style={{ fontSize:'15px', fontWeight:'bold', color:'var(--text)' }}>No graded results yet for {member.username}</div>
              <div style={{ fontSize:'12px', marginTop:'6px' }}>Check back after games finish</div>
            </div>
          ) : (
            picks.map(renderResultPick)
          )}
        </div>
      </div>
    </div>
  )
}

// ── Divisions live-view constants + pure helpers ──────────────────────────
// Polling cadence + max-failures mirror matchup's GolfPicksMode constants
// (LIVE_POLL_INTERVAL_MS / LIVE_POLL_MAX_FAILURES) verbatim — same
// DataGolf rate-limit profile, same backoff-after-3-fails posture.
const DIVISIONS_LIVE_POLL_INTERVAL_MS  = 75 * 1000
const DIVISIONS_LIVE_POLL_MAX_FAILURES = 3

// computePerPickCurrentTotal — honest-current per-pick running total,
// in RELATIVE-TO-PAR units. Requires par; callers that don't have par
// yet must skip this and render '—' for the aggregate (per-round cells
// are rendered separately from raw to-par scores and don't need par).
//
// Returns { total, countedRounds, mcPenaltyRounds, upcomingRounds, perRound }.
// perRound[r] is one of: number (played, to-par from DataGolf row.round),
// 'MC' (round final + score null), null (upcoming/in-progress with no
// score yet) — caller renders '—' for null.
//
// Rules (locked spec decision 4 — honest current, not projected):
//   - played round (numeric to-par from DataGolf)          → add score
//   - rounds[r]==='final' AND score is null                → MC, add (80 - par) to-par penalty
//   - otherwise (round not final + no score)               → upcoming, add NOTHING
//
// Critically the MC penalty ONLY lands when the round is server-classified
// 'final' AND the golfer has no score. Rounds that haven't happened yet
// add zero — we do NOT pre-penalize unknown future rounds. This is what
// makes the running total "honest current," not a projection: the number
// reflects what has actually happened, not a guess about who'll miss the
// cut. The visual jump when a round transitions 'in_progress' → 'final'
// (missed-cut golfers gain +(80-par) instantly) is intentional and the
// row's per-round strip shows the reason in the same view.
//
// Unit fix vs prior shape (pre-2026-06-22): cut-round contribution was
// `total += 80` (raw strokes), producing a mixed-unit `total` that
// disagreed with raw post-grade values for cut golfers. Now `total +=
// (80 - par)` so `total` is uniformly relative-to-par across played
// and cut contributions. Sort order against post-grade raw-strokes is
// preserved (relative differs from raw by a constant per pick) AND
// pre-grade kept/dropped now agrees with what the cron eventually
// writes to total_strokes.
//
// Pre-grade-display-only helper — does NOT feed total_strokes,
// divisions_score, post-grade kept/dropped, finalize_group_atomic, or
// any DB write path. See migration 047 + the cron grader for the
// authoritative post-grade scoring path.
function computePerPickCurrentTotal(dgId, scoresByDg, rounds, par) {
  const ds = scoresByDg?.[String(dgId)] || {}
  let total           = 0
  let countedRounds   = 0
  let mcPenaltyRounds = 0
  let upcomingRounds  = 0
  const perRound = { 1: null, 2: null, 3: null, 4: null }
  for (const r of [1, 2, 3, 4]) {
    const s     = ds[r]
    const state = rounds?.[r] || 'no_data'
    if (typeof s === 'number') {
      total += s
      countedRounds++
      perRound[r] = s
    } else if (state === 'final') {
      total += (80 - par)
      mcPenaltyRounds++
      perRound[r] = 'MC'
    } else {
      upcomingRounds++
      // perRound[r] stays null → renders '—' downstream
    }
  }
  return { total, countedRounds, mcPenaltyRounds, upcomingRounds, perRound }
}

// computeCurrentBestFour — pre-grade entry score from live data.
//
// Returns { perPick, keptIds, bestFourSum, hasSignal }.
// hasSignal: at least one pick has played-or-MC rounds. Gates whether the
// header surfaces a real number vs. '—' (showing "0 to par · current"
// before any round has data is misleading; gate behind hasSignal).
//
// bestFourSum is in RELATIVE-TO-PAR units (post-2026-06-22 helper fix),
// directly renderable via formatScoreToPar with no further conversion.
//
// Post-grade callers should NOT use this — they read from persisted
// total_strokes per pick (the f5a978b authoritative path) so the official
// result matches what the grader wrote. Pre-grade kept/dropped derived
// from these values now agrees with what the cron will write to
// total_strokes (constant-shift sort preserved).
function computeCurrentBestFour(picks, scoresByDg, rounds, par) {
  const perPick = picks.map(p => ({
    pick:    p,
    current: computePerPickCurrentTotal(p.selected_dg_id, scoresByDg, rounds, par),
  }))
  const sorted      = [...perPick].sort((a, b) => a.current.total - b.current.total)
  const keptIds     = new Set(sorted.slice(0, 4).map(e => e.pick.selected_dg_id))
  const bestFourSum = sorted.slice(0, 4).reduce((s, e) => s + e.current.total, 0)
  const hasSignal   = perPick.some(pp => pp.current.countedRounds > 0 || pp.current.mcPenaltyRounds > 0)
  return { perPick, keptIds, bestFourSum, hasSignal }
}

// divisionsMemberScore — single source of truth for the per-member display
// across StandingsPodium, the GroupDetail summary header, and MembersList.
// Returns { text, label, aria, sortKey, isLive } so callers stay byte-
// identical to each other without each one re-deriving the decision tree.
//
// ctx = { contestType, eventPar, picksByUserId, roundScoresAll }
//   - contestType   group.contest_type ('divisions' triggers the golf path)
//   - eventPar      number | null — known iff event is in KNOWN_EVENT_PARS
//   - picksByUserId { [userId]: picks[] } — Commit 2 fetch, may be missing
//                   a member if their fetch failed
//   - roundScoresAll { scoresByDg, rounds, fetchedAt } — Commit 1 poll, may
//                   be empty before first tick
//
// Branches (in priority order):
//   1. Non-divisions     → m.points (post-grade or 0), label 'PTS'.
//                          sortKey 0 — caller doesn't sort non-divisions.
//   2. Post-grade div    → divisions_score formatted to-par if par known,
//                          else raw strokes. Label 'TO PAR' / 'STROKES'.
//                          sortKey in the same unit as label.
//   3. Pre-grade div +   → live bestFourSum formatted to-par. Label
//      live signal         '{base} · LIVE'. sortKey = bestFourSum.
//   4. Pre-grade div +   → '—' / base label / Infinity sortKey (sinks).
//      no signal
//
// All formula logic (eventPar*16 conversion, gate-chain on liveTotalFor,
// hasSignal check) is verbatim from the prior StandingsPodium-inline
// closures. This is a refactor, not a reimplementation.
function divisionsMemberScore(member, ctx) {
  const { contestType, eventPar, picksByUserId, roundScoresAll } = ctx
  const isDivisions = contestType === 'divisions'
  const isParKnown  = isDivisions && typeof eventPar === 'number'

  if (!isDivisions) {
    const pts = (member.points || 0).toLocaleString()
    return {
      text:    pts,
      label:   'PTS',
      aria:    `${pts} points`,
      sortKey: 0,
      isLive:  false,
    }
  }

  const base = isParKnown ? 'TO PAR' : 'STROKES'

  if (member.divisions_score != null) {
    const toParText = isParKnown
      ? formatScoreToPar(member.divisions_score - eventPar * 16)
      : String(member.divisions_score)
    return {
      text:    toParText,
      label:   base,
      aria:    isParKnown
        ? `${toParText} to par`
        : `${member.divisions_score} strokes`,
      sortKey: isParKnown ? (member.divisions_score - eventPar * 16) : member.divisions_score,
      isLive:  false,
    }
  }

  // Pre-grade — try the live signal (verbatim gate chain from prior code).
  let live = null
  if (isParKnown) {
    const picks = picksByUserId?.[member.user_id]
    if (Array.isArray(picks) && picks.length > 0) {
      const scoresByDg = roundScoresAll?.scoresByDg
      const rounds    = roundScoresAll?.rounds
      if (scoresByDg && rounds) {
        live = computeCurrentBestFour(picks, scoresByDg, rounds, eventPar)
      }
    }
  }
  if (live && live.hasSignal) {
    const liveText = formatScoreToPar(live.bestFourSum)
    return {
      text:    liveText,
      label:   `${base} · LIVE`,
      aria:    `${liveText} to par, live`,
      sortKey: live.bestFourSum,
      isLive:  true,
    }
  }

  return {
    text:    '—',
    label:   base,
    aria:    'no score yet',
    sortKey: Number.POSITIVE_INFINITY,
    isLive:  false,
  }
}

// Divisions drill-down sheet — SIBLING to MemberResultsSheet. The original
// is pick'em-shaped (loadGradedPicks against picks, correct/incorrect
// semantics, points_earned aggregation). Divisions has a different data
// path (golf_division_picks, raw strokes, best-4-of-6) and renders a
// per-tier list rather than per-game rows, so the two coexist as separate
// components. The dispatch at openedMember branches on contest_type.
//
// SHELL is intentionally copied verbatim from MemberResultsSheet (backdrop,
// drag handle, header, scrollable body, z-index 200, max-width 520, max-
// height 90vh) — keeping the modal chrome in sync visually without
// extracting a shared base component. If the modal chrome design changes,
// both sheets need updating.
//
// LIVE per-round view: on open, polls fetchGolfRoundScores for the
// configured event. Per-pick row shows R1-R4 raw strokes + running total;
// header shows 'current' best-4-of-6 (pre-grade) or 'official' divisions_
// score (post-grade). Honest about errors — fetch failures and DataGolf
// focus-rotation both surface as an "unavailable" sub-line + '—' cells,
// never as silent dashes implying "not started" (the [[pairings-silent-
// empty-fallback]] lesson). Round state is read server-side from
// roundScoresAll.rounds[r] (NOT inferred from pairings + clock — sidesteps
// the matchup [[round-state-ui-inference]] bug).
//
// Post-grade: persisted total_strokes + divisions_score remain authoritative.
// Interval polling stops once member.divisions_score is non-null; a single
// fetch on open still populates the R1-R4 strip alongside the official total.
function DivisionsMemberResultsSheet({ group, member, currentUserId, eventPar, roundScoresAll, pollError, onClose }) {
  // par-known is the gate for relative-to-par display. When eventPar is
  // null (parent's fetch hasn't resolved, fetch failed, or the event
  // isn't in KNOWN_EVENT_PARS), fall back to raw stroke display rather
  // than guess a par. Same posture across all three display sites (sheet
  // header, per-pick total, leaderboard).
  const isParKnown = typeof eventPar === 'number'
  const [picks,          setPicks]          = useState([])
  const [loading,        setLoading]        = useState(true)
  // roundScoresAll + pollError are hoisted to GroupDetail as of Commit 1
  // of running-leaderboard work — passed in as props now so the parent can
  // share one poll with the leaderboard. Format unchanged from the prior
  // local state.

  const eventId       = group?.golf_config?.eventId || null
  // Self-vs-other branch: owners read their own picks via own-only RLS
  // (fast, no server roundtrip); non-owners read via the Slice 1 reveal
  // endpoint which applies the tee-time gate server-side and masks
  // identity for unrevealed picks. currentUserId is threaded from
  // GroupDetail where `user` lives.
  const isViewingSelf = !!(currentUserId && member?.user_id && currentUserId === member.user_id)

  useEffect(() => {
    if (!group?.id || !member?.user_id || !eventId) {
      setLoading(false)
      return
    }
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const data = isViewingSelf
        ? await loadDivisionGradedPicks(group.id, member.user_id, eventId)
        : (await fetchMemberDivisionPicks(group.id, member.user_id)).picks
      if (cancelled) return
      setPicks(data)
      setLoading(false)
    })()
    return () => { cancelled = true }
  }, [group?.id, member?.user_id, eventId, isViewingSelf])

  // Live per-round fetch + poll lives in GroupDetail (Commit 1 hoist).
  // The parent runs ONE poll per pool view, shared with the leaderboard,
  // and threads roundScoresAll + pollError down as props.

  // Reveal-aware split (Slice 1): for non-owner views, hidden picks
  // arrive with revealed:false and identity nulled. They MUST NOT
  // participate in kept/dropped sorting (would corrupt the live
  // best-4) or feed computePerPickCurrentTotal (selected_dg_id is
  // null). Owner views always have revealed!==false on every row.
  const revealedPicks   = picks.filter(p => p.revealed !== false)
  const revealedCount   = revealedPicks.length
  const allSixRevealed  = picks.length === 6 && revealedCount === 6

  // POST-GRADE authoritative path — preserved verbatim from f5a978b.
  // Sort the 6 picks by persisted total_strokes ASC; lowest 4 = kept
  // (contribute to divisions_score), highest 2 = dropped. Only fires
  // when all 6 picks have an integer total_strokes (defends partial
  // grading from rendering misleading kept/dropped badges) AND all 6
  // are revealed (a non-owner viewing a still-in-progress entry can
  // see graded scores for revealed picks but kept/dropped requires
  // the full set, since the lowest-4 calculation needs all 6 totals).
  const isGraded = allSixRevealed && picks.every(p => p.total_strokes != null)
  const postGradeKeptDgIds = isGraded
    ? new Set(
        [...picks]
          .sort((a, b) => a.total_strokes - b.total_strokes)
          .slice(0, 4)
          .map(p => p.selected_dg_id)
      )
    : new Set()

  // PRE-GRADE live computation — current best-4-of-6 from honest current
  // totals (NOT projected). Only consulted when !isGraded; post-grade
  // ignores this entirely so the official result stays authoritative.
  // Filter to revealed picks ONLY for the same reason as isGraded above:
  // unrevealed picks have no selected_dg_id, so computePerPickCurrentTotal
  // would return zeros and corrupt the live best-4 sort.
  //
  // par-required: the helper now produces relative-to-par totals. With
  // par unknown we cannot compute them honestly. Return a no-signal
  // shape so the rest of the render falls back to raw / '—' branches.
  const liveBestFour = isParKnown
    ? computeCurrentBestFour(
        revealedPicks,
        roundScoresAll.scoresByDg,
        roundScoresAll.rounds,
        eventPar,
      )
    : { perPick: [], keptIds: new Set(), bestFourSum: 0, hasSignal: false }

  // Live availability — distinct from pollError. Set when (a) we've done
  // at least one fetch (fetchedAt != null) AND (b) no pick has any
  // playable signal yet (every cell is upcoming or score-less). This is
  // the DataGolf focus-rotation case: fetch SUCCEEDED with empty/wrong
  // data, no fetchError raised. Surfaces the same "unavailable" copy as
  // pollError but via a different code path.
  const liveUnavailable =
    pollError ||
    (roundScoresAll.fetchedAt != null && !liveBestFour.hasSignal)

  // Header score + label + unit — three terminal states + one transient.
  //   post-grade            → divisions_score, 'official'
  //   pre-grade unavailable → '—', 'unavailable'
  //   pre-grade signal      → bestFourSum, 'current'
  //   pre-grade pre-signal  → '—', 'current'  (transient first-load OR genuine
  //                                            pre-tournament state — no
  //                                            number to show, but it IS the
  //                                            current best-4-of-zero)
  //
  // Unit: relative-to-par when isParKnown, raw strokes otherwise.
  // Conversion at this layer: divisions_score - eventPar × 16 (best-4
  // × 4 rounds). Pre-grade bestFourSum from liveBestFour is already
  // relative under the updated computePerPickCurrentTotal.
  let headerScore, headerLabel
  const headerUnit = isParKnown ? 'to par' : 'strokes'
  if (member.divisions_score != null) {
    headerScore = isParKnown
      ? formatScoreToPar(member.divisions_score - eventPar * 16)
      : String(member.divisions_score)
    headerLabel = 'official'
  } else if (liveUnavailable) {
    headerScore = '—'
    headerLabel = 'unavailable'
  } else if (isParKnown && liveBestFour.hasSignal) {
    headerScore = formatScoreToPar(liveBestFour.bestFourSum)
    headerLabel = 'current'
  } else {
    headerScore = '—'
    headerLabel = 'current'
  }

  return (
    <div style={{ position:'fixed', inset:0, zIndex:200, display:'flex', flexDirection:'column', justifyContent:'flex-end' }}>
      <div onClick={onClose} style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.72)' }} />
      <div style={{ position:'relative', zIndex:1, background:'var(--surface)', borderRadius:'18px 18px 0 0',
        paddingBottom:'40px', maxHeight:'90vh', overflowY:'auto', width:'100%', maxWidth:'520px',
        margin:'0 auto', boxSizing:'border-box', WebkitOverflowScrolling:'touch' }}>
        <div style={{ width:'40px', height:'4px', background:'rgba(255,255,255,0.2)', borderRadius:'2px', margin:'14px auto 0' }} />

        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 20px 0' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'10px', minWidth:0 }}>
            {member.avatar_url ? (
              <img src={member.avatar_url} alt="" className="lb-avatar"
                style={{ objectFit:'cover', display:'block' }}
                onError={e => { e.currentTarget.style.display = 'none' }} />
            ) : (
              <div className="lb-avatar">{member.username?.[0]?.toUpperCase()}</div>
            )}
            <div style={{ minWidth:0 }}>
              <div style={{ fontSize:'15px', fontWeight:'bold', color:'var(--gold)', fontFamily:'inherit', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {member.username}'s entry
              </div>
              <div style={{ fontSize:'12px', color:'var(--text-dim)', marginTop:'2px', fontFamily:'Arial,sans-serif' }}>
                <span style={{ color:'var(--gold)', fontWeight:'bold' }}>{headerScore}</span>
                {' '}{headerUnit} · best 4 of 6 · {headerLabel}
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ background:'rgba(255,255,255,0.08)', border:'none', color:'var(--text-dim)', cursor:'pointer', width:'28px', height:'28px', borderRadius:'50%', fontSize:'18px', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>×</button>
        </div>

        {/* Pre-grade "unavailable" banner — HONEST copy. Fires when fetch
            failed beyond MAX_FAILURES OR the event has rotated past DataGolf's
            focus (succeeded with empty data). Post-grade entries still show
            the persisted divisions_score / total_strokes authoritatively
            (handled in the header + per-pick rendering below). */}
        {member.divisions_score == null && !loading && picks.length > 0 && liveUnavailable && (
          <div style={{
            margin: '12px 20px 0',
            padding: '10px 12px',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 8,
            fontSize: 12,
            color: 'var(--text-dim)',
            fontFamily: 'Arial,sans-serif',
            lineHeight: 1.5,
          }}>
            {pollError
              ? "Live scoring is temporarily unavailable. Reconnecting automatically — totals will populate once the feed comes back."
              : "Live scores for your picks aren't in yet. Per-pick totals will populate as your golfers play."}
          </div>
        )}

        {/* Reveal banner — non-owner views only, hidden when all 6
            picks are revealed. Tells the viewer the gate is in effect
            so a partial entry doesn't read as "they only picked N." */}
        {!isViewingSelf && picks.length > 0 && revealedCount < picks.length && (
          <div style={{
            margin: '12px 20px 0',
            padding: '10px 12px',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 8,
            fontSize: 12,
            color: 'var(--text-dim)',
            fontFamily: 'Arial,sans-serif',
            lineHeight: 1.5,
          }}>
            {revealedCount} of {picks.length} picks revealed · others reveal at tee time
          </div>
        )}

        {/* Body */}
        <div style={{ padding:'14px 20px 0' }}>
          {loading ? (
            <div style={{ textAlign:'center', padding:'40px', color:'var(--text-dim)' }}>
              <div style={{ width:'24px', height:'24px', border:'2px solid var(--border)', borderTopColor:'var(--gold)', borderRadius:'50%', margin:'0 auto 12px', animation:'spin .8s linear infinite' }} />
              Loading entry…
            </div>
          ) : picks.length === 0 ? (
            <div style={{ textAlign:'center', padding:'40px 16px', color:'var(--text-dim)' }}>
              <div style={{ fontSize:'40px', marginBottom:'10px' }}>⛳</div>
              <div style={{ fontSize:'15px', fontWeight:'bold', color:'var(--text)' }}>{member.username} hasn't submitted an entry</div>
              <div style={{ fontSize:'12px', marginTop:'6px' }}>6 picks across 6 tiers required</div>
            </div>
          ) : (
            picks.map(p => {
              // Slice 1 reveal gate: hidden pick → render the locked-row
              // placeholder + bail. selected_dg_id is null on hidden picks
              // so we can't poll for scores or compute kept/dropped, and
              // rendering the golfer name would defeat the whole point of
              // the server-side identity strip.
              if (p.revealed === false) {
                return (
                  <div key={p.id} style={{
                    background:'var(--surface2)',
                    border:'1px solid var(--border)', borderRadius:10, marginBottom:6,
                    padding:'10px 12px',
                    opacity: 0.55,
                  }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <span style={{
                        fontSize:11, color:'var(--text-muted)', fontFamily:'Arial,sans-serif',
                        letterSpacing:0.5, minWidth: 22,
                      }}>
                        T{p.tier}
                      </span>
                      <span style={{ fontSize:13, color:'var(--text-muted)' }}>
                        🔒 Hasn't teed off yet
                      </span>
                    </div>
                  </div>
                )
              }
              // Live per-round breakdown for THIS pick. Used by both pre-
              // grade total computation AND the R1-R4 strip rendering —
              // the same source guarantees the visible cells sum to the
              // visible total. MC marker in a cell is always paired with
              // a +80 in the row's running total: the cause of any "current"
              // number jump is visible IN THE SAME view (locked spec).
              // Live per-pick total + per-round strip. When par is
              // unknown we still want the perRound strip (to-par
              // numbers don't need par), so pass eventPar ?? 72 just
              // for the cut-penalty arithmetic — but the aggregate
              // `total` from this helper is only DISPLAYED when
              // isParKnown (else we render raw or '—').
              const liveTotalInfo = computePerPickCurrentTotal(
                p.selected_dg_id,
                roundScoresAll.scoresByDg,
                roundScoresAll.rounds,
                eventPar ?? 72,
              )
              // Total to display: post-grade reads the persisted total
              // (authoritative); pre-grade reads live (or '—' before
              // any signal). Format: relative-to-par when isParKnown,
              // raw integer otherwise. NEVER show 0 with no signal —
              // that reads as if the golfer shot zero, not "data
              // hasn't arrived."
              let totalToShow
              if (isGraded) {
                totalToShow = isParKnown
                  ? formatScoreToPar(p.total_strokes - eventPar * 4)
                  : String(p.total_strokes)
              } else if (isParKnown && liveBestFour.hasSignal) {
                totalToShow = formatScoreToPar(liveTotalInfo.total)
              } else {
                totalToShow = '—'
              }
              // Kept/dropped sourcing: post-grade uses the persisted-sort
              // path (f5a978b — preserved verbatim above as postGradeKeptDgIds).
              // Pre-grade uses live keptIds but only renders badges once
              // there's a real signal (otherwise everyone tied at 0).
              const isKept = isGraded
                ? postGradeKeptDgIds.has(p.selected_dg_id)
                : (liveBestFour.hasSignal && liveBestFour.keptIds.has(p.selected_dg_id))
              const isDropped = isGraded
                ? !isKept
                : (liveBestFour.hasSignal && !isKept)
              const showKeptBadge = isGraded || liveBestFour.hasSignal
              return (
                <div key={p.id} style={{
                  background:'var(--surface2)',
                  border:'1px solid var(--border)', borderRadius:10, marginBottom:6,
                  opacity: isDropped ? 0.55 : 1,
                  padding:'10px 12px',
                }}>
                  {/* Row top: tier + name + kept/dropped + total */}
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:0 }}>
                      <span style={{
                        fontSize:11, color:'var(--text-muted)', fontFamily:'Arial,sans-serif',
                        letterSpacing:0.5, minWidth: 22,
                      }}>
                        T{p.tier}
                      </span>
                      <span style={{
                        fontSize:13, color:'var(--text)',
                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                      }}>
                        {p.selected_name}
                      </span>
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
                      {showKeptBadge && (
                        <span style={{
                          fontSize:10, fontFamily:'Arial,sans-serif', letterSpacing:0.5,
                          padding:'2px 6px', borderRadius:4, fontWeight:700,
                          color: isKept ? 'var(--green)' : 'var(--text-muted)',
                          background: isKept ? 'rgba(46,204,113,0.12)' : 'rgba(255,255,255,0.05)',
                          border: isKept ? '1px solid rgba(46,204,113,0.35)' : '1px solid rgba(255,255,255,0.10)',
                          textTransform:'uppercase',
                        }}>
                          {isKept ? 'KEPT' : 'DROPPED'}
                        </span>
                      )}
                      <span style={{
                        fontSize:14, fontWeight:700, fontFamily:'Arial,sans-serif',
                        color: isDropped ? 'var(--text-muted)' : 'var(--gold)',
                        minWidth: 32, textAlign:'right',
                      }}>
                        {totalToShow}
                      </span>
                    </div>
                  </div>

                  {/* Row bottom: R1-R4 strip. Cells from liveTotalInfo.perRound:
                      number = played, 'MC' = round final + score null (penalty
                      applied), null = upcoming/in-progress + no score yet
                      (rendered '—'). MC cells are visually marked so the +80
                      penalty in the row total has a visible source. */}
                  <div style={{
                    display:'flex', gap:8, marginTop:8,
                    fontSize:11, fontFamily:'Arial,sans-serif',
                  }}>
                    {[1, 2, 3, 4].map(r => {
                      const cell  = liveTotalInfo.perRound[r]
                      const isMC  = cell === 'MC'
                      const isNum = typeof cell === 'number'
                      const label = isNum ? formatScoreToPar(cell) : (isMC ? 'MC' : '—')
                      return (
                        <span key={r} style={{
                          display:'inline-flex', alignItems:'center', gap:4,
                          padding:'2px 6px', borderRadius:4,
                          background: isMC ? 'rgba(255,255,255,0.05)' : 'transparent',
                          border:     isMC ? '1px solid rgba(255,255,255,0.10)' : '1px solid transparent',
                          color: isNum ? 'var(--text)' : 'var(--text-muted)',
                        }}>
                          <span style={{ opacity:0.7 }}>R{r}</span>
                          <span style={{ fontWeight: isNum ? 700 : 500 }}>{label}</span>
                        </span>
                      )
                    })}
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}

// ── Hoisted from App's body (continuation of 620bbfa pattern) ───────────────
// Same rationale as 620bbfa: components defined inside App's body get a new
// function reference on every App render → React remounts their subtree.
// These four had no observable bug today (no internal state, no stateful
// descendants) but are latent ticking time bombs.

// Sport-specific noun for the FINAL row's margin label ("by 2 Runs",
// "by 7 Points", "by 1 Goal"). Pluralized in render based on magnitude.
const MARGIN_NOUN_BY_SPORT = { MLB: 'Run', NBA: 'Point', NFL: 'Point', NHL: 'Goal' }

// hex → rgba; mirrors the helper in PickemMode.jsx that the hero cards use
// for team-color overlays. Small enough to duplicate vs. extract for now.
// Null/empty input falls back to NEUTRAL_BG (#2a2a2a) so the gradient never
// errors — the tint just becomes near-invisible.
function hexToRGBA(hex, alpha) {
  if (!hex) return `rgba(42,42,42,${alpha})`
  const h = hex.replace('#', '')
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  return `rgba(${r},${g},${b},${alpha})`
}

// Section header for the LIVE NOW / FINAL game lists. Red dot for live;
// title text uppercase + letter-spaced to match the mockup typography.
function GameSectionHeader({ title, showLiveDot }) {
  return (
    <div style={{
      display:'flex', alignItems:'center', gap:'7px',
      padding:'14px 4px 8px',
      fontFamily:'Arial,sans-serif',
      fontSize:'12px', fontWeight:700,
      color:'#f4f6f8',
      textTransform:'uppercase', letterSpacing:'0.10em',
    }}>
      {showLiveDot && (
        <span style={{ width:8, height:8, borderRadius:'50%', background:'#e23b3b' }}/>
      )}
      {title}
    </div>
  )
}

// GameRow dispatcher — three distinct layouts per status:
//   LIVE     → 3-column horizontal with full-bleed inward team-color
//              gradients (away color from left, home from right, meeting
//              dark in the middle). Center column carries the MLB base
//              diamond + count or, for other sports, the live clock.
//   FINAL    → teams stacked on the left with their scores, center
//              "<Winner> Win / by N <noun>", right "FINAL / N Innings".
//              Fainter gradient than live.
//   UPCOMING → existing two-row gradient layout (spread + ML on the right).
function GameRow({ game, setSelectedGame }) {
  const awayColor = iconicTeamColor(game.sport, game.away.abbr, game.away.color, game.away.alternateColor)
  const homeColor = iconicTeamColor(game.sport, game.home.abbr, game.home.color, game.home.alternateColor)
  if (game.status === 'live')  return <LiveGameRow  game={game} awayColor={awayColor} homeColor={homeColor} setSelectedGame={setSelectedGame} />
  if (game.status === 'final') return <FinalGameRow game={game} awayColor={awayColor} homeColor={homeColor} setSelectedGame={setSelectedGame} />
  return                              <UpcomingGameRow game={game} awayColor={awayColor} homeColor={homeColor} setSelectedGame={setSelectedGame} />
}

// ═══════════════════════════════════════════════════════════════════════════════
// LIVE ROW — 3-column horizontal layout matching the mockup. Full-bleed
// gradient flows inward from each edge in the respective team color, fading
// to a dark center behind the live-context column.
// ═══════════════════════════════════════════════════════════════════════════════

function LiveGameRow({ game, awayColor, homeColor, setSelectedGame }) {
  const bothScored  = game.away.score != null && game.home.score != null
  const awayLeading = bothScored && game.away.score > game.home.score
  const homeLeading = bothScored && game.home.score > game.away.score

  // Two stacked linear gradients fading to transparent at 50%, so they meet
  // in the middle over a dark base without a hard seam.
  const cardBg = `linear-gradient(90deg, ${hexToRGBA(awayColor, 0.42)}, transparent 50%), linear-gradient(270deg, ${hexToRGBA(homeColor, 0.42)}, transparent 50%), #0d1016`

  return (
    <div onClick={() => setSelectedGame(game)} style={{
      background: cardBg,
      borderRadius: '12px',
      marginBottom: '10px',
      cursor: 'pointer',
      minHeight: '95px',
      display: 'grid',
      gridTemplateColumns: '1fr auto 1fr',
      alignItems: 'center',
      padding: '12px 14px',
      gap: '10px',
      overflow: 'hidden',
    }}>
      <LiveTeamSide team={game.away} sport={game.sport} side="left"  leading={awayLeading} />
      <LiveCenterColumn game={game} />
      <LiveTeamSide team={game.home} sport={game.sport} side="right" leading={homeLeading} />
    </div>
  )
}

function LiveTeamSide({ team, sport, side, leading }) {
  const isLeft   = side === 'left'
  const textAlign = isLeft ? 'left' : 'right'

  return (
    <div style={{
      display:'flex', flexDirection: isLeft ? 'row' : 'row-reverse',
      alignItems:'center', gap:'10px', minWidth:0,
    }}>
      <TeamLogoImg
        team={team.team} abbr={team.abbr} sport={sport}
        color={team.color} alternateColor={team.alternateColor} shortName={team.shortName}
        size={38}
      />
      <div style={{ flex:1, minWidth:0, textAlign }}>
        {team.location && (
          <div style={{
            fontSize:'10px', color:'#aab2bd', fontWeight:700,
            textTransform:'uppercase', letterSpacing:'0.06em',
            fontFamily:'Arial,sans-serif',
            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
          }}>
            {team.location}
          </div>
        )}
        <div style={{
          fontSize:'15px', fontWeight:800, color:'#f4f6f8',
          textTransform:'uppercase', letterSpacing:'0.02em',
          fontFamily:'Arial,sans-serif',
          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
        }}>
          {team.name || team.team}
        </div>
        {team.record && (
          <div style={{
            fontSize:'10px', color:'#7a828d', marginTop:'2px',
            fontFamily:'Arial,sans-serif',
          }}>
            {team.record}
          </div>
        )}
      </div>
      <div style={{
        display:'flex', alignItems:'center', gap:'4px',
        fontSize:'30px', fontWeight:800,
        color: leading ? 'var(--green)' : '#f4f6f8',
        fontFamily:'Arial,sans-serif',
        lineHeight: 1, flexShrink: 0,
      }}>
        {team.score != null ? team.score : '—'}
        {leading && <span style={{ fontSize:'14px', color:'var(--green)' }}>▲</span>}
      </div>
    </div>
  )
}

// LIVE center column — MLB renders the base diamond + balls-strikes count
// + outs; other sports get a simpler treatment (live dot + period + clock)
// per the build spec. UFC fights bypass parseESPN entirely so they have
// no `period`/`clock`; they'll just show the LIVE dot, which is the
// flagged simpler-center-column case.
function LiveCenterColumn({ game }) {
  const isMLB = game.sport === 'MLB'
  const hasSituation = isMLB && game.situation
  const periodLabel  = makePeriodInningLabel(game)
  const countLabel   = hasSituation ? makeCountLabel(game.situation) : null

  return (
    <div style={{
      display:'flex', flexDirection:'column',
      alignItems:'center', justifyContent:'center',
      gap:'3px', flexShrink:0,
      minWidth: '76px',
      fontFamily:'Arial,sans-serif',
    }}>
      <div style={{
        display:'flex', alignItems:'center', gap:'4px',
        fontSize:'9px', fontWeight:700,
        color:'#e23b3b', letterSpacing:'0.10em',
      }}>
        <span style={{ width:5, height:5, borderRadius:'50%', background:'#e23b3b' }}/>
        LIVE
      </div>
      {periodLabel && (
        <div style={{
          fontSize:'11px', fontWeight:700,
          color:'#f4f6f8', textTransform:'uppercase',
          letterSpacing:'0.04em',
        }}>
          {periodLabel}
        </div>
      )}
      {hasSituation && (
        <BaseDiamond
          onFirst={game.situation.onFirst}
          onSecond={game.situation.onSecond}
          onThird={game.situation.onThird}
        />
      )}
      {countLabel && (
        <div style={{
          fontSize:'9px', color:'#aab2bd',
          letterSpacing:'0.04em', fontWeight:700,
        }}>
          {countLabel}
        </div>
      )}
      {!isMLB && game.clock && (
        <div style={{
          fontSize:'10px', color:'#aab2bd',
          letterSpacing:'0.04em',
        }}>
          {game.clock}
        </div>
      )}
    </div>
  )
}

// Format MLB statusDetail ("Top 5th" / "Middle 4th") into the compact
// "TOP 5TH" / "MID 4TH" / "BOT 7TH" form the mockup uses. Other sports
// pass through their existing `period` string uppercased ("Q4" / "1ST" /
// etc.); empty when neither is available.
function makePeriodInningLabel(game) {
  if (game.sport === 'MLB' && game.statusDetail) {
    return game.statusDetail
      .replace(/Bottom/gi, 'Bot')
      .replace(/Middle/gi, 'Mid')
      .replace(/End of/gi, 'End')
      .toUpperCase()
  }
  return (game.period || '').toUpperCase()
}

// Compact "B-S | N OUT(S)" label. Hidden between innings (all-zero count
// AND empty bases) — the BaseDiamond + missing label communicate "no
// active at-bat" more cleanly than a stale "0-0 | 0 OUTS".
function makeCountLabel(situation) {
  const { balls, strikes, outs, onFirst, onSecond, onThird } = situation
  const noRunners = !onFirst && !onSecond && !onThird
  const zeroAll = (balls ?? 0) === 0 && (strikes ?? 0) === 0 && (outs ?? 0) === 0
  if (noRunners && zeroAll) return null
  return `${balls ?? 0}-${strikes ?? 0} | ${outs ?? 0} OUT${(outs ?? 0) !== 1 ? 'S' : ''}`
}

// ═══════════════════════════════════════════════════════════════════════════════
// FINAL ROW — teams stacked left (with inline scores + ✓ on winner), center
// "<Winner> Win / by N <noun>", right "FINAL / N Innings" with the small
// diamond glyph. Fainter gradient than live. Duration intentionally cut
// per build spec (would require a /summary?event=… fetch per row).
// ═══════════════════════════════════════════════════════════════════════════════

function FinalGameRow({ game, awayColor, homeColor, setSelectedGame }) {
  const bothScored = game.away.score != null && game.home.score != null
  const awayWon = bothScored && game.away.score > game.home.score
  const homeWon = bothScored && game.home.score > game.away.score
  const winner  = awayWon ? game.away : homeWon ? game.home : null
  const margin  = bothScored ? Math.abs(game.away.score - game.home.score) : null
  const marginNoun = MARGIN_NOUN_BY_SPORT[game.sport] || 'Point'

  // Fainter than live — same two-gradient shape, lower alpha.
  const cardBg = `linear-gradient(90deg, ${hexToRGBA(awayColor, 0.20)}, transparent 50%), linear-gradient(270deg, ${hexToRGBA(homeColor, 0.20)}, transparent 50%), #0d1016`

  return (
    <div onClick={() => setSelectedGame(game)} style={{
      background: cardBg,
      borderRadius: '12px',
      marginBottom: '10px',
      cursor: 'pointer',
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr) auto',
      gap: '12px',
      alignItems: 'center',
      padding: '12px 14px',
      minHeight: '105px',
      overflow: 'hidden',
    }}>
      {/* Left: teams stacked, each with their score + winner ✓ inline */}
      <div style={{ display:'flex', flexDirection:'column', gap:'10px', minWidth:0 }}>
        <FinalTeamRow team={game.away} sport={game.sport} won={awayWon} lost={homeWon} />
        <FinalTeamRow team={game.home} sport={game.sport} won={homeWon} lost={awayWon} />
      </div>

      {/* Center: "<Winner Name> Win / by N <Noun>" */}
      <div style={{ textAlign:'center', fontFamily:'Arial,sans-serif', minWidth:0 }}>
        {winner ? (
          <>
            <div style={{
              fontSize:'14px', fontWeight:800,
              color:'var(--green)',
              letterSpacing:'0.01em',
              overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
            }}>
              {winner.name || winner.team} Win
            </div>
            {margin != null && (
              <div style={{ fontSize:'12px', color:'#aab2bd', marginTop:'3px' }}>
                by {margin} {marginNoun}{margin !== 1 ? 's' : ''}
              </div>
            )}
          </>
        ) : (
          <div style={{ fontSize:'13px', color:'#7a828d' }}>Tie</div>
        )}
      </div>

      {/* Right: FINAL + (MLB) N Innings with small diamond glyph */}
      <div style={{ textAlign:'right', fontFamily:'Arial,sans-serif' }}>
        <div style={{
          fontSize:'11px', color:'#7a828d', fontWeight:700,
          textTransform:'uppercase', letterSpacing:'0.10em',
        }}>
          FINAL
        </div>
        {game.sport === 'MLB' && game.periodNumber && (
          <div style={{
            fontSize:'11px', color:'#aab2bd',
            marginTop:'4px',
            display:'flex', alignItems:'center', gap:'5px',
            justifyContent:'flex-end',
          }}>
            {game.periodNumber} Innings
            <span style={{
              display:'inline-block',
              width:7, height:7,
              border:'1.5px solid #aab2bd',
              transform:'rotate(45deg)',
            }}/>
          </div>
        )}
      </div>
    </div>
  )
}

function FinalTeamRow({ team, sport, won, lost }) {
  return (
    <div style={{
      display:'flex', alignItems:'center', gap:'10px',
      opacity: lost ? 0.6 : 1,
      minWidth:0,
    }}>
      <TeamLogoImg
        team={team.team} abbr={team.abbr} sport={sport}
        color={team.color} alternateColor={team.alternateColor} shortName={team.shortName}
        size={28}
      />
      <div style={{ flex:1, minWidth:0 }}>
        {team.location && (
          <div style={{
            fontSize:'9px', color:'#7a828d', fontWeight:700,
            textTransform:'uppercase', letterSpacing:'0.06em',
            fontFamily:'Arial,sans-serif',
            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
          }}>
            {team.location}
          </div>
        )}
        <div style={{
          fontSize:'13px', fontWeight:800, color:'#f4f6f8',
          textTransform:'uppercase', letterSpacing:'0.02em',
          fontFamily:'Arial,sans-serif',
          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
        }}>
          {team.name || team.team}
        </div>
        {team.record && (
          <div style={{ fontSize:'9px', color:'#7a828d', fontFamily:'Arial,sans-serif' }}>
            {team.record}
          </div>
        )}
      </div>
      <div style={{
        display:'flex', alignItems:'center', gap:'4px',
        fontSize:'22px', fontWeight:800,
        color: won ? 'var(--green)' : '#aab2bd',
        fontFamily:'Arial,sans-serif',
        flexShrink:0,
      }}>
        {team.score != null ? team.score : '—'}
        {won && <span style={{ fontSize:'13px', color:'var(--green)' }}>✓</span>}
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// UPCOMING ROW — 2-panel split matching the upcoming-games mockup:
//   LEFT  → gold start time + live lock-countdown + stacked teams with VS
//   RIGHT → 3-col odds table (SPREAD / MONEYLINE / O/U) with rows aligned
//           by team (away on top, home on bottom). Favorite values colored
//           green; underdog red. Vertical gradient: away color top, home
//           bottom, meeting in a dark middle behind the centerline.
//
// Per build feedback: no background-watermark team logos (just the existing
// colored chip badges via TeamLogoImg). Series info caption omitted from
// this layout — the upcoming-games mockup uses the whole header strip for
// time + lock countdown.
// ═══════════════════════════════════════════════════════════════════════════════

function UpcomingGameRow({ game, awayColor, homeColor, setSelectedGame }) {
  const cardBg = `linear-gradient(180deg, ${hexToRGBA(awayColor, 0.25)}, transparent 50%), linear-gradient(0deg, ${hexToRGBA(homeColor, 0.25)}, transparent 50%), #0d1016`

  return (
    <div onClick={() => setSelectedGame(game)} style={{
      background: cardBg,
      borderRadius: '12px',
      marginBottom: '10px',
      cursor: 'pointer',
      display: 'grid',
      gridTemplateColumns: '1fr 1px 1fr',
      overflow: 'hidden',
      minHeight: '150px',
    }}>
      <UpcomingLeftPanel game={game} />
      <div style={{ background: 'rgba(255,255,255,0.06)' }}/>
      <UpcomingOddsPanel game={game} />
    </div>
  )
}

function UpcomingLeftPanel({ game }) {
  return (
    <div style={{
      padding:'12px 14px',
      display:'flex', flexDirection:'column',
      gap:'10px',
      minWidth:0,
      fontFamily:'Arial,sans-serif',
    }}>
      {/* Time + lock countdown */}
      <div style={{ display:'flex', flexDirection:'column', gap:'2px' }}>
        <div style={{ fontSize:'13px', color:'var(--gold)', fontWeight:700 }}>
          {game.startTime}
        </div>
        {game.startTimeMs && (
          <div style={{
            fontSize:'11px', color:'#7a828d',
            display:'flex', alignItems:'center', gap:'4px',
          }}>
            <span style={{ fontSize:'9px' }}>🔒</span>
            <UpcomingLockCountdown startTimeMs={game.startTimeMs} />
          </div>
        )}
      </div>
      {/* Teams stacked with VS */}
      <UpcomingTeamLine team={game.away} sport={game.sport} />
      <div style={{
        fontSize:'9px', color:'#7a828d', fontWeight:700,
        textTransform:'uppercase', letterSpacing:'0.15em',
        textAlign:'center',
        margin:'-2px 0',
      }}>
        vs
      </div>
      <UpcomingTeamLine team={game.home} sport={game.sport} />
    </div>
  )
}

function UpcomingTeamLine({ team, sport }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:'10px', minWidth:0 }}>
      <TeamLogoImg
        team={team.team} abbr={team.abbr} sport={sport}
        color={team.color} alternateColor={team.alternateColor} shortName={team.shortName}
        size={30}
      />
      <div style={{ flex:1, minWidth:0 }}>
        {team.location && (
          <div style={{
            fontSize:'9px', color:'#aab2bd', fontWeight:700,
            textTransform:'uppercase', letterSpacing:'0.06em',
            fontFamily:'Arial,sans-serif',
            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
          }}>
            {team.location}
          </div>
        )}
        <div style={{
          fontSize:'14px', fontWeight:800, color:'#f4f6f8',
          textTransform:'uppercase', letterSpacing:'0.02em',
          fontFamily:'Arial,sans-serif',
          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
          lineHeight:1.1,
        }}>
          {team.name || team.team}
        </div>
        {team.record && (
          <div style={{
            fontSize:'9px', color:'#7a828d',
            fontFamily:'Arial,sans-serif',
            marginTop:'1px',
          }}>
            {team.record}
          </div>
        )}
      </div>
    </div>
  )
}

function UpcomingOddsPanel({ game }) {
  const awaySpreadStr = spreadLabel(game.spread, 'away')
  const homeSpreadStr = spreadLabel(game.spread, 'home')
  const awayMLStr     = fmtML(game.awayML)
  const homeMLStr     = fmtML(game.homeML)

  // All values white when present; muted gray for missing — no fav/dog
  // color coding per design feedback.
  const valColorFor = (hasValue) => hasValue ? '#f4f6f8' : '#7a828d'

  const labelStyle = {
    fontSize:'10px', color:'#7a828d', fontWeight:700,
    letterSpacing:'0.08em', textAlign:'center',
    fontFamily:'Arial,sans-serif',
  }
  const valStyle = {
    fontSize:'14px', fontWeight:700,
    fontFamily:'Arial,sans-serif',
    display:'flex', alignItems:'center', justifyContent:'center',
  }
  const dividerStyle = { background:'rgba(255,255,255,0.06)' }

  // 5-column × 4-row grid:
  //   columns: value | divider | value | divider | value
  //   rows:    header | away | divider | home
  // Vertical dividers span all 4 rows; horizontal divider spans all 5
  // columns. Value cells flex-center so they sit vertically centered in
  // their (1fr-flexible) row tracks.
  return (
    <div style={{
      padding:'12px 0',
      display:'grid',
      gridTemplateColumns:'1fr 1px 1fr 1px 1fr',
      gridTemplateRows:'auto 1fr 1px 1fr',
      gap:'8px 0',
      alignContent:'stretch',
    }}>
      {/* Vertical dividers — span all four rows. */}
      <div style={{ ...dividerStyle, gridColumn:'2', gridRow:'1 / 5' }}/>
      <div style={{ ...dividerStyle, gridColumn:'4', gridRow:'1 / 5' }}/>
      {/* Horizontal divider — between away and home rows, spans all columns. */}
      <div style={{ ...dividerStyle, gridColumn:'1 / 6', gridRow:'3' }}/>

      {/* Header row */}
      <div style={{ ...labelStyle, gridColumn:'1', gridRow:'1' }}>SPREAD</div>
      <div style={{ ...labelStyle, gridColumn:'3', gridRow:'1' }}>MONEYLINE</div>
      <div style={{ ...labelStyle, gridColumn:'5', gridRow:'1' }}>O/U</div>

      {/* Away row */}
      <div style={{ ...valStyle, gridColumn:'1', gridRow:'2', color: valColorFor(!!awaySpreadStr) }}>
        {awaySpreadStr || '—'}
      </div>
      <div style={{ ...valStyle, gridColumn:'3', gridRow:'2', color: valColorFor(!!awayMLStr) }}>
        {awayMLStr || '—'}
      </div>
      <div style={{ ...valStyle, gridColumn:'5', gridRow:'2', color: valColorFor(game.total != null) }}>
        {game.total ?? '—'}
      </div>

      {/* Home row */}
      <div style={{ ...valStyle, gridColumn:'1', gridRow:'4', color: valColorFor(!!homeSpreadStr) }}>
        {homeSpreadStr || '—'}
      </div>
      <div style={{ ...valStyle, gridColumn:'3', gridRow:'4', color: valColorFor(!!homeMLStr) }}>
        {homeMLStr || '—'}
      </div>
      <div style={{ ...valStyle, gridColumn:'5', gridRow:'4', color: valColorFor(game.total != null) }}>
        {game.total ?? '—'}
      </div>
    </div>
  )
}

// Live-ticking "Locks in Xh Ym" countdown. Ticks every 30s — we don't show
// second precision in this view, and ticking less often keeps the upcoming-
// games list cheap when there are many cards. Mirrors the PickemMode
// Countdown's "10 minutes before commenceTime" lock convention.
function UpcomingLockCountdown({ startTimeMs }) {
  const [text, setText] = useState('')
  useEffect(() => {
    if (!startTimeMs) return
    const update = () => {
      const now = Date.now()
      const lockTime = startTimeMs - 10 * 60 * 1000
      const diff = lockTime - now
      if (diff <= 0) { setText('LOCKED'); return }
      const h = Math.floor(diff / 3600000)
      const m = Math.floor((diff % 3600000) / 60000)
      setText(h > 0 ? `Locks in ${h}h ${m}m` : `Locks in ${m}m`)
    }
    update()
    const id = setInterval(update, 30000)
    return () => clearInterval(id)
  }, [startTimeMs])
  return <span>{text}</span>
}

// 3-base diamond — 2nd at top, 3rd left, 1st right. Each base is a 9px
// square rotated 45deg (renders as a small diamond outline). Filled
// (background = text color) when a runner is on the base; empty (just the
// outline) when not.
function BaseDiamond({ onFirst, onSecond, onThird }) {
  const baseStyle = (filled) => ({
    width: 9, height: 9,
    background: filled ? 'var(--text)' : 'transparent',
    border: '1.5px solid var(--text-dim)',
    transform: 'rotate(45deg)',
    transition: 'background 0.15s',
  })
  return (
    <div style={{ position:'relative', width: 44, height: 28, flexShrink: 0 }}>
      <div style={{ position:'absolute', top:1, left:'50%', transform:'translateX(-50%)' }}>
        <div style={baseStyle(onSecond)} />
      </div>
      <div style={{ position:'absolute', top:'50%', left:4, transform:'translateY(-50%)' }}>
        <div style={baseStyle(onThird)} />
      </div>
      <div style={{ position:'absolute', top:'50%', right:4, transform:'translateY(-50%)' }}>
        <div style={baseStyle(onFirst)} />
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// UFC GAMES VIEW — replaces the team-sport bucket layout (LIVE NOW / UPCOMING
// / RECENT) for the UFC sport pill. Section-by-section build:
//   §1 (this commit): main event hero card
//   §2 (next):        "Later this week" — co-main + main-card grid
//   §3 (after):       "Full UFC card" — compact single-line list
//
// Odds are intentionally absent from every section — getUFCFights bypasses
// the synth floor, so awayML / homeML / total are null for every UFC fight.
// No fabrication, no synth fill. The mockup's odds elements are CUT.
// ═══════════════════════════════════════════════════════════════════════════════

function UFCGamesView({ games }) {
  // getUFCFights sorts by roundPoints desc, so games[0] is the main event
  // (or Title Fight if there is one). If empty, fall through to GamesTab's
  // shared empty state.
  const mainEvent = games[0]
  if (!mainEvent) return null

  // §2 "Later this week" — Co-Main + Main Card fights, excluding whatever's
  // already showing as the hero (main event / title fight). Already sorted
  // by roundPoints desc, so Co-Main (3pt) leads, then Main Card (2pt) fights
  // in their card order. Prelims (1pt) intentionally excluded — they go to
  // §3 "Full UFC card" when that section ships.
  const midTierFights = games.filter(g =>
    g !== mainEvent && (g.roundName === 'Co-Main Event' || g.roundName === 'Main Card')
  )

  return (
    <>
      <GameSectionHeader title="UPCOMING UFC EVENT" />
      <UFCMainEventCard fight={mainEvent} />
      {midTierFights.length > 0 && (
        <>
          <GameSectionHeader title="LATER THIS WEEK" />
          <UFCMidTierGrid fights={midTierFights} />
        </>
      )}
      {/* §3 "Full UFC card" — every fight on the card, including prelims AND
          the main event / mid-tier that already appeared above. Intentional
          repeat per the mockup — this is the complete reference list. */}
      {games.length > 0 && (
        <>
          <GameSectionHeader title="FULL UFC CARD" />
          <UFCFullCardList fights={games} />
        </>
      )}
    </>
  )
}

function UFCMainEventCard({ fight }) {
  const RED  = '#e23b3b'
  const BLUE = '#2f7fd6'

  // First + last derived from the full displayName (fight.away.team), NOT
  // shortName — "B. Muhammad".split() would give a useless "B." for first.
  const awayFirst = ufcFirstName(fight.away.team)
  const awayLast  = ufcLastName(fight.away.team)
  const homeFirst = ufcFirstName(fight.home.team)
  const homeLast  = ufcLastName(fight.home.team)

  // 5-round fights: Title Fight (roundPts=7) and Main Event (5). All others
  // are 3-round (Co-Main, Main Card, Prelim).
  const rounds = fight.roundPts >= 5 ? 5 : 3

  // Card background: dark base with a red corner glow on the left and blue
  // on the right, fading to dark in the middle behind the VS hexagon.
  const cardBg = `radial-gradient(ellipse 60% 80% at 0% 50%, ${hexToRGBA(RED, 0.22)}, transparent 60%), radial-gradient(ellipse 60% 80% at 100% 50%, ${hexToRGBA(BLUE, 0.22)}, transparent 60%), #0d1016`

  return (
    <div style={{
      background: cardBg,
      border: '1px solid rgba(255,255,255,0.06)',
      borderRadius: '14px',
      padding: '14px 14px 18px',
      marginBottom: '12px',
      overflow: 'hidden',
    }}>
      {/* Top row: tier badge + lock countdown */}
      <div style={{
        display:'flex', justifyContent:'space-between', alignItems:'center',
        marginBottom:'14px',
      }}>
        <UFCTierBadge tier={fight.roundName} />
        {fight.startTimeMs && <UFCLockMeta startTimeMs={fight.startTimeMs} />}
      </div>

      {/* Matchup row: red side · VS hexagon · blue side */}
      <div style={{
        display:'grid',
        gridTemplateColumns:'1fr auto 1fr',
        alignItems:'center',
        gap:'4px',
      }}>
        <UFCFighterSide
          imageUrl="/ufc/fighter-red.webp"
          cornerColor={RED}
          firstName={awayFirst}
          lastName={awayLast}
          record={fight.away.record}
          flagUrl={fight.away.flagUrl}
          country={fight.away.country}
          side="left"
        />
        <UFCVSHexagon />
        <UFCFighterSide
          imageUrl="/ufc/fighter-blue.webp"
          cornerColor={BLUE}
          firstName={homeFirst}
          lastName={homeLast}
          record={fight.home.record}
          flagUrl={fight.home.flagUrl}
          country={fight.home.country}
          side="right"
        />
      </div>

      {/* Center bottom: weight class (large) over rounds · tier (small).
          Replaces the mockup's odds block — the field these would have
          held is null for every UFC fight, so we fill the center with the
          next most identifying piece of real data instead. */}
      <div style={{
        textAlign:'center',
        marginTop:'16px',
        fontFamily:'Arial,sans-serif',
      }}>
        {fight.weightClass && (
          <div style={{
            fontSize:'20px', fontWeight:800,
            color:'#f4f6f8',
            textTransform:'uppercase',
            letterSpacing:'0.04em',
          }}>
            {fight.weightClass}
          </div>
        )}
        <div style={{
          fontSize:'11px', color:'#aab2bd',
          marginTop:'4px',
          letterSpacing:'0.10em',
          textTransform:'uppercase',
          fontWeight:700,
        }}>
          {rounds} Rounds{fight.roundName ? ` · ${fight.roundName}` : ''}
        </div>
      </div>
    </div>
  )
}

function UFCTierBadge({ tier }) {
  if (!tier) return null
  return (
    <div style={{
      display:'flex', alignItems:'center', gap:'6px',
      fontSize:'10px', fontWeight:700,
      color:'#e23b3b', letterSpacing:'0.10em',
      textTransform:'uppercase',
      fontFamily:'Arial,sans-serif',
      // Never wrap. "CO-MAIN EVENT" used to break to a second line on the
      // narrow mid-tier card, making the Co-Main card taller than its
      // siblings and breaking the horizontal alignment of the grid.
      whiteSpace:'nowrap',
    }}>
      <span style={{ width:6, height:6, borderRadius:'50%', background:'#e23b3b', flexShrink:0 }}/>
      {tier}
    </div>
  )
}

function UFCLockMeta({ startTimeMs }) {
  // Gold/amber matches PickemMode HeroLockRow exactly (#F5B800) — one
  // visual language for "fight starting soon" across both surfaces.
  // The 🔒 + text both inherit this color (UpcomingLockCountdown is a
  // bare span with no own color).
  return (
    <div style={{
      display:'flex', alignItems:'center', gap:'5px',
      fontSize:'11px', color:'#F5B800', fontWeight: 700,
      fontFamily:'Arial,sans-serif',
    }}>
      <span style={{ fontSize:'10px' }}>🔒</span>
      <UpcomingLockCountdown startTimeMs={startTimeMs} />
    </div>
  )
}

function UFCFighterSide({ imageUrl, cornerColor, firstName, lastName, record, flagUrl, country, side }) {
  // Featured event card — full PickemMode treatment ported via the
  // shared cornerTintBg + FighterPanelLayers helpers. Single visual
  // language across the pickem hero card and this Games-tab card; any
  // future tuning happens in src/lib/ufcFighterPanel.js + src/
  // components/FighterPanelLayers.jsx and flows to both surfaces.
  // The translateX(±15/±26%) centering inside FighterPanelLayers is
  // card-width-invariant (figure-offset percentages scale with
  // backgroundSize:120% regardless of W), so it ports unchanged
  // despite this card's wider aspect than the pickem hero.
  return (
    <div style={{
      position:'relative',
      borderRadius:'12px',
      overflow:'hidden',
      minHeight:'220px',
      background: ufcCornerTintBg(cornerColor),
      border:`1px solid ${cornerColor}`,
      display:'flex', flexDirection:'column',
      justifyContent:'flex-end',
    }}>
      <FighterPanelLayers
        cornerColor={cornerColor}
        silhouetteUrl={imageUrl}
        flagUrl={flagUrl}
        country={country}
        side={side}
      />

      <div style={{
        position:'relative', zIndex:4,
        textAlign:'center',
        padding:'0 8px 14px',
        fontFamily:'Arial,sans-serif',
      }}>
        {firstName && (
          <div style={{
            fontSize:'11px', fontWeight:500,
            color:'#aab2bd',
            letterSpacing:'0.02em',
            lineHeight:1,
            marginBottom:'2px',
          }}>
            {firstName}
          </div>
        )}
        <div style={{
          fontSize:'20px', fontWeight:800,
          color:'#f4f6f8',
          textTransform:'uppercase',
          letterSpacing:'0.02em',
          lineHeight:1.1,
          wordBreak:'break-word',
        }}>
          {lastName}
        </div>
        <div style={{
          width:'32px', height:'2px', borderRadius:'1px',
          background:cornerColor,
          margin:'6px auto 0',
        }}/>
        {record && (
          <div style={{
            fontSize:'12px', color:'#aab2bd',
            marginTop:'6px',
          }}>
            {record}
          </div>
        )}
      </div>
    </div>
  )
}

function UFCVSHexagon() {
  return (
    <div style={{
      width: 44, height: 44,
      clipPath: 'polygon(25% 5%, 75% 5%, 100% 50%, 75% 95%, 25% 95%, 0% 50%)',
      background: '#0d1016',
      display:'flex', alignItems:'center', justifyContent:'center',
      fontSize:'12px', fontWeight:800, letterSpacing:'0.05em',
      color:'#aab2bd',
      fontFamily:'Arial,sans-serif',
      flexShrink: 0,
    }}>
      VS
    </div>
  )
}

// §2 "LATER THIS WEEK" — horizontally scrollable grid of compact cards for
// the Co-Main + Main Card tier fights. Each card is a smaller version of
// the hero's split-silhouette treatment, with a date pill instead of a live
// countdown (these fights are days out, not hours). NO odds — same reason
// as §1: getUFCFights bypasses the synth floor for UFC.
function UFCMidTierGrid({ fights }) {
  return (
    <div style={{
      display:'flex',
      gap:'10px',
      overflowX:'auto',
      paddingBottom:'10px',
      marginBottom:'6px',
      WebkitOverflowScrolling:'touch',
      scrollbarWidth:'thin',
      // `safe center` centers the cards when they fit and falls back to
      // start-alignment when they overflow (so the leftmost cards never
      // get cut off behind the center justification on scroll).
      justifyContent:'safe center',
    }}>
      {fights.map(f => <UFCMidTierCard key={f.id} fight={f} />)}
    </div>
  )
}

function UFCMidTierCard({ fight }) {
  const RED  = '#e23b3b'
  const BLUE = '#2f7fd6'

  const awayFirst = ufcFirstName(fight.away.team)
  const awayLast  = ufcLastName(fight.away.team)
  const homeFirst = ufcFirstName(fight.home.team)
  const homeLast  = ufcLastName(fight.home.team)
  const rounds = fight.roundPts >= 5 ? 5 : 3

  // Lower-alpha gradient than the hero — mid-tier should read as secondary.
  const cardBg = `radial-gradient(ellipse 60% 70% at 0% 50%, ${hexToRGBA(RED, 0.18)}, transparent 60%), radial-gradient(ellipse 60% 70% at 100% 50%, ${hexToRGBA(BLUE, 0.18)}, transparent 60%), #0d1016`

  return (
    <div style={{
      background: cardBg,
      border: '1px solid rgba(255,255,255,0.06)',
      borderRadius: '12px',
      padding: '10px 10px 12px',
      width: '210px',
      minWidth: '210px',
      flexShrink: 0,
      overflow: 'hidden',
    }}>
      {/* Top row: tier badge (left) + date pill (right) — mirrors the hero's
          tier-left / lock-right shape, just with a date instead of a live
          countdown since these fights are days out. */}
      <div style={{
        display:'flex', justifyContent:'space-between', alignItems:'center',
        marginBottom:'10px',
        gap:'6px',
      }}>
        <UFCTierBadge tier={fight.roundName} />
        <UFCDatePill startTimeMs={fight.startTimeMs} />
      </div>

      {/* Matchup: mini red side · mini VS · mini blue side */}
      <div style={{
        display:'grid',
        gridTemplateColumns:'1fr auto 1fr',
        alignItems:'center',
        gap:'2px',
      }}>
        <UFCMiniFighterSide
          imageUrl="/ufc/fighter-red.webp"
          cornerColor={RED}
          firstName={awayFirst}
          lastName={awayLast}
          record={fight.away.record}
          country={fight.away.country}
          flagUrl={fight.away.flagUrl}
          side="left"
        />
        <UFCMiniVSHexagon />
        <UFCMiniFighterSide
          imageUrl="/ufc/fighter-blue.webp"
          cornerColor={BLUE}
          firstName={homeFirst}
          lastName={homeLast}
          record={fight.home.record}
          country={fight.home.country}
          flagUrl={fight.home.flagUrl}
          side="right"
        />
      </div>

      {/* Bottom meta: weight class · rounds. Mirrors the hero's center
          treatment in compact form. */}
      <div style={{
        textAlign:'center',
        marginTop:'10px',
        fontFamily:'Arial,sans-serif',
        fontSize:'10px',
        color:'#aab2bd',
        fontWeight:700,
        textTransform:'uppercase',
        letterSpacing:'0.08em',
        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
      }}>
        {[fight.weightClass, `${rounds} Rounds`].filter(Boolean).join(' · ')}
      </div>
    </div>
  )
}

function UFCMiniFighterSide({ imageUrl, cornerColor, firstName, lastName, record, country, flagUrl, side }) {
  // "Later this week" small card — same shared treatment as the
  // featured card, INCLUDING the flag layer. The previous decision to
  // drop the flag at this size (per "flags at small card sizes can
  // turn to noise") was made when the flag layer rendered un-blurred.
  // With blur(6px) + scale(1.08) on the flag (FighterPanelLayers
  // z:0), the flag reads as a soft country-color field rather than
  // fine bands/stars — the noise concern is resolved by the blur,
  // not by omitting the layer. Mini and featured cards now match.
  return (
    <div style={{
      position:'relative',
      borderRadius:'8px',
      overflow:'hidden',
      minHeight:'120px',
      background: ufcCornerTintBg(cornerColor),
      border:`1px solid ${cornerColor}`,
      display:'flex', flexDirection:'column',
      justifyContent:'flex-end',
    }}>
      <FighterPanelLayers
        cornerColor={cornerColor}
        silhouetteUrl={imageUrl}
        flagUrl={flagUrl}
        country={country}
        side={side}
        flagBlurPx={2}
        glowOpacity={0.45}
      />
      <div style={{
        position:'relative', zIndex:4,
        textAlign:'center',
        padding:'0 4px 8px',
        fontFamily:'Arial,sans-serif',
      }}>
        {firstName && (
          <div style={{
            fontSize:'9px', fontWeight:500,
            color:'#aab2bd',
            letterSpacing:'0.02em',
            lineHeight:1,
            marginBottom:'1px',
            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
          }}>
            {firstName}
          </div>
        )}
        <div style={{
          fontSize:'12px', fontWeight:800,
          color:'#f4f6f8',
          textTransform:'uppercase',
          letterSpacing:'0.02em',
          lineHeight:1.05,
          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
        }}>
          {lastName}
        </div>
        {record && (
          <div style={{
            fontSize:'9px', color:'#aab2bd',
            marginTop:'2px',
          }}>
            {record}
          </div>
        )}
      </div>
    </div>
  )
}

function UFCMiniVSHexagon() {
  return (
    <div style={{
      width: 24, height: 24,
      clipPath: 'polygon(25% 5%, 75% 5%, 100% 50%, 75% 95%, 25% 95%, 0% 50%)',
      background: '#0d1016',
      display:'flex', alignItems:'center', justifyContent:'center',
      fontSize:'8px', fontWeight:800, letterSpacing:'0.05em',
      color:'#aab2bd',
      fontFamily:'Arial,sans-serif',
      flexShrink: 0,
    }}>
      VS
    </div>
  )
}

// Date pill — "FRI 10:00 PM" — short weekday + time, gold text matching
// the existing upcoming-row time treatment. Timezone dropped: with the
// tier badge in the same top row of a 210px mid-tier card, "FRI 10:00 PM
// EDT" pushed the row over the card width and forced "CO-MAIN EVENT" to
// wrap, making the Co-Main card visibly taller than its siblings. Users
// know their local zone; the trailing 4 chars weren't pulling weight.
function UFCDatePill({ startTimeMs }) {
  if (!startTimeMs) return null
  const d = new Date(startTimeMs)
  const day  = d.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  return (
    <div style={{
      fontSize:'9px', color:'var(--gold)',
      fontWeight:700, letterSpacing:'0.05em',
      fontFamily:'Arial,sans-serif',
      whiteSpace:'nowrap',
    }}>
      {day} {time}
    </div>
  )
}

// §3 "FULL UFC CARD" — single-line compact rows for every fight on the card,
// prelims included. The main event + mid-tier fights repeat here intentionally
// per the mockup; this is the complete reference list. NO odds columns —
// getUFCFights bypasses the synth floor; cutting them is the honest display.
function UFCFullCardList({ fights }) {
  return (
    <div style={{
      border:'1px solid rgba(255,255,255,0.06)',
      borderRadius:'10px',
      overflow:'hidden',
      marginBottom:'12px',
      background:'#0d1016',
    }}>
      {fights.map((f, i) => (
        <UFCFullCardRow key={f.id} fight={f} isLast={i === fights.length - 1} />
      ))}
    </div>
  )
}

function UFCFullCardRow({ fight, isLast }) {
  const awayFirst = ufcFirstName(fight.away.team)
  const awayLast  = ufcLastName(fight.away.team)
  const homeFirst = ufcFirstName(fight.home.team)
  const homeLast  = ufcLastName(fight.home.team)

  // Time only — every fight on a single UFC card happens on the same day,
  // so the time (which varies between prelims and main card) is the useful
  // disambiguator. Format: "10:00 PM".
  const timeStr = fight.startTimeMs
    ? new Date(fight.startTimeMs).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : ''

  // 5-round fights: Title Fight (roundPts=7) and Main Event (5). All others
  // are 3-round (Co-Main, Main Card, Prelim).
  const rounds = fight.roundPts >= 5 ? 5 : 3

  // Tier color for quick visual scanning. Title / Main Event in red, Co-Main
  // in green, all others muted gray — lets users skim down the list and see
  // fight importance without reading the tier label closely.
  const tier = fight.roundName || ''
  const tierColor =
    tier === 'Title Fight' || tier === 'Main Event' ? '#e23b3b' :
    tier === 'Co-Main Event' ? 'var(--green)' :
    '#7a828d'

  return (
    <div style={{
      display:'flex', alignItems:'center', gap:'12px',
      padding:'14px 12px',
      borderBottom: isLast ? 'none' : '1px solid rgba(255,255,255,0.06)',
      fontFamily:'Arial,sans-serif',
      minWidth:0,
    }}>
      {/* Time + lock — left column, vertically centered against the 2-line
          right column. */}
      <div style={{
        display:'flex', alignItems:'center', gap:'4px',
        fontSize:'10px', color:'var(--gold)', fontWeight:700,
        letterSpacing:'0.04em',
        minWidth:'58px',
        flexShrink:0,
      }}>
        <span style={{ fontSize:'8px' }}>🔒</span>
        {timeStr}
      </div>

      {/* Right side: matchup (line 1) over metadata (line 2). Metadata moved
          off line 1 so the names get the full width of the row to render
          without truncation. */}
      <div style={{
        flex:1, minWidth:0,
        display:'flex', flexDirection:'column',
        gap:'6px',
      }}>
        {/* Line 1: matchup — [chip] first/last · vs · [chip] first/last.
            Each fighter is a flex:1 block so they share width equally;
            the inner text block uses minWidth:0 so ellipsis can trigger
            cleanly when a name is too long for the available space. */}
        <div style={{
          display:'flex', alignItems:'center',
          gap:'8px', minWidth:0,
        }}>
          <UFCFullCardFighter
            team={fight.away}
            firstName={awayFirst}
            lastName={awayLast}
          />
          <span style={{
            fontSize:'9px', color:'#7a828d', fontWeight:700,
            letterSpacing:'0.10em', flexShrink:0,
          }}>
            VS
          </span>
          <UFCFullCardFighter
            team={fight.home}
            firstName={homeFirst}
            lastName={homeLast}
          />
        </div>

        {/* Line 2: metadata — weight class · N rounds · tier (color-coded). */}
        <div style={{
          display:'flex', alignItems:'center',
          gap:'6px',
          fontSize:'9px',
          fontWeight:700,
          letterSpacing:'0.06em',
          textTransform:'uppercase',
          fontFamily:'Arial,sans-serif',
        }}>
          {fight.weightClass && (
            <>
              <span style={{ color:'#aab2bd' }}>{fight.weightClass}</span>
              <span style={{ color:'#444' }}>·</span>
            </>
          )}
          <span style={{ color:'#aab2bd' }}>{rounds} Rounds</span>
          {tier && (
            <>
              <span style={{ color:'#444' }}>·</span>
              <span style={{ color: tierColor }}>{tier}</span>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// Single fighter block for the §3 full-card row: chip + first-name (small,
// muted) over last-name (big, bold). Mirrors the hero / mid-tier card
// treatment — first name above, last name below — but inline-flex with the
// chip on the left.
function UFCFullCardFighter({ team, firstName, lastName }) {
  return (
    <div style={{
      display:'flex', alignItems:'center',
      gap:'6px',
      flex:1, minWidth:0,
    }}>
      <TeamLogoImg
        team={team.team} abbr={team.abbr} sport="UFC"
        color={null} alternateColor={null} shortName={team.shortName}
        size={24}
      />
      <div style={{ minWidth:0, flex:1 }}>
        {firstName && (
          <div style={{
            fontSize:'9px', color:'#aab2bd', fontWeight:500,
            letterSpacing:'0.02em',
            lineHeight:1,
            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
          }}>
            {firstName}
          </div>
        )}
        <div style={{
          fontSize:'13px', fontWeight:800, color:'#f4f6f8',
          textTransform:'uppercase', letterSpacing:'0.02em',
          lineHeight:1.1,
          marginTop: firstName ? '1px' : 0,
          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
        }}>
          {lastName}
        </div>
      </div>
    </div>
  )
}

// Last-name extraction for fighter labels. Handles "B. Muhammad" → "Muhammad"
// (preferred shortName form) and "Belal Muhammad" → "Muhammad" (fallback when
// shortName is missing). Empty input → empty string (the badge then renders
// blank rather than throwing).
function ufcLastName(fullName) {
  if (!fullName) return ''
  return fullName.split(/\s+/).filter(Boolean).pop() || ''
}

// First-name extraction — everything BEFORE the last token. Multi-token first
// names join with a single space ("Marc-André" stays as one token; "Jose Luis"
// → "Jose Luis"). Single-token names return empty so the renderer can skip
// the first-name line entirely (no blank slot). MUST be derived from the
// full displayName ("Belal Muhammad") — calling this on shortName ("B.
// Muhammad") would return a useless "B.".
function ufcFirstName(fullName) {
  if (!fullName) return ''
  const parts = fullName.split(/\s+/).filter(Boolean)
  if (parts.length <= 1) return ''
  return parts.slice(0, -1).join(' ')
}

function GamesTab({ games, loadingGames, sport, setSport, user, setTab, setSelectedGame, fetchGames }) {
  const { pull, refreshing } = usePullToRefresh(async () => {
    await fetchGames(true)
  })
  // Section windows: today (00:00 → 24:00 local) and last 24h.
  const now           = Date.now()
  const startOfDay    = new Date(); startOfDay.setHours(0, 0, 0, 0)
  const startOfDayMs  = startOfDay.getTime()
  const endOfDayMs    = startOfDayMs + 86_400_000
  const dayAgoMs      = now - 86_400_000

  const live          = games.filter(g => g.status === 'live')
  const upcomingToday = games.filter(g =>
    g.status === 'upcoming' && g.startTimeMs >= startOfDayMs && g.startTimeMs < endOfDayMs
  )
  const upcomingLater = games.filter(g =>
    g.status === 'upcoming' && g.startTimeMs >= endOfDayMs
  )
  const recent        = games.filter(g =>
    g.status === 'final' && (g.startTimeMs ?? 0) >= dayAgoMs
  )

  return (
    <div className="page">
      <PullIndicator pull={pull} refreshing={refreshing} />
      <div className="page-header">
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <h1 className="page-title">Games & Scores</h1>
          <button onClick={() => setTab('profile')}
            style={{ background:'transparent', border:'none', padding:0, cursor:'pointer', width:36, height:36, borderRadius:'50%', overflow:'hidden' }}
            title="Profile"
          >
            {user?.avatar_url ? (
              <img
                src={user.avatar_url}
                alt=""
                style={{ width:36, height:36, borderRadius:'50%', objectFit:'cover', border:'2px solid var(--gold)', display:'block' }}
                onError={e => { e.currentTarget.style.display = 'none' }}
              />
            ) : (
              <div style={{
                width:36, height:36, borderRadius:'50%',
                background:'var(--gold-bg)', border:'2px solid var(--gold)',
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:16, fontWeight:800, color:'var(--gold)',
              }}>
                {(user?.username || user?.email || 'U')[0]?.toUpperCase()}
              </div>
            )}
          </button>
        </div>
        <div className="filter-row">
          {SPORTS.map(s => <button key={s} className={`pill ${sport===s?'active':''}`} onClick={() => setSport(s)}>{s}</button>)}
        </div>
      </div>

      <div className="page-body">
        {loadingGames && (
          <div>
            <SectionLabel text="LOADING GAMES…" />
            <SkeletonCard /><SkeletonCard /><SkeletonCard />
          </div>
        )}

        {!loadingGames && games.length === 0 && (
          <div style={{ textAlign:'center', padding:'48px 0', color:'var(--text-dim)' }}>
            <div style={{ fontSize:'36px', marginBottom:'12px' }}>📅</div>
            <div style={{ fontSize:'16px', fontWeight:'bold', marginBottom:'6px' }}>
              {sport === 'UFC' ? 'No UFC event in the next 30 days' : `No ${sport} games today`}
            </div>
            <div style={{ fontSize:'13px' }}>
              {sport === 'UFC' ? 'Check back closer to the next card' :
               sport === 'NFL' ? 'NFL season begins September 5, 2026' : `Check back for tomorrow's slate`}
            </div>
          </div>
        )}

        {/* UFC has its own dedicated view — bucket sections (LIVE NOW / UPCOMING
            TODAY / LATER THIS WEEK / FINAL) don't fit the UFC event structure
            and are gated to non-UFC sports below. */}
        {!loadingGames && games.length > 0 && sport === 'UFC' && (
          <UFCGamesView games={games} />
        )}

        {!loadingGames && games.length > 0 && sport !== 'UFC' && (
          <>
            {live.length > 0 && <>
              <GameSectionHeader title="LIVE NOW" showLiveDot />
              {live.map(g => <GameRow key={g.id} game={g} setSelectedGame={setSelectedGame} />)}
            </>}

            {upcomingToday.length > 0 && <>
              <SectionLabel text="UPCOMING TODAY" />
              {upcomingToday.map(g => <GameRow key={g.id} game={g} setSelectedGame={setSelectedGame} />)}
            </>}

            {upcomingLater.length > 0 && <>
              <SectionLabel text="LATER THIS WEEK" />
              {upcomingLater.map(g => <GameRow key={g.id} game={g} setSelectedGame={setSelectedGame} />)}
            </>}

            {recent.length > 0 && <>
              <GameSectionHeader title="FINAL" />
              {recent.map(g => <GameRow key={g.id} game={g} setSelectedGame={setSelectedGame} />)}
            </>}
          </>
        )}

        {!loadingGames && games.length > 0 && (
          <div style={{ textAlign:'center', marginTop:'20px', fontSize:'11px', color:'var(--text-muted)' }}>
            Live scores update every 30s · Go to Groups to compete
          </div>
        )}
      </div>
    </div>
  )
}

// Logged-out variant of the Profile tab — shown when ProfileTab receives
// no user. Mirrors Auth.jsx's welcome layout (brand lockup + tagline +
// CTA) but constrained to the tab area rather than full-screen, since
// the bottom nav stays visible. The CTA pops the same Auth surface the
// wall uses, via the showAuthPrompt overlay threaded from App.jsx.
function LoggedOutProfileView({ onLogIn }) {
  return (
    <div className="page">
      <div className="page-body" style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'60vh', padding:'24px 20px', textAlign:'center' }}>
        <BrandMark variant="lockup" style={{ width:'100%', maxWidth:'260px', height:'auto', marginBottom:'20px' }} />
        <div style={{ color:'var(--text-dim)', fontSize:'14px', lineHeight:'1.5', maxWidth:'320px', marginBottom:'28px' }}>
          Sign in to create pools, make picks, and track your standings.
        </div>
        <button className="btn btn-gold" style={{ maxWidth:'240px' }} onClick={onLogIn}>
          Log In / Sign Up
        </button>
      </div>
    </div>
  )
}

function ProfileTab({
  user, setUser, userStats, groups, recentPicks, uploadingAvatar,
  profileFileInputRef, setSelGroup, setTab, setGroupTab, handleSignOut, openGroup,
  followingCount, followersCount, onOpenFollowing, onOpenFollowers, onOpenDiscover,
  onLogIn, onUserUnblocked,
}) {
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  // Blocked users list (migration 053). Lazily fetched — only queried
  // when the user expands the section, so the profile initial render
  // stays fast and non-users pay nothing.
  const [blockedList, setBlockedList]         = useState(null)  // null=not-loaded, []=loaded-empty
  const [blockedLoading, setBlockedLoading]   = useState(false)
  const [blockedExpanded, setBlockedExpanded] = useState(false)
  async function loadBlocked() {
    if (!user?.id) return
    setBlockedLoading(true)
    const { data } = await getBlockedUsers(user.id)
    setBlockedList(data || [])
    setBlockedLoading(false)
  }
  async function handleUnblock(blockedId) {
    if (!user?.id) return
    const { error: uErr } = await unblockUser(user.id, blockedId)
    if (uErr) return
    setBlockedList(prev => (prev || []).filter(b => b.blocked_user_id !== blockedId))
    onUserUnblocked?.(blockedId)
  }

  // Logged-out branch — placed AFTER useState to preserve hook-call
  // order across renders (rules of hooks). Currently unreachable because
  // the wall at App.jsx:7921 intercepts before ProfileTab ever mounts
  // with a null user; lives here for Commit 2 when the wall relocates.
  if (!user) return <LoggedOutProfileView onLogIn={onLogIn} />

  const totalPicks   = userStats?.totalPicks    ?? 0
  const correct      = userStats?.correct       ?? 0
  const groupWinPct  = userStats?.groupWinPct   ?? 0
  const groupsWon    = userStats?.groupsWon     ?? 0
  const groupsEnt    = userStats?.groupsEntered ?? 0

  // Active vs historical groups (same rule as GroupsTab).
  const active     = groups.filter(g => !isHistoricalGroup(g))
  const historical = groups.filter(g =>  isHistoricalGroup(g))

  // "Member since" — comes from the profiles row via getUserStats.
  const memberSinceRaw = userStats?.memberSince || user.created_at
  const memberSince = memberSinceRaw
    ? new Date(memberSinceRaw).toLocaleDateString('en-US', { month:'long', year:'numeric' })
    : null

  return (
    <div className="page">
      <div className="page-header">
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <h1 className="page-title">Profile</h1>
          <button onClick={handleSignOut}
            style={{ background:'rgba(255,255,255,0.05)', border:'1px solid var(--border)', color:'var(--text-dim)', padding:'6px 12px', borderRadius:'8px', cursor:'pointer', fontSize:'12px' }}>
            Sign Out
          </button>
        </div>
      </div>

      <div className="page-body">
        {/* Identity card */}
        <div style={{ textAlign:'center', marginBottom:'18px' }}>
          <div style={{ position:'relative', display:'inline-block', margin:'8px auto 12px' }}>
            {user?.avatar_url ? (
              <img
                src={user.avatar_url}
                style={{
                  width:88, height:88, borderRadius:'50%',
                  objectFit:'cover',
                  border:'2px solid var(--gold)', display:'block',
                }}
                onError={e => { e.currentTarget.style.display = 'none' }}
              />
            ) : (
              <div style={{
                width:'88px', height:'88px', borderRadius:'50%',
                background:'var(--gold-bg)', border:'2px solid var(--gold)',
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:'36px', color:'var(--gold)', fontWeight:'bold',
              }}>
                {(user.username || user.email || 'U')[0]?.toUpperCase()}
              </div>
            )}
            <button
              type="button"
              onClick={() => profileFileInputRef.current?.click()}
              disabled={uploadingAvatar}
              title="Change profile picture"
              style={{
                position:'absolute', bottom:0, right:0,
                width:28, height:28, borderRadius:'50%',
                background:'var(--gold)', border:'2px solid #0d0d0d',
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:13, cursor: uploadingAvatar ? 'wait' : 'pointer',
                padding:0, lineHeight:1,
              }}
            >
              {uploadingAvatar ? '⏳' : '📷'}
            </button>
          </div>
          <div style={{ fontSize:'24px', fontWeight:'bold', marginBottom:'2px' }}>{user.username}</div>
          <div style={{ fontSize:'13px', color:'var(--text-dim)' }}>{user.email}</div>
          {memberSince && (
            <div style={{ fontSize:'11px', color:'var(--text-muted)', fontFamily:'Arial,sans-serif', marginTop:'4px' }}>
              Member since {memberSince}
            </div>
          )}
        </div>

        {/* Social: following/followers counts + discovery entry. Slot is
            identity-adjacent ("who you are" → "who you know" → "what you did"). */}
        <SectionLabel text="SOCIAL" />
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'8px' }}>
          <div onClick={onOpenFollowing}
            style={{ background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:10, padding:'12px 8px', textAlign:'center', cursor:'pointer' }}>
            <div style={{ fontSize:'20px', fontWeight:'bold', fontFamily:'Arial,sans-serif' }}>{followingCount}</div>
            <div style={{ fontSize:'11px', color:'var(--text-dim)', marginTop:'2px' }}>Following</div>
          </div>
          <div onClick={onOpenFollowers}
            style={{ background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:10, padding:'12px 8px', textAlign:'center', cursor:'pointer' }}>
            <div style={{ fontSize:'20px', fontWeight:'bold', fontFamily:'Arial,sans-serif' }}>{followersCount}</div>
            <div style={{ fontSize:'11px', color:'var(--text-dim)', marginTop:'2px' }}>Followers</div>
          </div>
        </div>
        <button onClick={onOpenDiscover} className="btn btn-ghost btn-sm" style={{ width:'100%', marginBottom:'16px' }}>
          Find people →
        </button>

        {/* Pick stats row */}
        <SectionLabel text="PICK RECORD" />
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'8px', marginBottom:'16px' }}>
          {[
            { label:'Total picks', val:totalPicks,           color:'var(--text)'  },
            { label:'Correct',     val:correct,              color:'var(--green)' },
            { label:'Groups won %', val:`${groupWinPct}%`,   color:'var(--gold)'  },
          ].map(s => (
            <div key={s.label} style={{ background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:'10px', padding:'12px 8px', textAlign:'center' }}>
              <div style={{ fontSize:'20px', fontWeight:'bold', color:s.color, fontFamily:'Arial,sans-serif' }}>{s.val}</div>
              <div style={{ fontSize:'11px', color:'var(--text-dim)', marginTop:'2px' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Groups summary + jump button */}
        <SectionLabel text="GROUPS" />
        <div style={{ background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'14px', marginBottom:'16px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
            <div>
              <div style={{ fontSize:'13px', color:'var(--text)', fontWeight:'bold' }}>
                {active.length} active · {historical.length} historical
              </div>
              <div style={{ fontSize:'11px', color:'var(--text-dim)', marginTop:'2px', fontFamily:'Arial,sans-serif' }}>
                Won {groupsWon} of {groupsEnt} all-time
              </div>
            </div>
            <button className="btn btn-gold btn-sm" style={{ width:'auto' }} onClick={() => { setSelGroup(null); setTab('groups') }}>
              View All
            </button>
          </div>
        </div>

        {/* Recent Activity feed */}
        <SectionLabel text="RECENT ACTIVITY" />
        {recentPicks.length === 0 ? (
          <div style={{ background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:'10px', padding:'16px', textAlign:'center', fontSize:'12px', color:'var(--text-dim)', marginBottom:'16px' }}>
            No resolved picks yet — they'll show up here as your games go final.
          </div>
        ) : (
          <div style={{ marginBottom:'16px' }}>
            {recentPicks.map(p => {
              const ok = p.result === 'correct'
              const when = p.created_at
                ? new Date(p.created_at).toLocaleDateString('en-US', { month:'short', day:'numeric' })
                : ''
              const label = p.picked_team_name || p.picked_team
              return (
                <div key={p.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 12px', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:'10px', marginBottom:'6px' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'10px', minWidth:0 }}>
                    <span style={{ fontSize:'18px', flexShrink:0 }}>{ok ? '✅' : '❌'}</span>
                    <div style={{ minWidth:0 }}>
                      <div style={{ fontSize:'13px', color:'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        Picked <strong>{label}</strong>
                      </div>
                      <div style={{ fontSize:'11px', color:'var(--text-dim)', fontFamily:'Arial,sans-serif' }}>{when}</div>
                    </div>
                  </div>
                  <div style={{ fontSize:'13px', fontWeight:'bold', color: ok ? 'var(--green)' : 'var(--text-muted)', fontFamily:'Arial,sans-serif', flexShrink:0 }}>
                    {ok ? `+${p.points_earned ?? 0}pt` : '—'}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* My History — quick preview, links to Groups tab History section */}
        {historical.length > 0 && (
          <>
            <SectionLabel text="MY HISTORY" />
            <div style={{ marginBottom:'24px' }}>
              {historical.slice(0, 3).map(g => (
                <div key={g.id} onClick={() => openGroup(g)}
                  style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 12px', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:'10px', marginBottom:'6px', cursor:'pointer', opacity:0.75 }}>
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontSize:'13px', fontWeight:'bold', color:'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{g.name}</div>
                    <div style={{ fontSize:'11px', color:'var(--text-dim)', fontFamily:'Arial,sans-serif' }}>{g.sport} · {CONTEST_LABEL[g.contest_type || 'pick_em']}</div>
                  </div>
                  <span style={{ fontSize:'10px', background:'rgba(255,255,255,0.06)', color:'var(--text-muted)', padding:'2px 7px', borderRadius:'4px', letterSpacing:'0.5px', flexShrink:0 }}>
                    FINAL
                  </span>
                </div>
              ))}
              {historical.length > 3 && (
                <button onClick={() => { setSelGroup(null); setTab('groups') }}
                  style={{ width:'100%', background:'none', border:'1px dashed var(--border)', color:'var(--text-dim)', padding:'10px', borderRadius:'10px', cursor:'pointer', fontSize:'12px' }}>
                  See all {historical.length} past groups →
                </button>
              )}
            </div>
          </>
        )}

        {/* Legal + support footer — small, unobtrusive, four links.
            Discoverable from any logged-in session via the Profile tab;
            app-store review will look for the same surface. Opens each
            page in a new tab matching the existing /terms link pattern
            used in Auth.jsx + the agreement checkboxes. */}
        <div style={{
          marginTop: '32px',
          paddingTop: '20px',
          borderTop: '1px solid var(--border)',
          textAlign: 'center',
          fontSize: '11px',
          color: 'var(--text-muted)',
          fontFamily: 'Arial, sans-serif',
          letterSpacing: '0.3px',
        }}>
          <a href="/terms.html"   target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-dim)', textDecoration: 'none', marginRight: '10px' }}>Terms</a>
          ·
          <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-dim)', textDecoration: 'none', margin: '0 10px' }}>Privacy</a>
          ·
          <a href="/cookies.html" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-dim)', textDecoration: 'none', margin: '0 10px' }}>Cookies</a>
          ·
          <a href="/contact.html" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-dim)', textDecoration: 'none', marginLeft: '10px' }}>Contact</a>
        </div>

        {/* Blocked users — migration 053. Expand-on-tap so the profile
            initial render doesn't pay a query for users who never block
            anyone (the common case). */}
        <div style={{ marginTop:'20px' }}>
          <button
            type="button"
            onClick={() => {
              const next = !blockedExpanded
              setBlockedExpanded(next)
              if (next && blockedList === null) loadBlocked()
            }}
            style={{
              width:'100%', textAlign:'left', background:'transparent',
              border:'none', color:'var(--text-dim)', cursor:'pointer',
              padding:'8px 0', fontSize:'13px', fontFamily:'inherit',
            }}
          >
            {blockedExpanded ? '▾' : '▸'} Blocked users
          </button>
          {blockedExpanded && (
            <div style={{ padding:'8px 0' }}>
              {blockedLoading && (
                <div style={{ fontSize:'12px', color:'var(--text-dim)' }}>Loading…</div>
              )}
              {!blockedLoading && blockedList && blockedList.length === 0 && (
                <div style={{ fontSize:'12px', color:'var(--text-dim)' }}>
                  You haven't blocked anyone.
                </div>
              )}
              {!blockedLoading && blockedList && blockedList.length > 0 && (
                <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
                  {blockedList.map(b => (
                    <div key={b.blocked_user_id} style={{
                      display:'flex', alignItems:'center', justifyContent:'space-between',
                      padding:'6px 8px', background:'var(--color-card-2)',
                      border:'1px solid var(--color-border)', borderRadius:'6px',
                    }}>
                      <div style={{ fontSize:'13px', color:'var(--text)' }}>
                        {b.blocked_username || b.blocked_user_id.slice(0, 8)}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleUnblock(b.blocked_user_id)}
                        className="btn btn-ghost btn-sm"
                        style={{ width:'auto' }}
                      >
                        Unblock
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Account deletion — destructive, sits below the legal footer.
            App Store 5.1.1(v) + Google Play data-deletion both require an
            in-app deletion surface for any account-based app. Server side
            is DELETE /api/account (server/index.js). */}
        <div style={{ marginTop:'20px', textAlign:'center' }}>
          <button
            className="btn btn-danger btn-sm"
            type="button"
            onClick={() => setShowDeleteModal(true)}
            style={{ width:'auto', padding:'8px 18px' }}
          >
            Delete Account
          </button>
        </div>

        {showDeleteModal && (
          <DeleteAccountModal
            onClose={() => setShowDeleteModal(false)}
            onDeleted={async () => {
              // Close first so the unmount-during-signout window has no
              // visible modal state. handleSignOut() clears `user` →
              // App-level branch routes back to Auth automatically.
              setShowDeleteModal(false)
              await handleSignOut()
            }}
          />
        )}
      </div>
    </div>
  )
}

// Small reusable avatar — 36px image with initial-letter fallback. Used by
// the follow lists and OtherProfileView header. Border color is configurable
// for "is me" highlighting (matches lb-avatar's gold-bordered style).
function Avatar({ user, size = 36, gold = false }) {
  const border = gold ? '2px solid var(--gold)' : '1px solid var(--border)'
  if (user?.avatar_url) {
    return (
      <img src={user.avatar_url} alt=""
        style={{ width:size, height:size, borderRadius:'50%', objectFit:'cover', border, display:'block', flexShrink:0 }}
        onError={e => { e.currentTarget.style.display = 'none' }}
      />
    )
  }
  return (
    <div style={{ width:size, height:size, borderRadius:'50%', background:'var(--gold-bg)', border, color:'var(--gold)', fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', fontSize:Math.round(size*0.42), flexShrink:0 }}>
      {(user?.username || '?')[0]?.toUpperCase()}
    </div>
  )
}

// Social sheet — single modal that hosts three list views (following /
// followers / discover). Tabs are inferred from `tab` so the same component
// renders all three with different data sources and per-row actions.
function SocialListSheet({ tab, users, isFollowingId, onTapUser, onToggleFollow, onClose, viewerId }) {
  const title = tab === 'following' ? 'Following'
              : tab === 'followers' ? 'Followers'
              : 'Find people'
  const emptyText = tab === 'following' ? "You're not following anyone yet. Tap Find people to discover players from your pools."
                  : tab === 'followers' ? "Nobody's following you yet."
                  : "No new players to discover. Join more pools to find people."
  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-handle" />
        <div className="modal-title">{title}</div>
        {users.length === 0 ? (
          <div style={{ padding:'24px 8px', textAlign:'center', fontSize:'13px', color:'var(--text-dim)' }}>
            {emptyText}
          </div>
        ) : (
          <div style={{ maxHeight:'60vh', overflowY:'auto', marginBottom:'12px' }}>
            {users.map(u => {
              const isMe   = u.user_id === viewerId
              const follows = isFollowingId(u.user_id)
              return (
                <div key={u.user_id}
                  style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 4px', borderBottom:'1px solid var(--border)' }}>
                  <div onClick={() => onTapUser(u.user_id)} style={{ display:'flex', alignItems:'center', gap:'10px', flex:1, minWidth:0, cursor:'pointer' }}>
                    <Avatar user={u} />
                    <div style={{ fontSize:'14px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{u.username}</div>
                  </div>
                  {!isMe && (
                    <button
                      onClick={() => onToggleFollow(u.user_id, follows)}
                      className={follows ? 'btn btn-ghost btn-sm' : 'btn btn-gold btn-sm'}
                      style={{ width:'auto', flexShrink:0 }}>
                      {follows ? 'Following' : 'Follow'}
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        )}
        <button className="btn btn-ghost" onClick={onClose}>Close</button>
      </div>
    </div>
  )
}

// Pick one of viewer's groups to invite a friend to. Onward to ShareModal
// (which copies the chosen group's invite_code). Filters to non-historical
// groups — inviting to a finished pool is meaningless.
function GroupPickerSheet({ groups, onClose, onPick }) {
  const active = groups.filter(g => !isHistoricalGroup(g))
  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-handle" />
        <div className="modal-title">Pick a group to invite to</div>
        {active.length === 0 ? (
          <div style={{ padding:'24px 8px', textAlign:'center', fontSize:'13px', color:'var(--text-dim)' }}>
            You don't have any active groups to invite to.
          </div>
        ) : (
          <div style={{ maxHeight:'60vh', overflowY:'auto', marginBottom:'12px' }}>
            {active.map(g => (
              <div key={g.id} onClick={() => onPick(g)}
                style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:10, marginBottom:'6px', cursor:'pointer' }}>
                <div style={{ minWidth:0 }}>
                  <div style={{ fontSize:'14px', fontWeight:'bold', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{g.name}</div>
                  <div style={{ fontSize:'11px', color:'var(--text-dim)', fontFamily:'Arial,sans-serif' }}>{g.sport} · {CONTEST_LABEL[g.contest_type || 'pick_em']}</div>
                </div>
                <span style={{ fontSize:'18px', color:'var(--text-dim)' }}>→</span>
              </div>
            ))}
          </div>
        )}
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
      </div>
    </div>
  )
}

// Read-only view of another user's profile. All profile fields are public per
// migration 011 RLS. Shared-pools list comes from getSharedPools (client-side
// intersection of group_members).
function OtherProfileView({ userId, viewer, onClose, onOpenGroup, onInvite }) {
  const [profile, setProfile]   = useState(null)
  const [shared,  setShared]    = useState([])
  const [follows, setFollows]   = useState(false)
  const [busy,    setBusy]      = useState(false)
  const [loading, setLoading]   = useState(true)
  const [counts,  setCounts]    = useState({ following: 0, followers: 0 })
  const isMe = viewer?.id === userId

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.all([
      getProfile(userId),
      getSharedPools(viewer.id, userId),
      getFollowing(viewer.id),
      getFollowing(userId),
      getFollowers(userId),
    ]).then(([p, sp, myFollowing, theirFollowing, theirFollowers]) => {
      if (cancelled) return
      setProfile(p)
      setShared(sp)
      setFollows(!!myFollowing.find(f => f.user_id === userId))
      setCounts({ following: theirFollowing.length, followers: theirFollowers.length })
      setLoading(false)
    })
    return () => { cancelled = true }
  }, [userId, viewer.id])

  async function toggleFollow() {
    if (busy || isMe) return
    setBusy(true)
    const next = !follows
    setFollows(next) // optimistic
    const res = next ? await followUser(userId) : await unfollowUser(userId)
    if (!res.ok) setFollows(!next) // rollback on failure
    setBusy(false)
  }

  const memberSince = profile?.created_at
    ? new Date(profile.created_at).toLocaleDateString('en-US', { month:'long', year:'numeric' })
    : null

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxHeight:'90vh', overflowY:'auto' }}>
        <div className="modal-handle" />
        {loading ? (
          <div style={{ padding:'40px', textAlign:'center', color:'var(--text-dim)' }}>Loading…</div>
        ) : !profile ? (
          <div style={{ padding:'40px', textAlign:'center', color:'var(--text-dim)' }}>Profile not found.</div>
        ) : (
          <>
            {/* Identity */}
            <div style={{ textAlign:'center', marginBottom:'14px' }}>
              <div style={{ display:'inline-block', margin:'4px auto 8px' }}>
                <Avatar user={profile} size={88} gold />
              </div>
              <div style={{ fontSize:'22px', fontWeight:'bold' }}>{profile.username}</div>
              {memberSince && (
                <div style={{ fontSize:'11px', color:'var(--text-muted)', fontFamily:'Arial,sans-serif', marginTop:'4px' }}>
                  Member since {memberSince}
                </div>
              )}
            </div>

            {/* Follow + invite buttons */}
            {!isMe && (
              <div style={{ display:'flex', gap:'8px', marginBottom:'14px' }}>
                <button
                  onClick={toggleFollow}
                  disabled={busy}
                  className={follows ? 'btn btn-ghost' : 'btn btn-gold'}
                  style={{ flex:1 }}>
                  {follows ? 'Following' : 'Follow'}
                </button>
                <button onClick={() => onInvite(userId)} className="btn btn-ghost" style={{ flex:1 }}>
                  Invite to a group
                </button>
              </div>
            )}

            {/* Follow counts — display-only on other profiles this PR. Tap-through
                to that user's own follow lists is a recursive view, intentionally
                deferred until the recursion ergonomics are designed. */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'14px' }}>
              <div style={{ background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:10, padding:'10px', textAlign:'center' }}>
                <div style={{ fontSize:'18px', fontWeight:'bold', fontFamily:'Arial,sans-serif' }}>{counts.following}</div>
                <div style={{ fontSize:'11px', color:'var(--text-dim)' }}>Following</div>
              </div>
              <div style={{ background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:10, padding:'10px', textAlign:'center' }}>
                <div style={{ fontSize:'18px', fontWeight:'bold', fontFamily:'Arial,sans-serif' }}>{counts.followers}</div>
                <div style={{ fontSize:'11px', color:'var(--text-dim)' }}>Followers</div>
              </div>
            </div>

            {/* Stats tile — total points (score-only; money tracking removed
                per the neutral-app refactor). */}
            <SectionLabel text="STATS" />
            <div style={{ marginBottom:'14px' }}>
              <div style={{ background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:10, padding:'12px 8px', textAlign:'center' }}>
                <div style={{ fontSize:'20px', fontWeight:'bold', color:'var(--gold)', fontFamily:'Arial,sans-serif' }}>{(profile.total_points || 0).toLocaleString()}</div>
                <div style={{ fontSize:'11px', color:'var(--text-dim)' }}>Total points</div>
              </div>
            </div>

            {/* Shared pools */}
            <SectionLabel text={`SHARED POOLS · ${shared.length}`} />
            {shared.length === 0 ? (
              <div style={{ background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:10, padding:'14px', textAlign:'center', fontSize:'12px', color:'var(--text-dim)', marginBottom:'14px' }}>
                {isMe ? 'No pools shared.' : `You and ${profile.username} aren't in any pools together.`}
              </div>
            ) : (
              <div style={{ marginBottom:'14px' }}>
                {shared.map(g => (
                  <div key={g.id} onClick={() => onOpenGroup(g)}
                    style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 12px', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:10, marginBottom:'6px', cursor:'pointer' }}>
                    <div style={{ minWidth:0 }}>
                      <div style={{ fontSize:'13px', fontWeight:'bold', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{g.name}</div>
                      <div style={{ fontSize:'11px', color:'var(--text-dim)', fontFamily:'Arial,sans-serif' }}>{g.sport} · {CONTEST_LABEL[g.contest_type || 'pick_em']}</div>
                    </div>
                    <span style={{ fontSize:'18px', color:'var(--text-dim)' }}>→</span>
                  </div>
                ))}
              </div>
            )}

            <button className="btn btn-ghost" onClick={onClose}>Close</button>
          </>
        )}
      </div>
    </div>
  )
}

export default function App() {
  // Auth
  const [user, setUser]             = useState(null)
  const [authLoading, setAuthLoading] = useState(true)
  const [showPasswordReset, setShowPasswordReset] = useState(false)
  // Auth-prompt overlay — triggered by requireAuth() when a logged-out
  // user hits a gated action. Mounted at the modal section near the end
  // of the JSX. The wall at line 7921 still intercepts a fully logged-
  // out app load, so this overlay is unreachable until Commit 2
  // relocates that gate.
  const [showAuthPrompt, setShowAuthPrompt] = useState(false)

  // Handle Supabase email redirects landing back on the app.
  //   /#access_token=...&type=signup    → email confirmation; Supabase auto-
  //                                       signs the user in via the SDK's
  //                                       URL-detection. We just scrub the
  //                                       hash off the URL so the bar reads
  //                                       cleanly.
  //   /#access_token=...&type=recovery  → password reset; Supabase signs the
  //                                       user in with a recovery session,
  //                                       then we open the reset modal so
  //                                       they can pick a new password.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.location.hash) return
    const params = new URLSearchParams(window.location.hash.slice(1))
    if (!params.has('access_token')) return
    if (params.get('type') === 'recovery') setShowPasswordReset(true)
    window.history.replaceState(null, '', window.location.pathname)
  }, [])

  // Umami Cloud analytics — production-domain only. Strict hostname
  // match (NOT import.meta.env.PROD) so the script is excluded from:
  //   - local dev (`vite dev`)
  //   - Vercel preview deploys (`*-cadenhamlett12-bits-projects.vercel.app`)
  //   - local prod-build previews (`vite build && vite preview`)
  // import.meta.env.PROD is true on Vercel previews + local previews,
  // which would pollute real visitor counts. Hostname is the only
  // signal that strictly identifies the live prod domain. Matches both
  // apex and www variants (don't know which Vercel canonicalizes).
  // Idempotency guard against double-injection if the effect ever
  // re-runs (StrictMode double-renders effects in dev — gate fails in
  // dev anyway, but the check is cheap insurance).
  useEffect(() => {
    if (typeof window === 'undefined') return
    const host = window.location.hostname
    if (host !== 'poolpartysports.org' && host !== 'www.poolpartysports.org') return
    if (document.querySelector('script[data-website-id="54349a5f-2fc9-4fcb-b9cc-5810f31ea54d"]')) return
    const s = document.createElement('script')
    s.defer = true
    s.src   = 'https://cloud.umami.is/script.js'
    s.setAttribute('data-website-id', '54349a5f-2fc9-4fcb-b9cc-5810f31ea54d')
    document.head.appendChild(s)
  }, [])

  // Navigation
  // Default tab is 'modes' (the Home surface) — the bottom-nav reorder
  // 2026-06-12 made Home the leftmost tab, so the app opens where the nav
  // now leads. The tab ID stays 'modes' for historical reasons; only the
  // bottom-nav label reads "Home" (see comment near line 617). The
  // id-vs-label misnomer is logged in HANDOFF housekeeping backlog.
  const [tab, setTab]               = useState('modes')
  const [groupFilter, setGroupFilter] = useState('active') // 'active' | 'completed' (legacy 'locked' values fall through to Active via the `shown` ternary)

  // Top-level image upload plumbing — keeps one <input> at the root of the
  // tree so iOS Safari's file picker reliably fires onChange (per-card inputs
  // inside transient render trees silently fail there).
  const [pendingImageGroupId, setPendingImageGroupId] = useState(null)
  const groupFileInputRef = useRef(null)

  // Same hoist-to-root pattern for the avatar uploader.
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const profileFileInputRef = useRef(null)

  async function handleAvatarUpload(e) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !user?.id) return
    setUploadingAvatar(true)
    try {
      const ext  = (file.name.split('.').pop() || 'jpg').toLowerCase()
      const path = `avatar-${user.id}-${Date.now()}.${ext}`
      const { error: uploadError } = await supabase.storage
        .from('group-images')                       // reusing the existing bucket
        .upload(path, file, { upsert: true })
      if (uploadError) { alert(`Upload failed: ${uploadError.message}`); return }
      const { data: urlData } = supabase.storage.from('group-images').getPublicUrl(path)
      const { error: updateError } = await supabase.from('profiles')
        .update({ avatar_url: urlData.publicUrl }).eq('id', user.id)
      if (updateError) { alert(`Failed to save avatar: ${updateError.message}`); return }
      setUser(prev => ({ ...prev, avatar_url: urlData.publicUrl }))
      // Refresh groups so member avatar circles pick up the new pic too.
      loadGroups(true)
    } catch (err) {
      log('[avatar] upload failed:', err?.message)
      alert(`Error: ${err?.message || 'Unknown error'}`)
    } finally {
      setUploadingAvatar(false)
    }
  }

  async function handleGroupImageChange(e) {
    const file    = e.target.files?.[0]
    const groupId = pendingImageGroupId
    e.target.value = ''           // allow re-selecting the same file later
    setPendingImageGroupId(null)
    if (!file || !groupId) {
      log('[upload] no file or no pending group')
      return
    }
    log('[upload] starting upload:', file.name, file.size, 'bytes', 'group:', groupId)
    try {
      const ext  = (file.name.split('.').pop() || 'jpg').toLowerCase()
      const path = `group-${groupId}-${Date.now()}.${ext}`
      log('[upload] uploading to bucket:', path)
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('group-images')
        .upload(path, file, { upsert: true })
      log('[upload] upload result:', uploadData, uploadError)
      if (uploadError) {
        alert(`Upload failed: ${uploadError.message}`)
        return
      }
      const { data: urlData } = supabase.storage.from('group-images').getPublicUrl(path)
      log('[upload] public URL:', urlData?.publicUrl)
      const { error: updateError } = await supabase.from('groups')
        .update({ image_url: urlData.publicUrl }).eq('id', groupId)
      log('[upload] db update result:', updateError)
      if (updateError) {
        alert(`Failed to save image: ${updateError.message}`)
        return
      }
      // Optimistic local update — flips the card before the network roundtrip
      // returns. loadGroups() below confirms the change (and now correctly
      // selects image_url so the optimistic state isn't overwritten).
      setGroups(prev => prev.map(g =>
        g.id === groupId ? { ...g, image_url: urlData.publicUrl } : g
      ))
      log('[upload] success! reloading groups')
      loadGroups(true)
    } catch (err) {
      log('[upload] unexpected error:', err?.message)
      alert(`Error: ${err?.message || 'Unknown error'}`)
    }
  }

  // Games tab
  const [sport, setSport]           = useState('NBA')
  const [games, setGames]           = useState([])
  const [loadingGames, setLoadingGames] = useState(false)

  // Groups
  const [groups, setGroups]         = useState([])
  const [loadingGroups, setLoadingGroups] = useState(true)
  const [selGroup, setSelGroup]     = useState(null)
  const [groupMembers, setGroupMembers] = useState([])
  const [showCreate, setShowCreate] = useState(false)
  // Tracks whether the currently-rendered CreateGroupModal was opened from
  // the SportModePicker (true → hide sport/contest_type selectors, show
  // locked summaries). Set true by the picker tap-handler, reset false by
  // both create triggers. Stale-true after modal close is fine because the
  // modal isn't rendered then; the next trigger resets it.
  const [modalPreFilled, setModalPreFilled] = useState(false)
  const [showJoin, setShowJoin]     = useState(false)
  const [inviteInput, setInviteInput] = useState('')
  const [joinError, setJoinError]   = useState('')
  const [createForm, setCreateForm] = useState(defaultCreateForm)
  const [createError, setCreateError] = useState('')
  const [createdGroup, setCreatedGroup] = useState(null)

  // Moderation (migration 053). Set of user_ids the current viewer has
  // blocked; passed through to GroupChat so blocked users' messages
  // + reactions are filtered from the render. Fetched on user login;
  // updated locally after block/unblock without a full refetch.
  const [blockedUserIds, setBlockedUserIds] = useState(new Set())
  useEffect(() => {
    if (!user?.id) { setBlockedUserIds(new Set()); return }
    let cancelled = false
    getBlockedUserIds(user.id).then(ids => { if (!cancelled) setBlockedUserIds(ids) })
    return () => { cancelled = true }
  }, [user?.id])
  function onUserBlocked(blockedId) {
    setBlockedUserIds(prev => {
      const next = new Set(prev); next.add(blockedId); return next
    })
  }
  function onUserUnblocked(unblockedId) {
    setBlockedUserIds(prev => {
      const next = new Set(prev); next.delete(unblockedId); return next
    })
  }

  // Backlog 25 Phase D — Stripe creator-fee toggle. Fails closed: defaults
  // false until the mount effect resolves, so the modal renders "FREE MODE"
  // and the submit branch routes to the free path if the backend config
  // endpoint is unreachable. Standalone effect — separate concern from the
  // auth-init below.
  const [stripeEnabled, setStripeEnabled] = useState(false)
  const [configLoaded, setConfigLoaded] = useState(false)
  useEffect(() => {
    let cancelled = false
    getServerConfig().then(cfg => {
      if (cancelled) return
      setStripeEnabled(cfg.stripePaymentsEnabled)
      // Gate the initial loadGroups() on this flag so the first call uses
      // the correct freemiumOn value (fixes the unlocked_at race that
      // caused stale "locked" CTAs to flash on already-paid pools).
      setConfigLoaded(true)
    })
    return () => { cancelled = true }
  }, [])

  // Phase 1.ζ-3 unblock — launch-time first-render race close. The
  // [user] mount-effect's loadGroups() (~L6543) and the
  // getServerConfig effect above kick off in parallel; the groups
  // fetch almost certainly resolves before /api/config does. That
  // means the first getUserGroups call passes stripeEnabled=false
  // (initial state) → freemiumOn=false → omits unlocked_at/lockout_at
  // from the select. Correct for prod-today (flag off — stays false).
  // Correct on flag-flip launch ONLY if we re-fetch the groups with
  // freemiumOn=true once stripeEnabled lands. Ref-guard so the
  // initial-mount false-value doesn't fire a duplicate load on top of
  // [user] effect's normal mount fire — only the true false→true (or
  // any subsequent change) triggers a silent refetch.
  const stripeEnabledInitRef = useRef(true)
  useEffect(() => {
    if (stripeEnabledInitRef.current) {
      stripeEnabledInitRef.current = false
      return
    }
    if (user) loadGroups(true)
  }, [stripeEnabled])

  // Backlog 25 Phase E — App-level toast surface. Lifted from PickemMode's
  // component-local pattern so the Stripe-return handler (below) can surface
  // outcomes regardless of which tab the user lands on. Variable timeout —
  // success cases short (or absent), failure/soft cases longer so they're
  // readable. Unmount cleanup is essential here because App is the
  // longest-lived component; a stranded timer post-unmount would leak.
  const [toast, setToast] = useState(null)
  const toastTimerRef = useRef(null)
  function showToast(message, durationMs = 5000) {
    setToast(message)
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current)
    toastTimerRef.current = setTimeout(() => setToast(null), durationMs)
  }
  useEffect(() => () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current)
  }, [])

  // Backlog 25 Phase E — Stripe-return URL handler. When Stripe redirects
  // back to /?session_id=cs_test_... (or /?canceled=1), this effect detects
  // the param, polls /api/payment-status, and surfaces the outcome via the
  // App-level toast above.
  //
  // CRITICAL — the [user] dep multi-fires: fast-path setUser → enrich
  // setUser → onAuthStateChange setUser. Each is a new object reference,
  // each triggers this effect. Without a ran-once guard the handler would
  // run 2-3 times — polling Stripe and toasting (and on success, calling
  // openGroup) multiple times. The ref claims AFTER the param-presence
  // check so a normal load (no params) doesn't burn the ref and suppress
  // a later same-session return.
  //
  // URL stripped synchronously RIGHT AFTER claiming the ref — before any
  // async work — so a re-render from the poll can't re-enter and a
  // refresh-after-strip doesn't re-trigger the handler.
  //
  // The async work goes in an IIFE because React effect callbacks must
  // return a cleanup fn or nothing, NOT a promise. Do NOT make the outer
  // callback async.
  const stripeReturnHandled = useRef(false)
  useEffect(() => {
    if (!user || stripeReturnHandled.current) return
    const params = new URLSearchParams(window.location.search)
    const sessionId = params.get('session_id')
    const canceled  = params.get('canceled')
    if (!sessionId && !canceled) return   // normal load — do NOT claim the ref
    stripeReturnHandled.current = true    // claim BEFORE any async work

    // Strip the URL synchronously now (per INV-E5; before async/navigate).
    window.history.replaceState(null, '', window.location.pathname)

    if (canceled) {
      showToast('Pool creation canceled.', 2000)
      return
    }

    // Phase 1.ζ-2 — flow=base_unlock fork. Server's success_url is
    //   /?session_id=...&flow=base_unlock&group_id=<uuid>
    // (server/index.js:941). Carries group_id so the browser can recover
    // which pool was unlocked without needing a status endpoint. The
    // pay-to-create handler below stays byte-identical; only this fork
    // is new.
    //
    // Do NOT call pollPaymentStatus here — that endpoint queries
    // pool_creation_payments (legacy) and 404s on every base_unlock
    // session (sink is pool_unlocks, written by the webhook handler).
    // Confirmed in Phase 1.ζ-2 Step-0 investigation. Instead: refetch
    // getUserGroups (which after 1.ζ-1 selects unlocked_at + lockout_at),
    // open the pool immediately, then poll for unlocked_at landing.
    const flow = params.get('flow')
    if (flow === 'base_unlock') {
      ;(async () => {
        try {
          const groupId = params.get('group_id')
          if (!groupId) {
            // Shouldn't happen post-1.ζ-1.5 (server stamps it into success_url),
            // but defensively: navigate to My Pools with a soft hint.
            showToast("Couldn't identify the unlocked pool — check My Groups.", 6000)
            setTab('groups')
            return
          }

          // Navigate to the pool immediately so the user lands on it. The
          // pool already existed pre-checkout — only unlocked_at is new.
          const initialGroups = await getUserGroups(user.id, stripeEnabled)
          setGroups(initialGroups)
          const initialPool = initialGroups.find(p => p.id === groupId)
          if (!initialPool) {
            // Edge case: user lost membership between paying and returning.
            // Soft-toast + navigate to groups list.
            showToast("Couldn't find the pool — check My Groups.", 6000)
            setTab('groups')
            return
          }
          openGroup(initialPool)
          if (deriveUnlockState(initialPool).unlocked) {
            // Webhook beat the redirect — pool is already unlocked, no poll.
            return
          }

          // Poll getUserGroups bounded until unlocked_at lands. Bounded
          // shape mirrors pollPaymentStatus (~30s/2s in stripe.js:285):
          // 20s window is enough for the steady-state webhook latency
          // while keeping the user from waiting forever on replication
          // lag. If the window elapses, fall through to the soft-toast
          // fallback (the pool exists, the unlock is in flight, refresh
          // resolves it).
          const deadline = Date.now() + 20000
          while (Date.now() < deadline) {
            await new Promise(r => setTimeout(r, 2000))
            const refreshed = await getUserGroups(user.id, stripeEnabled)
            setGroups(refreshed)
            const refreshedPool = refreshed.find(p => p.id === groupId)
            if (refreshedPool && deriveUnlockState(refreshedPool).unlocked) {
              setSelGroup(refreshedPool)  // re-render the open pool now unlocked
              return
            }
          }

          // Webhook race fallback — pool still locked after window. NOT
          // a dead-end error and NOT "no pool was created" (the pool
          // exists). Soft toast; user remains on the (still-locked)
          // pool. A page refresh after the webhook lands flips them
          // into the unlocked render naturally.
          showToast("Confirming your unlock — this can take a moment. Refresh shortly.", 6000)
        } catch (err) {
          console.error('[stripe-return base_unlock] failed:', err.message)
          showToast("Couldn't confirm your unlock. Check My Groups.", 6000)
        }
      })()
      return
    }

    // session_id path — poll, dispatch on result. Async IIFE because the
    // outer effect callback must not be async (React expects undefined or
    // a cleanup fn return, not a promise).
    ;(async () => {
      try {
        const { status, createdPoolId } = await pollPaymentStatus(sessionId)
        if (status === 'succeeded') {
          // Refetch groups, find the new pool by createdPoolId, navigate
          // via openGroup. The pool-detail view IS the confirmation —
          // intentionally NO toast on the found path (the planner's
          // refinement; success-toast + navigate could promise a thing
          // the navigate then fails to deliver if not-found).
          //
          // Fallback (not-found): replication lag — the webhook just wrote
          // the row but our refetch raced it. Real case, not an error.
          // Soft toast + setTab('groups') so the user has a clear next step.
          const updatedGroups = await getUserGroups(user.id, stripeEnabled)
          setGroups(updatedGroups)
          const newPool = updatedGroups.find(g => g.id === createdPoolId)
          if (newPool) {
            openGroup(newPool)
          } else {
            showToast('Pool created — find it in My Groups.', 5000)
            setTab('groups')
          }
        } else if (status === 'failed') {
          // Factual wording — no false "try again" CTA (E doesn't build a
          // retry affordance; form state was lost in the Stripe redirect).
          // Form-state-preservation across redirect is HANDOFF backlog 28.
          showToast("Payment didn't complete — no pool was created.", 6000)
        } else if (status === 'expired') {
          showToast('Checkout session expired — no pool was created.', 6000)
        } else if (status === 'expired_schema_mismatch') {
          showToast('Form changed during checkout — no pool was created.', 6000)
        } else {
          // 'unknown' (404 — auth-identity edge or row missing) or 'pending'
          // (30s timeout — webhook didn't resolve in window). Both soft
          // cases: the pool may actually exist server-side. Surface as
          // "check My Pools" so the user can self-verify rather than
          // hitting a hard error.
          showToast("Couldn't confirm payment. Check My Pools — it may already exist.", 6000)
        }
      } catch (err) {
        // pollPaymentStatus throws on "Not authenticated" or network blip.
        // Gated on `user` upstream so JWT should exist; but don't crash
        // mount on the rare exception — surface the soft toast.
        console.error('[stripe-return] poll failed:', err.message)
        showToast("Couldn't confirm payment. Check My Pools — it may already exist.", 6000)
      }
    })()
  }, [user])

  // Delete-group confirmation (creator-only, inline in GroupDetail)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleteError,   setDeleteError]   = useState('')

  // Game detail sheet
  const [selectedGame, setSelectedGame] = useState(null)

  // Share modal
  const [shareGroup, setShareGroup] = useState(null)

  // Social graph — single source of truth for which sheet is open. One sheet
  // at a time (no stacking): { type:'list', tab:'following'|'followers'|'discover' }
  // | { type:'profile', userId } | { type:'pickGroup', forUserId }. Tap a row
  // inside a list closes the list and opens the profile sheet, etc.
  const [socialView, setSocialView] = useState(null)
  const [following, setFollowing]   = useState([])
  const [followers, setFollowers]   = useState([])
  const [discoverPeers, setDiscoverPeers] = useState([])

  // Group detail: which sub-tab is active ('contest' | 'results')
  const [groupTab, setGroupTab] = useState('contest')
  // Incremented after server grading completes → tells PickemMode to reload picks
  const [gradeKey, setGradeKey] = useState(0)

  // Profile recent activity + aggregated stats
  const [recentPicks, setRecentPicks] = useState([])
  const [userStats,   setUserStats]   = useState(null)

  // ── Boot ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false

    // Triple safety so the loading screen always drops:
    //   1. 5s timeout fallback
    //   2. getSession() fast-path on mount (no profile fetch)
    //   3. onAuthStateChange handling INITIAL_SESSION / SIGNED_IN / SIGNED_OUT
    const safetyTimeout = setTimeout(() => {
      if (!cancelled) setAuthLoading(false)
    }, 5000)

    const userFromSession = (authUser) => ({
      id:       authUser.id,
      email:    authUser.email,
      username: authUser.user_metadata?.username || authUser.email?.split('@')[0],
      // Fast-path window default — user is set from session before the
      // enrich merge (at ~line 3414) runs. Default to non-bypass (the safe
      // direction: a real bypass user briefly sees non-bypass state and
      // self-corrects on enrich; a real non-bypass user is correct from the
      // first frame). Real status loads via the profiles SELECT in
      // enrichProfile. This is NOT the strict-coercion contract from
      // backlog 27 commit 1 (that defends against weird truthy values like
      // string "true"); this is a separate defense against the absent-field
      // window between fast-path-set and enrich-completes.
      bypass_payment: false,
    })

    if (!isSupabaseEnabled) {
      // Local-mode: synchronous profile from localStorage.
      getCurrentUser()
        .then(u => { if (!cancelled) setUser(u) })
        .finally(() => { if (!cancelled) setAuthLoading(false) })
      return () => { cancelled = true; clearTimeout(safetyTimeout) }
    }

    // Fast path: read the cached session directly. Skips the profiles fetch
    // entirely so a slow/failed .single() call can never make the app hang
    // on the login page when a session actually exists.
    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        if (!cancelled && session?.user) setUser(userFromSession(session.user))
      })
      .catch(err => { log('[auth] getSession failed:', err?.message) })
      .finally(() => { if (!cancelled) setAuthLoading(false) })

    // Background enrichment: pulls avatar_url + username from profiles after
    // the fast path has already painted the home screen. Fire-and-forget so
    // a slow profiles query can never gate the loading screen.
    const enrichProfile = async (authUser) => {
      const { data: prof } = await supabase
        .from('profiles')
        .select('username, avatar_url, bypass_payment')
        .eq('id', authUser.id)
        .maybeSingle()
      if (!prof || cancelled) return
      setUser(prev => prev && prev.id === authUser.id
        ? {
            ...prev,
            username:     prof.username || prev.username,
            avatar_url:   prof.avatar_url || null,
            // Strict === true coercion (backlog 27, Phase D commit 1). The
            // fast-path at line ~3329 sets user from session BEFORE this
            // enrich runs — during that window `prev.bypass_payment` is
            // absent. Strict coercion makes prof.bypass_payment===true the
            // only path to a true value; absence stays false, which the
            // handleCreateGroup branch (`!== true`) reads as NOT-bypass →
            // free path (safe default). Do NOT "tidy" to
            // !!prof.bypass_payment or drop the strict check — implicit
            // truthiness in the read path + `!user.bypass_payment` at the
            // use site (the old form) silently flipped the absent-field
            // window's default to the unsafe paid path. Strict === true
            // here + !== true at the use site is the matched pair.
            bypass_payment: prof?.bypass_payment === true,
          }
        : prev)
    }

    // Background: ensure profile row exists. Fire-and-forget so a slow
    // profiles query can never block the loading screen. Welcome-bonus
    // deposit insert removed in backlog 1(d).A — wallet is no longer
    // user-facing under the pivot, so seeding a $500 deposit row was
    // creating invisible legacy data for new signups.
    const ensureProfileBackground = async (authUser) => {
      const { data: existing } = await supabase
        .from('profiles').select('id').eq('id', authUser.id).maybeSingle()
      if (existing) return
      await supabase.from('profiles').insert({
        id:           authUser.id,
        username:     authUser.user_metadata?.username || authUser.email?.split('@')[0] || 'Player',
        total_points: 0,
        total_won:    0,
      })
    }

    const { data: authSub } = supabase.auth.onAuthStateChange((event, session) => {
      try {
        if (event === 'INITIAL_SESSION') {
          if (session?.user && !cancelled) {
            setUser(userFromSession(session.user))
            enrichProfile(session.user).catch(err => log('[auth] enrich failed:', err?.message))
          }
          if (!cancelled) setAuthLoading(false)
          return
        }
        if (event === 'SIGNED_IN' && session?.user) {
          if (!cancelled) { setUser(userFromSession(session.user)); setAuthLoading(false) }
          ensureProfileBackground(session.user)
            .then(() => enrichProfile(session.user))
            .catch(err => log('[auth] profile backfill failed:', err?.message))
          return
        }
        if (event === 'SIGNED_OUT') {
          if (!cancelled) { setUser(null); setAuthLoading(false) }
          return
        }
      } catch (err) {
        log('[auth] handler error:', err?.message)
        if (!cancelled) setAuthLoading(false)
      }
    })

    return () => {
      cancelled = true
      clearTimeout(safetyTimeout)
      authSub?.subscription?.unsubscribe?.()
    }
  }, [])

  useEffect(() => {
    if (!user) return
    getRecentPicks(user.id, 5).then(setRecentPicks)
    getUserStats(user.id).then(setUserStats)
    getFollowing(user.id).then(setFollowing)
    getFollowers(user.id).then(setFollowers)
  }, [user])

  // loadGroups split into its own effect gated on configLoaded so the first
  // getUserGroups call uses the resolved stripeEnabled value (freemiumOn).
  // Otherwise unlocked_at is omitted from the SELECT and already-paid pools
  // render with the unlock CTA until a later refetch.
  useEffect(() => {
    if (!user || !configLoaded) return
    loadGroups()
  }, [user, configLoaded])

  // Follow/unfollow handlers. Optimistic update of the in-memory `following`
  // list so the count tile and the discovery toggle reflect the new state
  // immediately. Roll back if the server rejects (network blip, RLS).
  async function handleToggleFollow(targetId, currentlyFollows) {
    // Anonymous-browsing gate — pop the Auth overlay on null user (was a
    // silent return before). targetId === user.id stays a silent no-op
    // since "following yourself" isn't a sign-in moment.
    if (!user?.id) { setShowAuthPrompt(true); return }
    if (targetId === user.id) return
    if (currentlyFollows) {
      const prev = following
      setFollowing(f => f.filter(x => x.user_id !== targetId))
      const res = await unfollowUser(targetId)
      if (!res.ok) setFollowing(prev)
    } else {
      const optimistic = { user_id: targetId, username: targetId, avatar_url: null, created_at: new Date().toISOString() }
      setFollowing(f => [optimistic, ...f])
      const res = await followUser(targetId)
      // Refetch on success to replace the stub with real username/avatar.
      if (res.ok) getFollowing(user.id).then(setFollowing)
      else setFollowing(f => f.filter(x => x.user_id !== targetId))
    }
  }

  // Discovery list = shared-pool peers minus those the viewer already follows.
  // Fetched lazily when the user opens the "Find people" sheet so it stays
  // fresh against follow toggles inside the same sheet.
  async function openDiscover() {
    const peers = await getSharedPoolMemberIds(user.id)
    setDiscoverPeers(peers)
    setSocialView({ type: 'list', tab: 'discover' })
  }

  function openProfile(userId) {
    setSocialView({ type: 'profile', userId })
  }
  function openInvite(userId) {
    setSocialView({ type: 'pickGroup', forUserId: userId })
  }
  function pickGroupForInvite(group) {
    setSocialView(null)
    setShareGroup(group)
  }
  const followingIdSet = new Set(following.map(f => f.user_id))
  function isFollowingId(uid) { return followingIdSet.has(uid) }

  useEffect(() => {
    if (!user) return
    fetchGames()
    // Auto-refresh every 60 seconds so live scores tick without visible churn.
    const id = setInterval(() => { fetchGames(true) }, 60_000)
    return () => clearInterval(id)
  }, [sport, user])

  async function fetchGames(silent) {
    if (!silent) setLoadingGames(true)
    const data = await getGames(sport)
    setGames(data)
    if (!silent) setLoadingGames(false)
  }

  async function loadGroups(silent) {
    if (!silent) setLoadingGroups(true)
    const data = await getUserGroups(user.id, stripeEnabled)
    setGroups(data)
    // Sync selGroup so a stale row (e.g. opened pre-stripeEnabled-resolve
    // with unlocked_at omitted from the SELECT) gets corrected on every
    // refresh. Same find-by-id pattern as the just-paid polling path below.
    setSelGroup(prev => {
      if (!prev) return prev
      const updated = data.find(g => g.id === prev.id)
      return updated || prev
    })
    if (!silent) setLoadingGroups(false)
    // Fire-and-forget: any group past its end_date but still 'active' needs
    // to be finalized. In steady state this matches nothing; only kicks in
    // for groups that aged out between loads.
    checkAndFinalizeGroups(data)
  }

  async function checkAndFinalizeGroups(list) {
    for (const g of list || []) {
      if (!isHistoricalGroup(g)) continue
      if (g.status === 'completed' || g.status === 'archived') continue
      try {
        await finalizeIfDue(g)
      } catch (e) { log('[finalize] error:', e?.message) }
    }
  }

  async function openGroup(group) {
    setSelGroup(group)
    setGroupTab(defaultTabForStatus(group.status))
    setConfirmDelete(false)
    setDeleteError('')
    const finalizeResult = await finalizeIfDue(group)
    let workingGroup = group
    if (finalizeResult?.winnings?.length || finalizeResult?.alreadyCompleted === false) {
      // Group just finalized — refresh group list so the row shows 'completed'
      // and the in-memory selGroup picks up the new status.
      const updatedGroups = await getUserGroups(user.id, stripeEnabled)
      setGroups(updatedGroups)
      const updated = updatedGroups.find(x => x.id === group.id)
      if (updated) {
        setSelGroup(updated)
        workingGroup = updated
        // Status just flipped — re-land on the right tab. Without this,
        // the initial setGroupTab above fired against the pre-finalize
        // status (typically 'active') so the user would be sitting on
        // the Picks/Bracket tab even though the group is now completed.
        setGroupTab(defaultTabForStatus(updated.status))
      }
    }
    if (workingGroup.contest_type === 'pick_em' && workingGroup.sport === 'Golf') {
      // Golf grading runs server-side on the 15-min cron with the round-
      // completeness gate; there's no client-callable per-user golf grader
      // to call here. Bump gradeKey unconditionally so GolfPicksMode
      // re-loads picks — picks up any round_scores + result transitions
      // written by the gated grader since this user's last open.
      setGradeKey(k => k + 1)
    } else if (workingGroup.contest_type === 'pick_em') {
      const gradeResult = await resolvePicks(workingGroup.id, user.id)
      if (gradeResult?.graded > 0) {
        // Grading wrote new results — bump gradeKey so PickemMode reloads picks,
        // then refresh the groups list so standings reflect new points.
        setGradeKey(k => k + 1)
        const updatedGroups = await getUserGroups(user.id, stripeEnabled)
        setGroups(updatedGroups)
        const updated = updatedGroups.find(g => g.id === workingGroup.id)
        if (updated) setSelGroup(updated)
      }
    }
    const mems = await getGroupMembers(group.id, group.contest_type)
    setGroupMembers(mems)
    setTab('groups')
  }

  function handleLogin(u) { setUser(u) }

  // requireAuth gates an action on authentication. If the user is logged
  // in, runs the action immediately. If not, surfaces the Auth UI via
  // the showAuthPrompt overlay (same Auth surface the wall uses). After
  // signup the prompt dismisses and the user is back on the same screen
  // — they tap the action again to proceed. No intent replay; the
  // helper deliberately does NOT remember which action triggered it.
  // Unreachable today because the wall at line 7921 intercepts logged-
  // out renders; will become live in Commit 2.
  function requireAuth(action) {
    if (user) return action()
    setShowAuthPrompt(true)
  }
  async function handleSignOut() {
    await signOut()
    setUser(null); setSelGroup(null); setGroups([])
  }

  // ── Group creation ────────────────────────────────────────────────────────

  function validateCreate() {
    const { name, contest_type, duration_type, sport: s } = createForm
    if (!name.trim()) return 'Group name is required'
    if (containsProfanity(name)) return 'Please choose a different pool name.'
    if (!s) return 'Please choose a sport'
    // 2026-06-13: the "Please select a pool size" gate on createForm.tier
    // was deleted alongside the §4 size-tier picker swap. createForm.tier
    // now stays null throughout — handleCreateGroup's TIERS.find lookup
    // returns undefined, max_players threads as null to the RPC which
    // defaults p_max_players to NULL. Free path unchanged; paid path
    // (currently gated false) is Phase D's rewrite anyway.
    if (contest_type === 'pick_em') {
      const avail = getPickemAvailability(s, duration_type)
      if (!avail.available) return avail.reason
    }
    if (contest_type === 'bracket') {
      if (!isPlayoffSeason(s)) return `${s} playoffs are not currently active`
    }
    return null
  }

  // Backlog 25 Phase D — single source of truth for the paid-path branch
  // condition. Consumed by BOTH the agreement-checkbox text in
  // CreateGroupModal (which mentions the creator fee iff paidPathActive)
  // AND handleCreateGroup's submit branch (which calls createCheckoutSession
  // iff paidPathActive). Centralized here so the consent text and the
  // actual charge can NEVER disagree — they're the same boolean.
  //
  // Strict `!== true` is the matched-pair contract from backlog 27 commit 1:
  // it defends against weird non-boolean truthy values (e.g. the string
  // "true") sneaking through the read sites in db.js and the enrich merge.
  // For the absent-field window (fast-path set before enrich completes),
  // userFromSession defaults bypass_payment to false (see ~line 3362) — a
  // separate defense; do NOT conflate the two protections.
  //
  // DO NOT "tidy" this to `!user?.bypass_payment`. Implicit truthiness
  // resolves the string "true" to false (suppressing paid path — wrong
  // direction for a non-bypass user with a malformed value), while strict
  // `!== true` resolves it to true (paid path — correct for a non-bypass
  // user). Different behaviors for weird values; identical for clean
  // booleans. The strict form is the contract; preserve it.
  //
  // This one line now gates BOTH the charge and the consent text. The
  // comment lives here at the single computation site, not duplicated at
  // each use site.
  // Pay-to-create retired under freemium (freemium-unlock-rls-design.md §5
  // L428-445). Create is FREE; creators pay only the unlock fee like any
  // other member. Decoupled from paidPathActive (which still gates the
  // LockedShell dispatcher at L3924 — the load-bearing piece of the
  // freemium unlock UI from e4340ce; do NOT collapse the two).
  const paidCreateActive = false
  const paidPathActive = stripeEnabled && user?.bypass_payment !== true

  async function handleCreateGroup(e, agreementAccepted) {
    e.preventDefault()
    // Anonymous-browsing gate — Create reaches createGroup which needs
    // user.id. A logged-out user submitting the Create form pops the
    // Auth overlay instead; after sign-in they're back on the form and
    // submit again (no intent replay).
    if (!user?.id) { setShowAuthPrompt(true); return }
    const err = validateCreate()
    if (err) { setCreateError(err); return }

    setCreateError('')
    try {
      const bracketInfo = createForm.contest_type === 'bracket' ? getBracketAvailability(createForm.sport) : {}
      const durationType = createForm.contest_type === 'bracket' ? 'playoffs' : createForm.duration_type
      // Tier drives max_players on BOTH branches. Free-text max_players input
      // was deleted in this commit (backlog 25 Phase D); tier selector is the
      // single source. Server (Phase B) also hard-validates poolConfig.max_players
      // === TIER_MAX[tier] on the paid path and 400s on mismatch, so this
      // must match the tier cap exactly.
      const tierCap = TIERS.find(t => t.id === createForm.tier)?.maxPlayers
      // Neutral-app refactor: entry_fee / prize_split / budget are hardcoded
      // to 0/null/null because the wager-operating UI is gone. The fields
      // remain in basePayload (and pass through to the existing RPC signatures)
      // so no server change is required tonight — Phase D drops them
      // permanently along with the RPC + column migrations.
      const basePayload = {
        name:        createForm.name,
        contest_type: createForm.contest_type,
        sport:       createForm.sport,
        entry_fee:   0,
        prize_split: null,
        duration:    createForm.contest_type === 'bracket' ? (bracketInfo.days || 60) : 30,
        duration_type: durationType,
        start_date:  createForm.start_date || null,
        budget:      null,
        start_round: createForm.contest_type === 'bracket' ? (createForm.startRound || 1) : null,
        max_players: tierCap,
        image_url:   createForm.image_url || null,
        // golf_config: Stage 4 picker MUST store the golf pool config under
        // createForm.golf_config (shape: { eventId, rounds, format }). Undefined
        // today (golf is coming-soon, no picker yet) so this defaults to null —
        // inert until the picker ships. If Stage 4 uses a different field name,
        // update THIS line or golf pools will silently create with null config.
        golf_config: createForm.golf_config ?? null,
        ufc_config:  createForm.ufc_config ?? null,
      }

      // GOLF DIVISIONS — Phase 3 commit 3 wire-up, EXTENDED 2026-06-13 PM
      // by Phase 3.5 (deferred snapshot, Option A).
      //
      // Lifecycle decision tree (at create time):
      //
      //   eventStartDate ≤ today (calendar)
      //     → HARD BLOCK ("tournament has already started").
      //
      //   fetchTierSnapshot ok
      //     → at-create-time freeze. golf_config carries
      //       snapshotStatus='frozen' + tierSnapshot + tierPlayerLookup
      //       + tierStats + tierFetchedAt + tierFrozenAt.
      //
      //   fetchTierSnapshot fails with EVENT_MISMATCH
      //     → NEW for Phase 3.5: allow creation in 'awaiting_odds'
      //       state. DataGolf is serving odds for a different/current
      //       event right now, so we don't have a snapshot yet — but
      //       the user picked a FUTURE event and we should let them
      //       prepare the pool. Lazy-freeze when the first member
      //       opens the picker after odds drop (see picker freeze-on-
      //       mount in src/components/GolfDivisionsMode.jsx, calling
      //       attemptFreezeSnapshot → POST /freeze-snapshot → the
      //       race-safe RPC from migration 037).
      //
      //   fetchTierSnapshot fails with any other code
      //     (FIELD_INCOMPLETE, FIELD_TOO_SMALL, EVENT_NAME_MISMATCH,
      //      UPSTREAM_FAILED, NETWORK_FAILED, BAD_REQUEST)
      //     → HARD BLOCK per the existing operator-locked discipline.
      //       FIELD_INCOMPLETE / EVENT_NAME_MISMATCH are "wait a few
      //       minutes" states (odds ARE out, just not stable); pool
      //       creation should retry rather than freeze partial data.
      //       FIELD_TOO_SMALL is the limited-field-event permanent
      //       block. The 502 family is transient infra.
      if (
        basePayload.contest_type === 'divisions' &&
        basePayload.sport === 'Golf' &&
        basePayload.golf_config?.eventId
      ) {
        // ── (a) Hard-block: tournament must be in the future ──
        //
        // Phase 3.5 provisional threshold: eventStartDate (calendar
        // date in event-local TZ from the schedule) > today's local
        // calendar date. Comparing YYYY-MM-DD strings lexicographically
        // is correct because both are ISO-formatted calendar dates.
        //
        // Provisional in the sense that this is a coarse "the day of R1
        // has begun" check, not a "first tee time has been hit" check.
        // Operator-confirmable: could tighten to first-round tee time
        // later (would need fetching field-updates teetime array per
        // event). See HANDOFF "Phase 3.5 deferred snapshot — provisional
        // threshold."
        const eventStartDate = basePayload.golf_config.eventStartDate
        const todayCalendar  = getDayKey()
        if (!eventStartDate) {
          setCreateError("Pick a tournament from the schedule.")
          return
        }
        if (eventStartDate <= todayCalendar) {
          setCreateError(
            `This tournament has already started (Round 1 was ${eventStartDate}). ` +
            `Golf Pick 6 pools must be created before the tournament begins.`
          )
          return
        }

        // ── (b) Snapshot fetch with deferred-OK on EVENT_MISMATCH ──
        const snapshotResult = await fetchTierSnapshot(basePayload.golf_config.eventId)
        if (snapshotResult.ok) {
          // At-create-time freeze (existing path). Mark snapshotStatus
          // explicitly so the picker treats this as 'frozen' without
          // needing the legacy-compat tierSnapshot-presence fallback.
          basePayload.golf_config = {
            ...basePayload.golf_config,
            snapshotStatus:   'frozen',
            eventName:        snapshotResult.eventName || basePayload.golf_config.eventName,
            tierSnapshot:     snapshotResult.snapshot,
            tierPlayerLookup: snapshotResult.playerLookup,
            tierStats:        snapshotResult.stats,
            tierFetchedAt:    snapshotResult.fetchedAt,
            tierFrozenAt:     snapshotResult.fetchedAt,   // at-create freeze: fetched==frozen
          }
        } else if (
          snapshotResult.error?.code === 'EVENT_MISMATCH' ||
          snapshotResult.error?.code === 'FIELD_INCOMPLETE' ||
          snapshotResult.error?.code === 'EVENT_NAME_MISMATCH'
        ) {
          // ── Deferred-snapshot path (Phase 3.5, extended) ──
          // Odds not ready yet — three transient DataGolf states, all of
          // which resolve without user action once DataGolf catches up to
          // the requested event and the field firms up:
          //   EVENT_MISMATCH       — DataGolf is still serving prior event
          //   FIELD_INCOMPLETE     — some players not yet priced
          //   EVENT_NAME_MISMATCH  — DataGolf sources disagree transiently
          // Create the pool in 'awaiting_odds' state and let the picker
          // lazy-freeze via attemptFreezeSnapshot on first mount once
          // odds firm up. Mirrors the golf pick'em modes' "create now,
          // picks open closer to the tournament" experience.
          basePayload.golf_config = {
            ...basePayload.golf_config,
            snapshotStatus: 'awaiting_odds',
            // tierSnapshot / tierPlayerLookup / tierStats / tierFetchedAt
            // / tierFrozenAt all absent — the lazy-freeze RPC writes them.
          }
        } else {
          // Hard block — permanent structural or unclear-state failures:
          //   FIELD_TOO_SMALL — event has <51 players, never eligible for
          //                     Pick 6 (T1-T5 need 10 each, T6 needs 1+)
          //   UPSTREAM_FAILED — DataGolf infra down; can't tell if odds
          //                     would have been ready
          //   NETWORK_FAILED  — our network to DataGolf failed
          //   BAD_REQUEST     — invalid event id from picker
          // For UPSTREAM_FAILED / NETWORK_FAILED, we could technically
          // create in awaiting_odds and lazy-freeze later, but the user
          // would be paying for a pool without knowing if it will ever
          // work — force a retry to surface the underlying state.
          const e = snapshotResult.error || {}
          let msg
          switch (e.code) {
            case 'FIELD_TOO_SMALL':
              msg = `This event has only ${e.fieldSize ?? '?'} players; Golf Pick 6 requires at least 51 ` +
                    `(T1-T5 are exactly 10 each; T6 is rank 51 to end of field). ` +
                    `Limited-field events aren't eligible.`
              break
            case 'UPSTREAM_FAILED':
              msg = `Odds source temporarily unavailable. Try again in a few minutes.`
              break
            case 'BAD_REQUEST':
              msg = `Invalid event selection. Pick an event from the schedule.`
              break
            case 'NETWORK_FAILED':
            default:
              msg = `Could not build the tier snapshot (${e.code || 'unknown error'}). Try again.`
              break
          }
          setCreateError(msg)
          return
        }
      }

      // GOLF PICK'EM date override: the duration-based calculateGroupDates
      // math in db.js was creating Golf pools with end_date = start + 1
      // day (when duration_type='daily') which let the overdue-finalize
      // sweep flip status='completed' MID-TOURNAMENT — observed live on
      // the RBC "Office picks" pool, finalized after R1 with 3 rounds
      // remaining. Real fix: derive dates from the event's actual
      // tournament window via getEventMeta (field-updates' date_end +
      // 1 day buffer). Fallback to start+5 with a warn when meta is
      // unavailable (rare — pre-publish window or focus mismatch).
      // See src/lib/golfPoolDates.js for the math + tests.
      //
      // 2026-06-13 — extended to also apply to Golf:divisions pools
      // (Phase 3). Same tournament-window math; the snapshot-freeze
      // block above runs first to gate creation on a valid snapshot.
      if (
        (basePayload.contest_type === 'pick_em' || basePayload.contest_type === 'divisions') &&
        basePayload.sport === 'Golf' &&
        basePayload.golf_config?.eventId
      ) {
        const meta = await fetchGolfEventMeta(basePayload.golf_config.eventId)
        // dateStart from getEventMeta is field-updates' date_start (the
        // tournament's R1 calendar day). Falls back to the schedule's
        // startDate (already on the create form via the picker), or
        // ultimately to whatever the user picked.
        const tournamentStart = meta?.dateStart || basePayload.start_date
        if (tournamentStart) {
          const { startISO, endISO, usedFallback } = computeGolfPoolDates({
            startDate: tournamentStart,
            dateEnd:   meta?.dateEnd ?? null,
          })
          basePayload.start_date = startISO
          basePayload.end_date   = endISO
          if (usedFallback) {
            console.warn(
              '[create-group] Golf pool date_end fallback used (start+5) for event',
              basePayload.golf_config.eventId,
              meta?.eventName ? `(${meta.eventName})` : '(unknown name)',
              '— getEventMeta did not surface dateEnd; pool will sweep on',
              endISO,
              'based on the heuristic. Verify after R4.'
            )
          }
        }
      }

      // Time-precise lockout for the unlock guard. computePoolLockout reads
      // sport / contest_type / golf_config / ufc_config / start_date /
      // duration_type off basePayload and returns a Date (event start) or
      // null on any failure. Double try/catch (inner in the helper, outer
      // here) so an unexpected throw can't break creation — a null
      // lockout_at is same-as-today for the RPC (its DEFAULT NULL takes
      // over) and creation proceeds unchanged. Placed before the paid-vs-
      // free branch so both paths carry lockout_at on basePayload; paid
      // path server-side threading is a separate step.
      let lockoutAt = null
      try {
        lockoutAt = await computePoolLockout(basePayload)
      } catch {
        lockoutAt = null
      }
      basePayload.lockout_at = lockoutAt

      // Branch on paidCreateActive (defined above) — hard-coded false under
      // freemium so the legacy tier-checkout never fires. Kept as a named
      // constant rather than deleting the branch outright to preserve the
      // shape for any future re-introduction of a paid create-time flow.
      // The agreement-text/preview consumers still key off paidPathActive.
      if (paidCreateActive) {
        const poolConfig = completePoolConfig(basePayload)
        const { checkoutUrl } = await createCheckoutSession({ tier: createForm.tier, poolConfig })
        window.location.href = checkoutUrl
        return  // redirect — pool doesn't exist yet; webhook creates it; Phase E handles return
      }

      // Free / bypass path — unchanged from pre-Phase-D behavior.
      const g = await createGroup(basePayload, user.id, agreementAccepted)
      setCreatedGroup(g)
      await loadGroups()
    } catch (err) { setCreateError(err.message) }
  }

  async function handleJoinGroup(agreementAccepted) {
    // Anonymous-browsing gate — Join reaches joinGroup which needs user.id.
    if (!user?.id) { setShowAuthPrompt(true); return }
    setJoinError('')
    try {
      const g = await joinGroup(inviteInput.trim(), user.id, agreementAccepted)
      await loadGroups()
      setShowJoin(false); setInviteInput('')
      openGroup(g)
    } catch (err) { setJoinError(err.message) }
  }

  async function handleDeleteGroup() {
    if (!selGroup) return
    setDeleteError('')
    try {
      await deleteGroup(selGroup.id, user.id)
      setConfirmDelete(false)
      setSelGroup(null)
      await loadGroups()
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete group')
    }
  }

  // ── Phase 1.ζ-2 dev harness — LockedShell preview ────────────────────────
  // Mounted via URL param ?preview=lockedshell. Bypasses auth so the
  // operator (and future iterators) can compare every §10.4 cell side-
  // by-side without seeding pools in each state. Purely a dev surface —
  // no DB, no Stripe, no Supabase. The mount is checked before the auth
  // gate so the harness renders even when nobody's logged in.
  if (typeof window !== 'undefined'
      && new URLSearchParams(window.location.search).get('preview') === 'lockedshell') {
    return <LockedShellPreview />
  }

  // ── Auth loading gate ─────────────────────────────────────────────────────
  // Logged-out users now browse the app normally (anonymous-browsing
  // commit). The front-door wall (`if (!user) return <Auth>`) was
  // removed; ProfileTab branches to LoggedOutProfileView for null user,
  // and every identity-bound action (Create/Join/picks/chat/follow) is
  // wrapped in a requireAuth gate that pops the same Auth UI as an
  // overlay. The authLoading spinner stays — it covers the brief window
  // before the first Supabase session check resolves, preventing a
  // logged-in user from flashing the logged-out UI on app launch.

  if (authLoading) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#08080e' }}>
      <BrandMark variant="icon" height={110} />
    </div>
  )

  // ── Games tab ─────────────────────────────────────────────────────────────


  // ── Groups tab ────────────────────────────────────────────────────────────




  // ── Group detail ──────────────────────────────────────────────────────────


  // ── Leaderboard tab ───────────────────────────────────────────────────────


  // ── Profile tab ───────────────────────────────────────────────────────────


  // ── Modals ────────────────────────────────────────────────────────────────

  // ── Shared helpers ────────────────────────────────────────────────────────


  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {tab === 'games'                && (
        <GamesTab
          games={games}
          loadingGames={loadingGames}
          sport={sport}
          setSport={setSport}
          user={user}
          setTab={setTab}
          setSelectedGame={setSelectedGame}
          fetchGames={fetchGames}
        />
      )}
      {tab === 'modes'                && (
        <SportModePicker
          onSelect={(sport, contest_type, startDate) => {
            setCreateForm(defaultCreateForm({
              sport,
              contest_type,
              ...(startDate ? { start_date: startDate } : {}),
            }))
            setModalPreFilled(true)
            setTab('groups')
            setShowCreate(true)
          }}
        />
      )}
      {tab === 'groups' && !selGroup  && (
        <GroupsTab
          groups={groups}
          groupFilter={groupFilter}
          setGroupFilter={setGroupFilter}
          loadingGroups={loadingGroups}
          loadGroups={loadGroups}
          setShowJoin={setShowJoin}
          setJoinError={setJoinError}
          setShowCreate={setShowCreate}
          setTab={setTab}
          user={user}
          openGroup={openGroup}
          setShareGroup={setShareGroup}
          setPendingImageGroupId={setPendingImageGroupId}
          groupFileInputRef={groupFileInputRef}
        />
      )}
      {tab === 'groups' &&  selGroup  && (
        <GroupDetail
          selGroup={selGroup}
          groupMembers={groupMembers}
          setGroupMembers={setGroupMembers}
          user={user}
          gradeKey={gradeKey}
          groupTab={groupTab}
          setGroupTab={setGroupTab}
          confirmDelete={confirmDelete}
          setConfirmDelete={setConfirmDelete}
          deleteError={deleteError}
          setDeleteError={setDeleteError}
          setSelGroup={setSelGroup}
          setShareGroup={setShareGroup}
          handleDeleteGroup={handleDeleteGroup}
          paidPathActive={paidPathActive}
          showToast={showToast}
          blockedUserIds={blockedUserIds}
          onUserBlocked={onUserBlocked}
        />
      )}
      {tab === 'profile'              && (
        <ProfileTab
          user={user}
          userStats={userStats}
          setUser={setUser}
          groups={groups}
          recentPicks={recentPicks}
          uploadingAvatar={uploadingAvatar}
          profileFileInputRef={profileFileInputRef}
          setSelGroup={setSelGroup}
          setTab={setTab}
          setGroupTab={setGroupTab}
          handleSignOut={handleSignOut}
          openGroup={openGroup}
          followingCount={following.length}
          followersCount={followers.length}
          onOpenFollowing={() => setSocialView({ type:'list', tab:'following' })}
          onOpenFollowers={() => setSocialView({ type:'list', tab:'followers' })}
          onOpenDiscover={openDiscover}
          onLogIn={() => setShowAuthPrompt(true)}
          onUserUnblocked={onUserUnblocked}
        />
      )}

      <nav className="bottom-nav">
        {[
          { id:'modes',     label:'Home',      Icon:IcoModes   },
          { id:'groups',    label:'Groups',    Icon:IcoGroups  },
          { id:'games',     label:'Games',     Icon:IcoGames   },
          { id:'profile',   label:'Profile',   Icon:IcoProfile },
        ].map(({ id, label, Icon }) => (
          <button key={id} className={`nav-tab ${tab===id?'active':''}`}
            onClick={() => { setTab(id); if (id !== 'groups') setSelGroup(null) }}>
            {id === 'profile' && user?.avatar_url
              ? <img src={user.avatar_url} alt="" style={{ width:24, height:24, borderRadius:'50%', objectFit:'cover' }} />
              : <Icon />}
            {' '}{label}
          </button>
        ))}
      </nav>

      {showCreate  && <CreateGroupModal createForm={createForm} setCreateForm={setCreateForm} createdGroup={createdGroup} setCreatedGroup={setCreatedGroup} createError={createError} setCreateError={setCreateError} handleCreateGroup={handleCreateGroup} setShowCreate={setShowCreate} openGroup={openGroup} stripeEnabled={stripeEnabled} paidPathActive={paidPathActive} preFilled={modalPreFilled} />}
      {showJoin    && <JoinGroupModal inviteInput={inviteInput} setInviteInput={setInviteInput} joinError={joinError} setJoinError={setJoinError} setShowJoin={setShowJoin} handleJoinGroup={handleJoinGroup} />}
      {showPasswordReset && <PasswordResetModal onClose={() => setShowPasswordReset(false)} onDone={() => setShowPasswordReset(false)} />}
      {/* Auth-prompt overlay — Auth.jsx already styles itself as a
          full-screen view, so just mounting it here covers the app
          behind. Dismissed by the wrapped onLogin callback below. */}
      {showAuthPrompt && <Auth onLogin={(u) => { handleLogin(u); setShowAuthPrompt(false) }} onClose={() => setShowAuthPrompt(false)} />}
      {selectedGame && <GameDetailSheet game={selectedGame} groups={groups} user={user} setTab={setTab} onClose={() => setSelectedGame(null)} promptAuth={() => setShowAuthPrompt(true)} />}
      {shareGroup && <ShareModal group={shareGroup} onClose={() => setShareGroup(null)} />}

      {/* Social sheets — one at a time. Tap-through transitions (list row →
          profile → invite picker → ShareModal) update socialView in place
          rather than stacking modals, so the backdrop never doubles up. */}
      {socialView?.type === 'list' && (
        <SocialListSheet
          tab={socialView.tab}
          users={
            socialView.tab === 'following' ? following
            : socialView.tab === 'followers' ? followers
            : discoverPeers.filter(p => !followingIdSet.has(p.user_id))
          }
          isFollowingId={isFollowingId}
          viewerId={user?.id}
          onTapUser={uid => openProfile(uid)}
          onToggleFollow={handleToggleFollow}
          onClose={() => setSocialView(null)}
        />
      )}
      {socialView?.type === 'profile' && (
        <OtherProfileView
          userId={socialView.userId}
          viewer={user}
          onClose={() => setSocialView(null)}
          onOpenGroup={g => { setSocialView(null); openGroup(g) }}
          onInvite={openInvite}
        />
      )}
      {socialView?.type === 'pickGroup' && (
        <GroupPickerSheet
          groups={groups}
          onClose={() => setSocialView(null)}
          onPick={pickGroupForInvite}
        />
      )}

      {/* App-level toast — backlog 25 Phase E. Surfaces Stripe-return
          outcomes (and any future App-scope notifications). Mirrors
          PickemMode.jsx:489-500 styling (fixed bottom-center, surface2
          background, green-accent border) but drops the `animation: fadeIn`
          line — `@keyframes fadeIn` is not defined anywhere in the
          codebase, so PickemMode's reference has been silently no-op'ing
          all along. Toast renders fine without it; not introducing a
          dangling reference here. (PickemMode:496 has the same issue —
          backlog candidate for a future style cleanup pass.) */}
      {toast && (
        <div style={{
          position:'fixed', bottom:'90px', left:'50%', transform:'translateX(-50%)',
          background:'var(--surface2)', color:'var(--text)',
          border:'1px solid rgba(0,230,118,0.4)', borderRadius:'8px',
          padding:'8px 16px', fontSize:'13px', fontFamily:'Arial,sans-serif',
          boxShadow:'0 4px 12px rgba(0,0,0,0.4)', zIndex:1000,
        }}>
          {toast}
        </div>
      )}

      {/* Top-level hidden file input for group-image uploads. Lives outside
          every card/modal so iOS Safari's file picker can reliably fire its
          onChange. The pencil button on a GroupCard sets pendingImageGroupId
          and programmatically clicks this input. */}
      <input
        ref={groupFileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp,image/heic"
        style={{ display: 'none' }}
        onChange={handleGroupImageChange}
      />
      {/* Same pattern for profile avatar uploads. */}
      <input
        ref={profileFileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp,image/heic"
        style={{ display: 'none' }}
        onChange={handleAvatarUpload}
      />
    </div>
  )
}
